import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { api } from '../lib/api'
import { useToast } from '../ui/ToastProvider'

const EMPTY_LIST = []

const pad2 = (n) => String(n).padStart(2, '0')

const deriveBirthDateFromIdentityDigits = (identityDigits) => {
  const id = (identityDigits || '').toString().replaceAll(/[^0-9]/g, '')
  if (id.length < 6) return ''
  const yy = id.slice(0, 2)
  const mm = id.slice(2, 4)
  const dd = id.slice(4, 6)

  if (!/^\d{2}$/.test(yy) || !/^\d{2}$/.test(mm) || !/^\d{2}$/.test(dd)) return ''
  const mmN = Number(mm)
  const ddN = Number(dd)
  if (!(mmN >= 1 && mmN <= 12)) return ''
  if (!(ddN >= 1 && ddN <= 31)) return ''

  const yyN = Number(yy)
  const nowYY = Number(String(new Date().getFullYear()).slice(-2))
  const century = yyN >= 0 && yyN <= nowYY ? '20' : '19'
  return `${century}${yy}${mm}${dd}`
}

const formatMaskedIdentityForUi = (identityDigits) => {
  const birth = deriveBirthDateFromIdentityDigits(identityDigits)
  if (!birth) return ''
  // 요구사항: YYYYMMDD-******* (뒤 7자리는 마스킹)
  return `${birth}-*******`
}

const toDateObject = (value) => {
  if (value === null || value === undefined) return null
  const s = String(value).trim()
  if (!s) return null

  // yyyyMMdd
  if (/^\d{8}$/.test(s)) {
    const y = Number(s.slice(0, 4))
    const m = Number(s.slice(4, 6))
    const d = Number(s.slice(6, 8))
    const dt = new Date(y, m - 1, d, 0, 0, 0)
    return Number.isNaN(dt.getTime()) ? null : dt
  }

  // yyyyMMddHHmmss
  if (/^\d{14}$/.test(s)) {
    const y = Number(s.slice(0, 4))
    const m = Number(s.slice(4, 6))
    const d = Number(s.slice(6, 8))
    const hh = Number(s.slice(8, 10))
    const mm = Number(s.slice(10, 12))
    const ss = Number(s.slice(12, 14))
    const dt = new Date(y, m - 1, d, hh, mm, ss)
    return Number.isNaN(dt.getTime()) ? null : dt
  }

  // "YYYY-MM-dd HH:mm:ss" 형태를 ISO로 보정
  const normalized = s.includes(' ') && !s.includes('T') ? s.replace(' ', 'T') : s
  const dt = new Date(normalized)
  return Number.isNaN(dt.getTime()) ? null : dt
}

const formatDate = (value) => {
  const dt = toDateObject(value)
  if (!dt) return ''
  return `${dt.getFullYear()}-${pad2(dt.getMonth() + 1)}-${pad2(dt.getDate())}`
}

const formatDateTime = (value) => {
  const dt = toDateObject(value)
  if (!dt) return ''
  return `${dt.getFullYear()}-${pad2(dt.getMonth() + 1)}-${pad2(dt.getDate())} ${pad2(
    dt.getHours(),
  )}:${pad2(dt.getMinutes())}:${pad2(dt.getSeconds())}`
}

const DEMO_TREATMENTS = [
  {
    id: 1,
    diagnosisName: '위염',
    diagnosisCode: 'K29.7',
    startDate: '2025-01-10',
    endDate: '2025-01-12',
    count: 1,
    hiddenGuide: '위내시경 결과에 따라 과거 실손보험 청구 가능 여부를 확인해 보세요.',
    totalTreatmentDays: 3,
    totalDrugDays: 5,
    inpatient: false,
    surgeryName: null,
    dentalMain: '해당 없음',
    dentalDetail: '해당 없음',
  },
  {
    id: 2,
    diagnosisName: '무릎 관절 수술',
    diagnosisCode: 'S83.2',
    startDate: '2024-11-01',
    endDate: '2024-11-07',
    count: 1,
    hiddenGuide: '입원/수술 특약, 재활치료 관련 추가 보장 가능 여부를 확인해 보세요.',
    totalTreatmentDays: 7,
    totalDrugDays: 14,
    inpatient: true,
    surgeryName: '무릎 관절경 수술',
    dentalMain: '해당 없음',
    dentalDetail: '해당 없음',
  },
  {
    id: 3,
    diagnosisName: '어금니 임플란트',
    diagnosisCode: 'Z46.3',
    startDate: '2024-08-20',
    endDate: '2024-09-05',
    count: 1,
    hiddenGuide: '치과/치아 보장 특약에서 보상 가능 여부를 확인해 보세요.',
    totalTreatmentDays: 5,
    totalDrugDays: 10,
    inpatient: false,
    surgeryName: null,
    dentalMain: '임플란트 시술',
    dentalDetail: '좌측 하악 제1대구치 임플란트',
  },
]

export default function HealthAnalysisPage() {
  const { show } = useToast()
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const [exporting, setExporting] = useState(false)
  const [activeTab, setActiveTab] = useState('new') // 'new' | 'reports' | 'customers'
  const [form, setForm] = useState({
    customerId: '',
    name: '',
    phoneNumber: '',
    identity: '',
    birthDate: '',
    telecom: '0',
    authProvider: 'KAKAO',
  })

  const deriveBirthDateFromIdentity = useCallback((identityDigits) => {
    return deriveBirthDateFromIdentityDigits(identityDigits)
  }, [])

  const identityMaskedDisplay = useMemo(() => {
    const digits = (form.identity || '').toString().replaceAll(/[^0-9]/g, '')
    return formatMaskedIdentityForUi(digits)
  }, [form.identity])
  const [lastReport, setLastReport] = useState(null)
  const [selectedReport, setSelectedReport] = useState(null)
  const [selectedReportId, setSelectedReportId] = useState(null)
  const [selectedReportMeta, setSelectedReportMeta] = useState(null) // { customerName?, createdAt? }
  const [search, setSearch] = useState('')
  const [notes, setNotes] = useState({})
  const [resultTab, setResultTab] = useState('hidden') // 'hidden' | 'treatments' | 'drugs' | 'healthy' | 'substandard' | 'dental'
  const [hiddenSubTab, setHiddenSubTab] = useState('진단비')
  const [treatmentSubTab, setTreatmentSubTab] = useState('1개월')
  const [treatmentSourceSubTab, setTreatmentSourceSubTab] = useState('전체')
  const [drugSubTab, setDrugSubTab] = useState('3개월')
  const [healthySubTab, setHealthySubTab] = useState('3개월')
  const [substandardSubTab, setSubstandardSubTab] = useState('3개월')
  const [dentalSubTab, setDentalSubTab] = useState('틀니')
  const [currentJob, setCurrentJob] = useState(null)
  const [twoWayModalOpen, setTwoWayModalOpen] = useState(false)
  const [twoWayJobId, setTwoWayJobId] = useState(null)
  const [twoWayInfo, setTwoWayInfo] = useState(null)
  const [twoWayInput, setTwoWayInput] = useState({
    secureNo: '',
    smsAuthNo: '',
    simpleAuth: '1',
  })
  const [detailEpisode, setDetailEpisode] = useState(null)
  const [detailShowHiddenGuide, setDetailShowHiddenGuide] = useState(false)
  const [collapsedDetailDays, setCollapsedDetailDays] = useState(() => new Set())
  const [retryJobId, setRetryJobId] = useState(null)
  const [retryIdentity, setRetryIdentity] = useState('')
  const [page, setPage] = useState(0)
  const PAGE_SIZE = 5
  const [threeNnModalOpen, setThreeNnModalOpen] = useState(false)
  const [threeNnModalMode, setThreeNnModalMode] = useState('SIMPLE_3N5') // 'SIMPLE_3N5' | 'TEMPLATE_3NN'
  const [threeNnLoading, setThreeNnLoading] = useState(false)
  const [threeNnResult, setThreeNnResult] = useState(null)
  const [threeNnTemplateLoading, setThreeNnTemplateLoading] = useState(false)
  const [threeNnTemplateResult, setThreeNnTemplateResult] = useState(null)
  // 보험사별 예외 적용(예외질환/3NN) UI 상태
  const [selectedInsurerCodesForRequest, setSelectedInsurerCodesForRequest] = useState(() => new Set()) // Set<insurerCode>
  const [threeNnTemplateExpandedTemplates, setThreeNnTemplateExpandedTemplates] = useState(() => new Set()) // `${insurerCode}__${templateCode}`
  const [threeNnTemplateCellDetail, setThreeNnTemplateCellDetail] = useState(null) // { insurerCode, insurerName, templateCode, r, asOf? }
  const [threeNnTemplateKcdExpanded, setThreeNnTemplateKcdExpanded] = useState(() => new Set()) // `${insurerCode}__${templateCode}__${kcd}`
  const [substandardIgnoreRecent3mOpinion, setSubstandardIgnoreRecent3mOpinion] = useState(false)
  const [questionnaireModalType, setQuestionnaireModalType] = useState(null) // 'healthy' | 'substandard' | null
  const [designRequestModalOpen, setDesignRequestModalOpen] = useState(false)
  const [designCoverageType, setDesignCoverageType] = useState('substandard') // 'healthy' | 'substandard'
  const [healthyAutoSummary, setHealthyAutoSummary] = useState(null)

  // 재분석 버튼 노출 정책: 생성일로부터 7일 이내만 노출
  const canShowReanalyzeButton = useMemo(() => {
    const createdAt = selectedReport?.createdAt || selectedReportMeta?.createdAt || null
    const dt = toDateObject(createdAt)
    if (!dt) return true // createdAt이 없으면 차단하지 않음(데이터 누락 대비)
    const ageMs = Date.now() - dt.getTime()
    const sevenDaysMs = 7 * 24 * 60 * 60 * 1000
    return ageMs >= 0 && ageMs <= sevenDaysMs
  }, [selectedReport?.createdAt, selectedReportMeta?.createdAt])

  const threeNnModalData = useMemo(() => {
    return threeNnModalMode === 'TEMPLATE_3NN' ? threeNnTemplateResult : threeNnResult
  }, [threeNnModalMode, threeNnTemplateResult, threeNnResult])

  const toStatusCode = (v) => (v || '').toString().trim().toUpperCase()
  const statusLabelKr = (code) => {
    const c = toStatusCode(code)
    if (c === 'OK') return '가능'
    if (c === 'WARN') return '주의'
    if (c === 'REVIEW') return '심사'
    if (c === 'FAIL') return '불가'
    return c || '-'
  }

  // 보험사 표시명(한글) 매핑 (운영에서 마스터 연동 전 임시)
  const insurerNameKrOf = (code, fallbackName) => {
    const c = (code || '').toString().trim().toUpperCase()
    const map = {
      ABL_LIFE: 'ABL생명',
      AIG: 'AIG손해보험',
      AIG_FIRE: 'AIG손해보험',
      DB: 'DB손해보험',
      DB_LIFE: 'DB생명',
      DB_FIRE: 'DB손해보험',
      HANA: '하나손해보험',
      HANA_FIRE: '하나손해보험',
      HANHWALIFE: '한화생명',
      HANWHA: '한화손해보험',
      HANWHA_FIRE: '한화손해보험',
      HK: '흥국화재',
      HK_LIFE: '흥국생명',
      HYUNDAI: '현대해상',
      HYUNDAI_FIRE: '현대해상',
      KB_LIFE: 'KB생명',
      KB_NONLIFE: 'KB손해보험',
      KYOBO_LIFE: '교보생명',
      LINA_LIFE: '라이나생명',
      LOTTE_NONLIFE: '롯데손해보험',
      LOTTE_FIRE: '롯데손해보험',
      MERITZ: '메리츠화재',
      MERITZ_FIRE: '메리츠화재',
      MIRAEASSET: '미래에셋생명',
      NH_FIRE: 'NH농협손해보험',
      NH_LIFE: 'NH농협생명',
      PUBON_LIFE: '푸본현대생명',
      SAMSUNG: '삼성화재',
      SAMSUNG_FIRE: '삼성화재',
      SHINHAN_LIFE: '신한라이프',
    }
    if (map[c]) return map[c]
    const fb = (fallbackName || '').toString().trim()
    return fb || c || '-'
  }

  // UI 중복 병합용: 같은 회사(예: SAMSUNG vs SAMSUNG_FIRE)를 하나로 묶는다.
  // - 데이터(템플릿/보험사 마스터)에 동일 회사가 서로 다른 insurerCode로 존재하는 케이스가 있어,
  //   한글 표시명이 동일하게 매핑되면서 컬럼이 중복으로 보이는 문제가 발생한다.
  const canonicalInsurerCodeForUi = (code) => {
    const c = (code || '').toString().trim().toUpperCase()
    if (!c) return ''
    const map = {
      AIG_FIRE: 'AIG',
      DB_FIRE: 'DB',
      HANA_FIRE: 'HANA',
      HANWHA_FIRE: 'HANWHA',
      HYUNDAI_FIRE: 'HYUNDAI',
      LOTTE_FIRE: 'LOTTE_NONLIFE',
      MERITZ_FIRE: 'MERITZ',
      SAMSUNG_FIRE: 'SAMSUNG',
    }
    if (map[c]) return map[c]
    return c
  }

  // 보험사 카테고리 추정(필터용): LIFE / NONLIFE
  const insurerCategoryOf = (code) => {
    const c = canonicalInsurerCodeForUi(code)
    if (!c) return 'ALL'
    const LIFE = new Set([
      'ABL_LIFE',
      'DB_LIFE',
      'HANHWALIFE',
      'HK_LIFE',
      'KB_LIFE',
      'KYOBO_LIFE',
      'LINA_LIFE',
      'MIRAEASSET',
      'NH_LIFE',
      'PUBON_LIFE',
      'SHINHAN_LIFE',
    ])
    const NONLIFE = new Set([
      'AIG',
      'DB',
      'HANA',
      'HANWHA',
      'HK',
      'HYUNDAI',
      'KB_NONLIFE',
      'LOTTE_NONLIFE',
      'MERITZ',
      'NH_FIRE',
      'SAMSUNG',
    ])
    if (LIFE.has(c)) return 'LIFE'
    if (NONLIFE.has(c)) return 'NONLIFE'
    if (c.endsWith('_LIFE') || c.includes('LIFE')) return 'LIFE'
    return 'NONLIFE'
  }

  // NOTE: 아래 computeHealthy* 함수들이 이 변수들을 참조하는데,
  // 파일 구조상 선언 위치가 뒤쪽에 있어 TDZ(선언 전 참조) 에러가 날 수 있다.
  // 따라서 "먼저 선언"하고, parsedSummary 이후에 값만 할당한다.
  let byPeriodFromSummary = null
  let healthyViewFromSummary = null
  let rawEpisodesFromSummary = []

  const detailListScrollRef = useRef(null)
  const [detailEpisodeMode, setDetailEpisodeMode] = useState('treatments') // 'treatments' | 'drugs'
  const [detailEpisodeContext, setDetailEpisodeContext] = useState('ALL') // 'ALL' | 'EXAM' | 'INHOSP_SURGERY' | 'HIDDEN_SURGERY' | 'TREATMENTS' | 'DRUGS'
  const [examIncludeImaging, setExamIncludeImaging] = useState(false)
  const [inhospIncludeNonSurgery, setInhospIncludeNonSurgery] = useState(false)
  const [hiddenIncludeAllProcedures, setHiddenIncludeAllProcedures] = useState(false)
  const [drugsShowAllInfo, setDrugsShowAllInfo] = useState(false)

  // 상세 팝업이 열려있는 동안 배경(분석목록) 스크롤을 잠근다.
  // 그렇지 않으면 휠/트랙패드 스크롤이 body로 전달되어 뒤 컨텐츠가 움직인다.
  useEffect(() => {
    if (!detailEpisode) return
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = prev
    }
  }, [detailEpisode])

  const closeDetailEpisode = useCallback(() => {
    setDetailEpisode(null)
    setDetailShowHiddenGuide(false)
    setCollapsedDetailDays(new Set())
    setDetailEpisodeMode('treatments')
    setDetailEpisodeContext('ALL')
    setExamIncludeImaging(false)
    setInhospIncludeNonSurgery(false)
    setHiddenIncludeAllProcedures(false)
    setDrugsShowAllInfo(false)
  }, [])

  const openDetailEpisode = useCallback((episode, opts = {}) => {
    setDetailShowHiddenGuide(Boolean(opts.showHiddenGuide))
    // opts.mode가 명시되지 않으면, details에 투약 라인이 있는지로 모드를 자동 결정한다.
    const autoMode = (() => {
      if (!episode) return 'treatments'
      const details = episode?.details
      if (!Array.isArray(details) || details.length === 0) return 'treatments'
      return details.some((x) => x?.source === 'PRESCRIBE_DRUG' || x?.drugName) ? 'drugs' : 'treatments'
    })()
    setDetailEpisodeMode(opts.mode === 'drugs' ? 'drugs' : opts.mode === 'treatments' ? 'treatments' : autoMode)
    setDetailEpisode(episode)
    setCollapsedDetailDays(new Set())
    setDetailEpisodeContext(
      opts.context === 'EXAM' ||
        opts.context === 'INHOSP_SURGERY' ||
        opts.context === 'HIDDEN_SURGERY' ||
        opts.context === 'TREATMENTS' ||
        opts.context === 'DRUGS'
        ? opts.context
        : 'ALL',
    )
    // 상세 목록이 길면 이전 스크롤 위치가 남아 UX가 나빠서, 열 때마다 상단으로 이동
    requestAnimationFrame(() => {
      if (detailListScrollRef.current) detailListScrollRef.current.scrollTop = 0
    })
  }, [])

  const handleEvaluate3nn = useCallback(
    async (reportId, opts = {}) => {
      const { openModal = false, ignoreRecent3mOpinion = false } = opts || {}
      if (!reportId) {
        show('리포트가 선택되지 않았습니다.', 'error')
        return
      }

      setThreeNnLoading(true)
      const parsedSummaryLocal = (() => {
        const raw = selectedReport?.summaryJson
        if (!raw) return null
        try {
          return JSON.parse(raw)
        } catch (e) {
          return null
        }
      })()

      const profile = parsedSummaryLocal?.profile || null
      const byPeriodGroupedDiagnosis = profile?.byPeriodGroupedDiagnosis || null
      const subView = profile?.substandardView || null
      if (!subView) {
        show('분석 결과(유병자 데이터)가 없습니다. 재분석 후 다시 시도해 주세요.', 'error')
        setThreeNnLoading(false)
        return
      }

      const baseAt = parsedSummaryLocal?.createdAt || selectedReport?.createdAt || null
      const baseDate = baseAt ? new Date(baseAt) : new Date()

      const normalizeDateDigitsSafe = (v) => {
        if (!v) return null
        const s = String(v).replaceAll('-', '').trim()
        if (s.length !== 8) return null
        return s
      }
      const dateDigitsToDate = (yyyymmdd) => {
        const s = normalizeDateDigitsSafe(yyyymmdd)
        if (!s) return null
        const y = Number(s.slice(0, 4))
        const m = Number(s.slice(4, 6)) - 1
        const d = Number(s.slice(6, 8))
        const dt = new Date(y, m, d)
        return Number.isFinite(dt.getTime()) ? dt : null
      }
      const daysAgo = (digits) => {
        const dt = dateDigitsToDate(digits)
        if (!dt) return null
        const diff = Math.floor((baseDate.getTime() - dt.getTime()) / (24 * 60 * 60 * 1000))
        return Number.isFinite(diff) ? diff : null
      }

      const hasAnyGroup = (list) => Array.isArray(list) && list.length > 0
      const anyNonDrugWithinDays = (groups, limitDays) => {
        if (!Array.isArray(groups) || groups.length === 0) return false
        for (const g of groups) {
          const details = Array.isArray(g?.details) ? g.details : null
          if (details && details.length > 0) {
            for (const x of details) {
              const src = String(x?.source || '')
              if (src === 'PRESCRIBE_DRUG') continue
              const d = normalizeDateDigitsSafe(x?.startDate || x?.endDate)
              const a = d ? daysAgo(d) : null
              if (a !== null && a >= 0 && a <= limitDays) return true
            }
          }
          const gd = normalizeDateDigitsSafe(g?.startDate || g?.endDate)
          const ga = gd ? daysAgo(gd) : null
          if (ga !== null && ga >= 0 && ga <= limitDays) return true
        }
        return false
      }
      const buildNonDrugExamplesWithinDays = (groups, limitDays, maxItems = 8) => {
        if (!Array.isArray(groups) || groups.length === 0) return []

        const normalizeOrgName = (x) => {
          const raw =
            x?.hospitalName ||
            x?.orgName ||
            x?.orgNm ||
            x?.medicalOrgName ||
            x?.medicalOrgNm ||
            x?.treatOrgName ||
            x?.treatOrgNm ||
            null
          const s = (raw || '').toString().trim()
          return s || null
        }
        const formatYmd = (digits) => {
          const s = normalizeDateDigitsSafe(digits)
          if (!s) return null
          return `${s.slice(0, 4)}-${s.slice(4, 6)}-${s.slice(6, 8)}`
        }

        const out = []
        const push = (x, g) => {
          const src = String(x?.source || g?.source || '')
          if (src === 'PRESCRIBE_DRUG') return
          const d = normalizeDateDigitsSafe(x?.startDate || x?.endDate || g?.startDate || g?.endDate)
          const a = d ? daysAgo(d) : null
          if (a === null || a < 0 || a > limitDays) return

          const code = (x?.diagnosisCode || g?.diagnosisCode || '').toString().trim() || null
          const name = (x?.diagnosisName || g?.diagnosisName || '').toString().trim() || null
          const org = normalizeOrgName(x) || normalizeOrgName(g) || null

          out.push({
            day: d,
            code,
            name,
            org,
          })
        }

        for (const g of groups) {
          const details = Array.isArray(g?.details) ? g.details : null
          if (details && details.length > 0) {
            for (const x of details) push(x, g)
          } else {
            push(g, g)
          }
        }

        // 최신순, (일자/기관/진단코드) 단위로 중복 제거
        out.sort((a, b) => String(b.day || '').localeCompare(String(a.day || '')))
        const seen = new Set()
        const rendered = []
        for (const row of out) {
          if (rendered.length >= maxItems) break
          const key = `${row.day || ''}__${row.org || ''}__${row.code || ''}`
          if (seen.has(key)) continue
          seen.add(key)

          const ymd = formatYmd(row.day) || (row.day || '-')
          const org = row.org || '-'
          const diag = row.code ? `${row.code}${row.name ? `(${row.name})` : ''}` : row.name || '-'
          rendered.push(`${ymd} / ${org} / ${diag}`)
        }

        return rendered
      }

      const anyInpatientOrSurgeryWithinDays = (groups, limitDays) => {
        if (!Array.isArray(groups) || groups.length === 0) return false

        // NOTE: substandardView의 hosp* 그룹은 "입원/수술(또는 유사)" 후보들을 모아둔 값이지만,
        // 데이터 소스/버전에 따라 단순 진료 그룹이 섞일 수 있다.
        // 따라서 실제로 입원/수술 플래그(입원 true / 수술명 존재)를 가진 항목만 대상으로 기간 체크를 한다.
        const isInpatientOrSurgeryLike = (x) => {
          if (!x) return false
          if (x?.inpatient) return true
          const surgeryName = (x?.surgeryName || '').toString().trim()
          if (surgeryName) return true
          if (Array.isArray(x?.surgeryNames) && x.surgeryNames.length > 0) return true
          return false
        }
        const within = (v) => {
          const d = normalizeDateDigitsSafe(v?.startDate || v?.endDate)
          const a = d ? daysAgo(d) : null
          return a !== null && a >= 0 && a <= limitDays
        }

        for (const g of groups) {
          const details = Array.isArray(g?.details) ? g.details : null
          if (details && details.length > 0) {
            for (const x of details) {
              if (!isInpatientOrSurgeryLike(x)) continue
              if (within(x)) return true
            }
          }
          if (!isInpatientOrSurgeryLike(g)) continue
          if (within(g)) return true
        }
        return false
      }

      // 1.5) 5년 내 14일 이상 투약 (고지 요약용)
      // - 화면의 투약 집계(일자별 최대 투약일수 합산)와 동일 컨셉으로 계산
      const longDrug14Info = (() => {
        const byPeriod = profile?.byPeriod || null
        const rawByPeriod = byPeriod && byPeriod['5Y']
        const rawList = Array.isArray(rawByPeriod) ? rawByPeriod : (Array.isArray(profile?.allEpisodesRaw) ? profile.allEpisodesRaw : [])
        if (!Array.isArray(rawList) || rawList.length === 0) return { has: false, top: [] }

        const normalizeDrugKeyLocal = (line) => {
          const n = (line?.drugName || '').toString().trim().toLowerCase()
          const days = line?.totalDrugDays ?? ''
          return `${n}|${days}`
        }
        const isPharmacyLikeLocal = (line) => {
          const t = (line?.treatTypeDetail || line?.treatType || '').toString()
          const org = (line?.hospitalName || '').toString()
          if (t.includes('조제') || t.includes('처방')) return true
          if (org.includes('약국')) return true
          return false
        }
        const daysBetweenLocal = (aDigits, bDigits) => {
          const a = dateDigitsToDate(aDigits)
          const b = dateDigitsToDate(bDigits)
          if (!a || !b) return null
          const diffMs = Math.abs(a.getTime() - b.getTime())
          return Math.floor(diffMs / (24 * 60 * 60 * 1000))
        }
        const dedupeAndLinkLocal = (drugLines) => {
          if (!Array.isArray(drugLines) || drugLines.length === 0) return []
          const perDayKey = new Map()
          for (const line of drugLines) {
            const day = normalizeDateDigitsSafe(line?.startDate || line?.endDate)
            const key = normalizeDrugKeyLocal(line)
            if (!day || !key || key.startsWith('|')) continue
            const mapKey = `${day}__${key}`
            const prev = perDayKey.get(mapKey)
            if (!prev) {
              perDayKey.set(mapKey, line)
              continue
            }
            const prevIsPharm = isPharmacyLikeLocal(prev)
            const curIsPharm = isPharmacyLikeLocal(line)
            if (!prevIsPharm && curIsPharm) {
              perDayKey.set(mapKey, line)
            }
          }
          const compact = Array.from(perDayKey.values())
          const pharmacies = compact.filter((x) => isPharmacyLikeLocal(x))
          const hospitals = compact.filter((x) => !isPharmacyLikeLocal(x))
          const usedHospital = new Set()
          const enriched = []
          for (const p of pharmacies) {
            const pDay = normalizeDateDigitsSafe(p?.startDate || p?.endDate)
            const key = normalizeDrugKeyLocal(p)
            let best = null
            let bestDelta = null
            for (let i = 0; i < hospitals.length; i++) {
              if (usedHospital.has(i)) continue
              const h = hospitals[i]
              const hDay = normalizeDateDigitsSafe(h?.startDate || h?.endDate)
              if (!pDay || !hDay) continue
              if (normalizeDrugKeyLocal(h) !== key) continue
              const delta = daysBetweenLocal(pDay, hDay)
              if (delta === null) continue
              if (delta > 5) continue
              if (bestDelta === null || delta < bestDelta) {
                bestDelta = delta
                best = { idx: i, h, hDay }
              }
            }
            if (best) {
              usedHospital.add(best.idx)
              enriched.push({
                ...p,
                prescriberOrgName: best.h?.hospitalName || null,
                prescriberDate: best.hDay || null,
              })
            } else {
              enriched.push(p)
            }
          }
          for (let i = 0; i < hospitals.length; i++) {
            if (usedHospital.has(i)) continue
            enriched.push(hospitals[i])
          }
          return enriched
        }

        const dateToCodes = new Map()
        for (const e of rawList) {
          if (!e || e.source !== 'BASIC_TREAT') continue
          const day = normalizeDateDigitsSafe(e.startDate || e.endDate)
          const code = (e.diagnosisCode || '').toString().trim()
          if (!day || !code) continue
          if (!dateToCodes.has(day)) dateToCodes.set(day, new Set())
          dateToCodes.get(day).add(code)
        }
        const drugRaw = rawList.filter((e) => e && e.source === 'PRESCRIBE_DRUG' && e.drugName)
        const deduped = dedupeAndLinkLocal(drugRaw)
        const diagGroups = new Map()
        for (const line of deduped) {
          const day = normalizeDateDigitsSafe(line.startDate || line.endDate)
          const explicitCode = (line.diagnosisCode || '').toString().trim()
          let groupCode = explicitCode || null
          if (!groupCode && day && dateToCodes.has(day)) {
            const set = dateToCodes.get(day)
            if (set && set.size === 1) groupCode = Array.from(set)[0]
          }
          if (!groupCode) continue
          if (!diagGroups.has(groupCode)) diagGroups.set(groupCode, [])
          diagGroups.get(groupCode).push(line)
        }
        const computeTotalByDayMax = (lines, valueKey) => {
          if (!Array.isArray(lines) || lines.length === 0) return 0
          const maxByDay = new Map()
          for (const x of lines) {
            const day = normalizeDateDigitsSafe(x?.startDate || x?.endDate) || '__NO_DAY__'
            const v = x?.[valueKey]
            const n = typeof v === 'number' ? v : v !== null && v !== undefined && String(v).trim() !== '' ? Number(v) : null
            if (!Number.isFinite(n)) continue
            const prev = maxByDay.get(day)
            maxByDay.set(day, prev === undefined ? Math.max(0, n) : Math.max(prev, Math.max(0, n)))
          }
          let sum = 0
          for (const v of maxByDay.values()) sum += v
          return sum
        }
        const totals = []
        for (const [code, lines] of diagGroups.entries()) {
          const total = computeTotalByDayMax(lines, 'totalDrugDays')
          if (total >= 14) totals.push({ code, total })
        }
        totals.sort((a, b) => b.total - a.total)
        return { has: totals.length > 0, top: totals.slice(0, 3).map((x) => `${x.code}(투약${x.total})`) }
      })()

      // 1) 5년 내 6대 중대질병(3N5의 '5'에 해당): 있으면 즉시 불가
      const major6 = subView?.MAJOR6_5Y
      if (hasAnyGroup(major6)) {
        setThreeNnResult({
          kind: 'SIMPLE_3N5',
          asOf: baseDate.toISOString().slice(0, 10),
          status: 'DECLINED',
          possibleCombos: [],
          reasons: [
            '최근 5년 내 6대 중대질병(암/뇌졸중/심근경색/협심증/심장판막증/간경화) 내역이 감지되었습니다.',
            '3N5(간편심사) 기준에서 해당 조건은 일반적으로 가입 제한(거절) 요인으로 처리됩니다.',
          ],
          message:
            '최근 5년 내 6대 중대질병(암/뇌졸중/심근경색/협심증/심장판막증/간경화) 내역이 있어 3N5 기준의 유병자 상품 가입이 불가능합니다.',
          footnote:
            '보험사별 예외 질환에 따라 가입이 가능할 수 있으니 설계매니저에게 문의해주세요.',
        })
        if (openModal) setThreeNnModalOpen(true)
        setThreeNnLoading(false)
        return
      }

      // 2) 3개월: 유병자 3개월은 "진료 내역(투약 제외)"을 기준으로 단순 판단
      const recent3mGroups = Array.isArray(subView?.recentHighRisk3M) ? subView.recentHighRisk3M : null
      // (구버전 summary에는 substandardView.recentHighRisk3M가 없을 수 있어, byPeriodGroupedDiagnosis['3M']로 fallback)
      const byPeriod3m = byPeriodGroupedDiagnosis?.['3M']
      const recent3mSource = recent3mGroups || (Array.isArray(byPeriod3m) ? byPeriod3m : [])
      const hasRecent3mNonDrug = anyNonDrugWithinDays(recent3mSource, 90)
      const recent3mNonDrugExamples = hasRecent3mNonDrug ? buildNonDrugExamplesWithinDays(recent3mSource, 90, 8) : []
      if (hasRecent3mNonDrug && !ignoreRecent3mOpinion) {
        setThreeNnResult({
          kind: 'SIMPLE_3N5',
          asOf: baseDate.toISOString().slice(0, 10),
          status: 'DECLINED',
          possibleCombos: [],
          reasons: [
            '최근 3개월 내 진료내역이 감지되었습니다. (투약 내역은 제외)',
            '3N5(간편심사)에서 “최근 3개월” 조건은 가입 가능 조합 산출의 선행 조건으로 사용됩니다.',
          ],
          message: '최근 3개월 내 진료내역이 있어 유병자 상품 가입이 불가능합니다. (투약 제외 기준)',
          footnote:
            '보험사별 예외 질환에 따라 가입이 가능할 수 있으니 설계매니저에게 문의해주세요.',
        })
        if (openModal) setThreeNnModalOpen(true)
        setThreeNnLoading(false)
        return
      }

      // 3) N년 내 입원/수술 이력 기반으로 315/325/... 산출
      // - 규칙: (3개월/1년) 부적합이면 불가
      // - 그 외에는 "통과 가능한 가장 큰 N"의 3N5를 안내한다.
      const hosp5 = subView?.inpatientOrSurgeryLike5Y
      const hosp4 = subView?.inpatientOrSurgeryLike4Y
      const hosp3 = subView?.inpatientOrSurgeryLike3Y
      const hosp2 = subView?.inpatientOrSurgeryLike2Y
      const hosp1 = subView?.inpatientOrSurgeryLike1Y

      const has1Y = anyInpatientOrSurgeryWithinDays(Array.isArray(hosp1) ? hosp1 : Array.isArray(hosp2) ? hosp2 : [], 365)
      if (has1Y) {
        setThreeNnResult({
          kind: 'SIMPLE_3N5',
          asOf: baseDate.toISOString().slice(0, 10),
          status: 'POSSIBLE',
          possibleCombos: ['3-0-5'],
          reasons: [
            '최근 1년 내 입원/수술(또는 유사 내역) 이력이 감지되었습니다.',
            '이 흐름(설계의뢰 > 유병자 3N5 1차 요약)에서는 최근 1년 내 입원/수술 이력이 있으면 “3-0-5” 조합을 우선 안내합니다.',
          ],
          message: '최근 1년 내 입원/수술 내역이 있어 “3-0-5” 조합을 우선 추천합니다.',
          footnote:
            '보험사별 예외 질환에 따라 가입이 가능할 수 있으니 설계매니저에게 문의해주세요.',
        })
        if (openModal) setThreeNnModalOpen(true)
        setThreeNnLoading(false)
        return
      }

      const within2Y = anyInpatientOrSurgeryWithinDays(Array.isArray(hosp2) ? hosp2 : [], 365 * 2)
      const within3Y = anyInpatientOrSurgeryWithinDays(Array.isArray(hosp3) ? hosp3 : Array.isArray(hosp5) ? hosp5 : [], 365 * 3)
      const within4Y = anyInpatientOrSurgeryWithinDays(Array.isArray(hosp4) ? hosp4 : Array.isArray(hosp5) ? hosp5 : [], 365 * 4)
      const within5Y = anyInpatientOrSurgeryWithinDays(Array.isArray(hosp5) ? hosp5 : [], 365 * 5)

      const possibleCombos = (() => {
        // “가능한 조합 목록만” 노출 (단순 1차 판정)
        if (within2Y) return ['3-1-5']
        if (within3Y) return ['3-2-5']
        if (within4Y) return ['3-3-5']
        if (within5Y) return ['3-4-5']
        // 5년 내 입원/수술 이력이 없으면 3N5 외 확장 조합도 가능할 수 있음(보험사별 상이)
        return ['3-5-5', '3-7-5', '3-10-5', '3-10-10']
      })()

      const reasons = (() => {
        const out = [
          '최근 5년 내 6대 중대질병 내역이 감지되지 않았습니다.',
          hasRecent3mNonDrug
            ? ignoreRecent3mOpinion
              ? '최근 3개월 내 진료내역이 감지되었습니다. (투약 제외) - 인수기준 예외 처리로 3N5 “3개월(소견)” 조건은 판정에서 제외했습니다.'
              : '최근 3개월 내 진료내역이 감지되었습니다. (투약 제외 기준)'
            : '최근 3개월 내 진료내역이 감지되지 않았습니다. (투약 제외 기준)',
          '최근 1년 내 입원/수술 내역이 감지되지 않았습니다.',
        ]
        if (longDrug14Info.has) {
          out.push(
            `최근 5년 내 14일 이상 투약 내역이 감지되었습니다.${longDrug14Info.top.length ? ` (예: ${longDrug14Info.top.join(', ')})` : ''}`,
          )
        } else {
          out.push('최근 5년 내 14일 이상 투약 내역이 감지되지 않았습니다.')
        }
        if (within2Y) out.push('최근 2년 내 입원/수술 이력이 있어, 보수적으로 “3-1-5” 조합을 우선 안내합니다.')
        else if (within3Y) out.push('최근 3년 내 입원/수술 이력이 있어, “3-2-5” 조합을 우선 안내합니다.')
        else if (within4Y) out.push('최근 4년 내 입원/수술 이력이 있어, “3-3-5” 조합을 우선 안내합니다.')
        else if (within5Y) out.push('최근 5년 내 입원/수술 이력이 있어, “3-4-5” 조합을 우선 안내합니다.')
        else out.push('최근 5년 내 입원/수술 이력이 감지되지 않아, 확장 조합까지 함께 안내합니다.')
        return out
      })()

      setThreeNnResult({
        kind: 'SIMPLE_3N5',
        asOf: baseDate.toISOString().slice(0, 10),
        status: 'POSSIBLE',
        possibleCombos,
        reasons,
        exception:
          hasRecent3mNonDrug && ignoreRecent3mOpinion
            ? { ignoreRecent3mOpinion: true, recent3mNonDrugExamples }
            : null,
        message: '유병자 상품 가입 가능 조합(1차)입니다.',
        footnote:
          '보험사별 예외 질환에 따라 가입이 가능할 수 있으니 설계매니저에게 문의해주세요.',
      })
      if (openModal) setThreeNnModalOpen(true)
      setThreeNnLoading(false)
    },
    [show, selectedReport],
  )

  const handleEvaluate3nnTemplates = useCallback(
    async (reportId, opts = {}) => {
      const { openModal = true, asOf = null, insurerCodes = null } = opts || {}
      if (!reportId) {
        show('리포트를 먼저 선택해 주세요.', 'error')
        return
      }
      setThreeNnTemplateLoading(true)
      try {
        const payload = {
          reportId,
          asOf: asOf || null,
          insurerCodes: Array.isArray(insurerCodes) && insurerCodes.length > 0 ? insurerCodes : null,
        }
        const res = await api.post('/underwriting/3nn/evaluate', payload)
        const data = res.data?.data || res.data
        // data: { reportId, asOf, items:[{insurerCode, insurerName, results:[...], best:{...}}] }
        setThreeNnTemplateResult({ kind: 'TEMPLATE_3NN', ...(data || {}) })
        if (openModal) {
          setThreeNnModalMode('TEMPLATE_3NN')
          setThreeNnModalOpen(true)
        }
      } catch (e) {
        const msg = e?.response?.data?.message || e?.message || null
        show(`보험사별 예외 적용 결과를 불러오지 못했습니다.${msg ? ` (${msg})` : ''}`, 'error')
      } finally {
        setThreeNnTemplateLoading(false)
      }
    },
    [show],
  )

  const computeHealthy7305YSummaries = useCallback(() => {
    // 건강체 질문서(5년 내 같은 원인 7일 이상 치료 또는 30일 이상 투약)용 요약을 만든다.
    // - 기존 건강체 탭의 로직과 동일한 기준(일자별 최대 합산) 사용
    const key = '5Y'
    const rawByPeriod = byPeriodFromSummary && byPeriodFromSummary[key]
    const rawList = Array.isArray(rawByPeriod) ? rawByPeriod : rawEpisodesFromSummary

    const dateToCodes = new Map()
    const codeToName = new Map()
    for (const e of rawList) {
      if (!e || e.source !== 'BASIC_TREAT') continue
      const day = normalizeDateDigitsSafe(e.startDate || e.endDate)
      const code = (e.diagnosisCode || '').toString().trim()
      if (!day || !code) continue
      if (!dateToCodes.has(day)) dateToCodes.set(day, new Set())
      dateToCodes.get(day).add(code)
      if (!codeToName.has(code) && e.diagnosisName) codeToName.set(code, e.diagnosisName)
    }

    // 치료(외래/입원)일수: BASIC_TREAT 기반으로 동일 진단코드 그룹핑(약국 제외)
    const treatGroups = new Map()
    for (const e of rawList) {
      if (!e || e.source !== 'BASIC_TREAT') continue
      if (isPharmacyLike(e)) continue
      const code = (e.diagnosisCode || '').toString().trim()
      if (!code) continue
      if (!treatGroups.has(code)) treatGroups.set(code, [])
      treatGroups.get(code).push(e)
    }

    const drugRaw = rawList.filter((e) => e && e.source === 'PRESCRIBE_DRUG' && e.drugName)
    const deduped = dedupeAndLinkDrugsPharmacyFirst(drugRaw)

    const diagGroups = new Map()
    for (const line of deduped) {
      const day = normalizeDateDigitsSafe(line.startDate || line.endDate)
      const explicitCode = (line.diagnosisCode || '').toString().trim()
      let groupCode = explicitCode || null
      if (!groupCode && day && dateToCodes.has(day)) {
        const set = dateToCodes.get(day)
        if (set && set.size === 1) groupCode = Array.from(set)[0]
      }
      if (!groupCode) continue
      if (!diagGroups.has(groupCode)) diagGroups.set(groupCode, [])
      diagGroups.get(groupCode).push(line)
    }

    const computeTotalByDayMax = (lines, valueKey) => {
      if (!Array.isArray(lines) || lines.length === 0) return 0
      const maxByDay = new Map()
      let hasAny = false
      for (const x of lines) {
        const day = normalizeDateDigitsSafe(x?.startDate || x?.endDate) || '__NO_DAY__'
        const v = x?.[valueKey]
        const n = typeof v === 'number' ? v : v !== null && v !== undefined && String(v).trim() !== '' ? Number(v) : null
        if (!Number.isFinite(n)) continue
        const prev = maxByDay.get(day)
        maxByDay.set(day, prev === undefined ? Math.max(0, n) : Math.max(prev, Math.max(0, n)))
        hasAny = true
      }
      if (!hasAny) return 0
      let sum = 0
      for (const v of maxByDay.values()) sum += Math.max(0, v)
      return sum
    }

    const minMaxDateFromLines = (lines) => {
      let min = null
      let max = null
      for (const x of lines || []) {
        const day = normalizeDateDigitsSafe(x?.startDate || x?.endDate)
        if (!day) continue
        if (!min || day < min) min = day
        if (!max || day > max) max = day
      }
      return { min, max }
    }

    const summaries = []
    const allCodes = new Set([...Array.from(diagGroups.keys()), ...Array.from(treatGroups.keys())])
    for (const code of allCodes) {
      const drugLines = diagGroups.get(code) || []
      const treatLines = treatGroups.get(code) || []
      const totalDrugDays = computeTotalByDayMax(drugLines, 'totalDrugDays') || 0
      const totalTreatmentDays = computeTotalByDayMax(treatLines, 'totalTreatmentDays') || 0
      if (totalDrugDays < 30 && totalTreatmentDays < 7) continue

      const mergedDetails = [...treatLines, ...drugLines]
      const { min, max } = minMaxDateFromLines(mergedDetails)
      summaries.push({
        id: `DRUG_7_30_5Y-${code}`,
        groupType: 'DRUGS_DIAG',
        diagnosisCode: code,
        diagnosisName: codeToName.get(code) || '진단',
        startDate: min,
        endDate: max,
        count: mergedDetails.length,
        totalDrugDays,
        totalTreatmentDays,
        details: mergedDetails,
      })
    }
    summaries.sort((a, b) => String(a.diagnosisCode || '').localeCompare(String(b.diagnosisCode || '')))
    return summaries
  }, [byPeriodFromSummary, rawEpisodesFromSummary])

  const computeHealthyQuestionnaireSummary = useCallback(() => {
    const hv = healthyViewFromSummary || {}
    const g3m = Array.isArray(hv?.['3M']) ? hv['3M'] : []
    const g1yCheck = Array.isArray(hv?.['1Y_CHECK']) ? hv['1Y_CHECK'] : []
    const gHosp5y = Array.isArray(hv?.['HOSP_5Y']) ? hv['HOSP_5Y'] : []
    const gMajor11 = Array.isArray(hv?.['MAJOR11_5Y']) ? hv['MAJOR11_5Y'] : []
    const g730 = computeHealthy7305YSummaries()

    const extractRange = (arr) => {
      if (!Array.isArray(arr) || arr.length === 0) return null
      let min = null
      let max = null
      for (const g of arr) {
        const s = normalizeDateDigitsSafe(g?.startDate || g?.endDate)
        const e = normalizeDateDigitsSafe(g?.endDate || g?.startDate)
        if (s) {
          if (!min || s < min) min = s
          if (!max || s > max) max = s
        }
        if (e) {
          if (!min || e < min) min = e
          if (!max || e > max) max = e
        }
      }
      if (!min && !max) return null
      const a = min ? formatDate(min) : '-'
      const b = max ? formatDate(max) : '-'
      return `${a}~${b}`
    }

    const toExample = (g) => {
      const code = (g?.diagnosisCode || '').toString().trim()
      const name = (g?.diagnosisName || '').toString().trim()
      const codeName = code ? `${code}${name ? ` ${name}` : ''}` : (name || '-')
      const treat = g?.totalTreatmentDays ?? ''
      const drug = g?.totalDrugDays ?? ''
      const extra =
        treat || drug
          ? ` (진료${treat || 0} / 투약${drug || 0})`
          : ''
      return `${codeName}${extra}`
    }

    const pick = (arr, limit = 3) => (Array.isArray(arr) ? arr.slice(0, limit).map(toExample) : [])

    return {
      recent3m: { has: g3m.length > 0, examples: pick(g3m, 3), range: extractRange(g3m) },
      check1y: { has: g1yCheck.length > 0, examples: pick(g1yCheck, 3), range: extractRange(g1yCheck) },
      treat7orDrug30_5y: { has: g730.length > 0, examples: pick(g730, 3), range: extractRange(g730) },
      hosp5y: { has: gHosp5y.length > 0, examples: pick(gHosp5y, 3), range: extractRange(gHosp5y) },
      major11_5y: { has: gMajor11.length > 0, examples: pick(gMajor11, 3), range: extractRange(gMajor11) },
    }
  }, [healthyViewFromSummary, computeHealthy7305YSummaries])

  const openDesignRequestModal = useCallback(
    async (defaultType = 'substandard') => {
      const t = defaultType === 'healthy' ? 'healthy' : 'substandard'
      setDesignCoverageType(t)
      setSubstandardIgnoreRecent3mOpinion(false)
      setDesignRequestModalOpen(true)
      // 유병자 선택 시: 팝업에서 바로 요약을 보여주기 위해 3N5(1차) 결과를 미리 계산한다.
      if (t === 'substandard' && selectedReportId) {
        try {
          await handleEvaluate3nn(selectedReportId, { openModal: false, ignoreRecent3mOpinion: false })
        } catch (_) {
          // ignore
        }
      }
      // 건강체 선택 시: 질문서 항목별 “있음/없음 + 예시” 요약을 만들어 둔다.
      if (t === 'healthy') {
        try {
          setHealthyAutoSummary(computeHealthyQuestionnaireSummary())
        } catch (_) {
          setHealthyAutoSummary(null)
        }
      }
    },
    [handleEvaluate3nn, selectedReportId, computeHealthyQuestionnaireSummary],
  )

  const openQuestionnaire = useCallback((type) => {
    setQuestionnaireModalType(type)
  }, [])

  const JOB_LIST_PAGE_SIZE = 10
  const [reportsPage, setReportsPage] = useState(0)
  const [customersPage, setCustomersPage] = useState(0)

  // 탭 전환/검색어 변경 시 1페이지로 되돌림
  useEffect(() => {
    setReportsPage(0)
    setCustomersPage(0)
  }, [activeTab, search])

  // 분석 목록(서버 페이징 + 고객명 검색(q))
  const jobsQuery = useQuery({
    queryKey: ['health-jobs', { page: reportsPage, size: JOB_LIST_PAGE_SIZE, q: search || '' }],
    enabled: activeTab === 'reports',
    queryFn: async () => {
      const params = new URLSearchParams()
      params.set('page', String(reportsPage))
      params.set('size', String(JOB_LIST_PAGE_SIZE))
      if (search && String(search).trim()) params.set('q', String(search).trim())
      const res = await api.get(`/health-jobs?${params.toString()}`)
      return res.data?.data || res.data
    },
  })

  const jobs = useMemo(() => jobsQuery.data?.items ?? EMPTY_LIST, [jobsQuery.data])
  const jobsHasNext = Boolean(jobsQuery.data?.hasNext)
  const filteredJobs = jobs // 서버에서 q로 필터링

  // 고객 목록(서버에서 dedup + 페이징)
  const customersQuery = useQuery({
    queryKey: ['health-job-customers', { page: customersPage, size: JOB_LIST_PAGE_SIZE, q: search || '' }],
    enabled: activeTab === 'customers',
    queryFn: async () => {
      const params = new URLSearchParams()
      params.set('page', String(customersPage))
      params.set('size', String(JOB_LIST_PAGE_SIZE))
      if (search && String(search).trim()) params.set('q', String(search).trim())
      const res = await api.get(`/health-jobs/customers?${params.toString()}`)
      return res.data?.data || res.data
    },
  })

  const customers = useMemo(() => customersQuery.data?.items ?? EMPTY_LIST, [customersQuery.data])
  const customersHasNext = Boolean(customersQuery.data?.hasNext)
  const filteredCustomers = customers

  const jobStatusLabel = (status) => {
    switch (status) {
      case 'AUTH_REQUESTED':
        return '인증요청'
      case 'AUTH_WAITING':
        return '인증 대기'
      case 'AUTH_INPUT_REQUIRED':
        return '추가입력 필요'
      case 'AUTH_COMPLETED':
        return '인증 완료'
      case 'ANALYSIS_RUNNING':
        return '분석 중'
      case 'ANALYSIS_COMPLETED':
        return '분석 완료'
      case 'FAILED':
        return '실패'
      default:
        return status || '-'
    }
  }

  const parseTwoWayInfo = useCallback((job) => {
    const raw = job?.twoWayInfoJson
    if (!raw) return null
    try {
      return typeof raw === 'string' ? JSON.parse(raw) : raw
    } catch (_) {
      return null
    }
  }, [])

  const openTwoWayInputModal = useCallback((job) => {
    const info = parseTwoWayInfo(job)
    setTwoWayJobId(job.jobId)
    setTwoWayInfo(info)
    setTwoWayInput((prev) => ({ ...prev, secureNo: '', smsAuthNo: '' }))
    setTwoWayModalOpen(true)
  }, [parseTwoWayInfo])

  const submitTwoWayInput = async () => {
    if (!twoWayJobId) return
    try {
      await api.post(`/health-jobs/${twoWayJobId}/two-way-input`, {
        secureNo: twoWayInput.secureNo || '',
        smsAuthNo: twoWayInput.smsAuthNo || '',
        simpleAuth: twoWayInput.simpleAuth || '1',
      })
      show('추가 인증 입력을 제출했습니다. 처리 재개 중...', 'success')
      setTwoWayModalOpen(false)
      queryClient.invalidateQueries({ queryKey: ['health-job', twoWayJobId] })
      queryClient.invalidateQueries({ queryKey: ['health-jobs'] })
    } catch (e) {
      show('추가 인증 입력 제출에 실패했습니다.', 'error')
    }
  }

  const canRetryIdentityJob = (job) => {
    if (!job) return false
    if (job.status !== 'FAILED') return false
    const msg = String(job.lastError || '')
    return msg.includes('IDENTITY_TTL_EXPIRED')
  }

  const quota = useMemo(() => {
    const used = jobs.length || 0
    const total = 100 // TODO: 플랜/구독 연동 시 서버 값으로 교체
    return { used, total }
  }, [jobs])

  const createReportMutation = useMutation({
    mutationFn: async (payload) => {
      const res = await api.post('/health-jobs', payload)
      const data = res.data?.data || res.data
      return data
    },
    onSuccess: (data) => {
      setCurrentJob({ jobId: data.jobId, status: data.status })
      show(`${form.name} 고객님의 인증 요청이 완료되었습니다.`, 'success')
      // 인증 요청이 성공했으면 입력 폼은 초기화(다음 고객 입력 대비)
      setForm((prev) => ({
        ...prev,
        customerId: '',
        name: '',
        phoneNumber: '',
        identity: '',
        birthDate: '',
      }))
      // 분석 목록 갱신 및 탭 전환
      queryClient.invalidateQueries({ queryKey: ['health-jobs'] })
      setActiveTab('reports')
    },
    onError: (e) => {
      const msg =
        e?.response?.data?.message ||
        e?.response?.data?.error?.message ||
        e?.message ||
        null
      show(`인증 요청에 실패했습니다. 입력 정보를 확인해 주세요.${msg ? ` (${msg})` : ''}`, 'error')
    },
  })

  const jobStatusQuery = useQuery({
    queryKey: ['health-job', currentJob?.jobId],
    queryFn: async () => {
      if (!currentJob?.jobId) return null
      const res = await api.get(`/health-jobs/${currentJob.jobId}`)
      const data = res.data?.data || res.data
      return data
    },
    enabled: !!currentJob?.jobId,
    refetchInterval: currentJob?.jobId ? 5000 : false,
  })

  // 단건 Job이 AUTH_INPUT_REQUIRED로 내려오면 자동으로 입력 모달을 띄운다.
  useEffect(() => {
    const job = jobStatusQuery.data
    if (!job) return
    if (job.status === 'AUTH_INPUT_REQUIRED') {
      if (twoWayModalOpen) return
      openTwoWayInputModal(job)
    }
  }, [jobStatusQuery.data, openTwoWayInputModal, twoWayModalOpen])

  const handleChange = (e) => {
    const { name, value } = e.target
    if (name === 'identity') {
      const digits = (value || '').toString().replaceAll(/[^0-9]/g, '')
      const derived = deriveBirthDateFromIdentity(digits)
      setForm((prev) => ({ ...prev, identity: value, birthDate: derived }))
      return
    }
    // 생년월일은 주민번호로 자동 추출하며, 화면에서는 읽기 전용으로만 보여준다.
    if (name === 'birthDate') return
    setForm((prev) => ({ ...prev, [name]: value }))
  }

  const handleCreateReport = (e) => {
    e.preventDefault()

    // --- 길이/형식 검증 ---
    const identityDigits = (form.identity || '').replaceAll(/[^0-9]/g, '')
    const phoneDigits = (form.phoneNumber || '').replaceAll(/[^0-9]/g, '')
    const birthDigits = deriveBirthDateFromIdentity(identityDigits)

    if (identityDigits.length !== 13) {
      show('주민등록번호는 숫자 13자리로 입력해 주세요.', 'error')
      return
    }
    if (birthDigits.length !== 8) {
      show('주민등록번호 앞 6자리(YYMMDD)에서 생년월일을 추출할 수 없습니다. 입력값을 확인해 주세요.', 'error')
      return
    }
    if (!(phoneDigits.length === 10 || phoneDigits.length === 11)) {
      show('연락처는 숫자 10~11자리로 입력해 주세요.', 'error')
      return
    }

    const payload = {
      customerId: form.customerId ? Number(form.customerId) : null,
      name: form.name,
      phoneNumber: phoneDigits,
      identity: identityDigits,
      birthDate: birthDigits,
      telecom: form.telecom,
      authProvider: form.authProvider,
    }
    createReportMutation.mutate(payload)
  }

  const handleViewResult = async (reportId, meta = null) => {
    try {
      setSelectedReportMeta(meta)
      const res = await api.get(`/health-reports/${reportId}`)
      const data = res.data?.data || res.data
      setSelectedReport(data)
      setSelectedReportId(reportId)
      setLastReport({ reportId: data.id, riskLevel: data.riskLevel })
      // 생년월일은 HealthReport에 저장되지 않고(보관 정책), HealthJob(birth_date)에 존재한다.
      // 결과 조회 시 jobId가 있으면 해당 Job에서 birthDate를 보강한다.
      const jobId = meta?.jobId
      if (jobId) {
        try {
          const jr = await api.get(`/health-jobs/${jobId}`)
          const jd = jr.data?.data || jr.data
          const bd = jd?.birthDate || jd?.birth_date || null
          if (bd) {
            setSelectedReportMeta((prev) => ({ ...(prev || {}), birthDate: bd }))
          }
        } catch (_) {
          // ignore
        }
      }
      show('분석 결과를 불러왔습니다.', 'success')
    } catch (e) {
      show('분석 결과를 불러오지 못했습니다.', 'error')
    }
  }

  const handleGoToRequestNewFrom3nn = useCallback((opts = {}) => {
    const coverageType = opts?.coverageType === 'healthy' ? 'healthy' : 'substandard'
    const selectedInsurerCodes = Array.isArray(opts?.selectedInsurerCodes)
      ? opts.selectedInsurerCodes.map((x) => (x != null ? String(x) : '')).filter((x) => x.trim() !== '')
      : []
    if (!selectedReportId) {
      show('리포트를 먼저 선택해 주세요.', 'error')
      return
    }
    const customerName = ((selectedReportMeta?.customerName || '').toString().trim()) || '고객'
    const title = `${customerName} 고객님 설계부탁드립니다.`
    const parsedSummary = (() => {
      const raw = selectedReport?.summaryJson
      if (!raw) return null
      try {
        return JSON.parse(raw)
      } catch {
        return null
      }
    })()
    const profile = parsedSummary?.profile || null
    const createdAt = parsedSummary?.createdAt || selectedReportMeta?.createdAt || selectedReport?.createdAt || null
    const analyzedYmd = createdAt ? formatDate(createdAt) : formatDate(new Date())
    const birth = (() => {
      const v = selectedReportMeta?.birthDate || parsedSummary?.profile?.birthDate || parsedSummary?.profile?.birth || null
      const s = v ? String(v).trim() : ''
      if (!s) return '-'
      // yyyyMMdd
      if (/^\d{8}$/.test(s)) return formatDate(s)
      return formatDate(s) || s
    })()

    const takeCodes = (groups, limit = 3) => {
      if (!Array.isArray(groups) || groups.length === 0) return []
      const out = []
      for (const g of groups) {
        const code = (g?.diagnosisCode || '').trim()
        if (!code) continue
        const treatDays = g?.totalTreatmentDays ?? g?.sumTreatmentDays ?? ''
        const drugDays = g?.totalDrugDays ?? g?.sumDrugDays ?? ''
        const daysPart =
          (treatDays || drugDays) ? `(${treatDays ? `진료${treatDays}` : ''}${treatDays && drugDays ? '/' : ''}${drugDays ? `투약${drugDays}` : ''})` : ''
        out.push(`${code}${daysPart}`)
        if (out.length >= limit) break
      }
      return out
    }

    const aggregateInhospByDiagnosisCode = (groups) => {
      if (!Array.isArray(groups) || groups.length === 0) return []
      const map = new Map()
      for (const g of groups) {
        const code = (g?.diagnosisCode || '').toString().trim()
        if (!code) continue
        const start = (g?.startDate || '').toString().trim()
        const end = (g?.endDate || '').toString().trim()
        const daysRaw = g?.totalTreatmentDays ?? g?.sumTreatmentDays ?? 0
        const days = Number(daysRaw)
        const key = `${code}|${start}|${end}|${Number.isFinite(days) ? days : String(daysRaw)}`

        if (!map.has(code)) {
          map.set(code, {
            diagnosisCode: code,
            diagnosisName: (g?.diagnosisName || '').toString().trim(),
            totalInpatientDays: 0,
            dateRanges: new Set(),
            dedupeKeys: new Set(),
          })
        }
        const cur = map.get(code)
        if (cur.dedupeKeys.has(key)) continue
        cur.dedupeKeys.add(key)
        if (!cur.diagnosisName && g?.diagnosisName) cur.diagnosisName = String(g.diagnosisName).trim()
        if (Number.isFinite(days)) cur.totalInpatientDays += Math.max(0, days)
        const s = start ? formatDate(start) : ''
        const e = end ? formatDate(end) : ''
        if (s || e) cur.dateRanges.add(`${s || '-'}~${e || '-'}`)
      }
      const out = Array.from(map.values()).map((x) => ({
        diagnosisCode: x.diagnosisCode,
        diagnosisName: x.diagnosisName,
        totalInpatientDays: x.totalInpatientDays,
        dateRanges: Array.from(x.dateRanges),
      }))
      out.sort((a, b) => (b.totalInpatientDays || 0) - (a.totalInpatientDays || 0))
      return out
    }

    const byPeriod = profile?.byPeriodGroupedDiagnosis || {}
    const subView = profile?.substandardView || {}

    // 1) 3개월 내 모든 진료/투약 포함
    const g3m = Array.isArray(byPeriod?.['3M']) ? byPeriod['3M'] : []
    const has3m = g3m.length > 0
    const codes3m = takeCodes(g3m, 3)

    // 2) N년 내 입원/수술 (가장 가까운 기간을 N으로)
    const windows = [
      { key: '1Y', label: '1년' },
      { key: '2Y', label: '2년' },
      { key: '3Y', label: '3년' },
      { key: '4Y', label: '4년' },
      { key: '5Y', label: '5년' },
    ]
    const pickInhosp = () => {
      for (const w of windows) {
        const gl = Array.isArray(byPeriod?.[w.key]) ? byPeriod[w.key] : []
        const hit = gl.filter((x) => !!x?.inpatient || (x?.surgeryName && String(x.surgeryName).trim()))
        if (hit.length > 0) return { label: w.label, groups: hit }
      }
      return null
    }
    const inhosp = pickInhosp()
    const inhospLabel = inhosp?.label || '5년'
    const inhospAgg = inhosp ? aggregateInhospByDiagnosisCode(inhosp.groups) : []

    // 4) 6대질병
    const major6 = Array.isArray(subView?.MAJOR6_5Y) ? subView.MAJOR6_5Y : []
    const major6Codes = takeCodes(major6, 5)

    const lines = []
    lines.push('[설계 유형]')
    lines.push(`- 선택: ${coverageType === 'healthy' ? '표준체(건강체)' : '유병자(간편보험)'}`)
    lines.push('')
    lines.push(`고객명 : ${customerName}`)
    lines.push(`생년월일 : ${birth}`)
    lines.push(`분석일 : ${analyzedYmd}`)

    const byPeriod5y = Array.isArray(byPeriod?.['5Y']) ? byPeriod['5Y'] : []
    const inhosp5yGroups = byPeriod5y.filter((x) => !!x?.inpatient || (x?.surgeryName && String(x.surgeryName).trim()))
    const inhospAgg5y = aggregateInhospByDiagnosisCode(inhosp5yGroups)

    if (coverageType === 'substandard') {
      lines.push('')
      lines.push('[유병자 질문서 요약 및 상품추천]')
      lines.push(
        `- 최근 3개월 내 진료/투약: ${has3m ? `있음${codes3m.length ? ` (${codes3m.join(', ')})` : ''}` : '없음'}`,
      )
      lines.push(`- 최근 5년 내 입원/수술: ${inhospAgg5y.length ? '있음' : '없음'}`)
      if (inhospAgg5y.length) {
        inhospAgg5y.slice(0, 5).forEach((g, idx) => {
          const code = g?.diagnosisCode || '-'
          const name = g?.diagnosisName ? ` ${g.diagnosisName}` : ''
          const days = g?.totalInpatientDays ?? '-'
          const ranges = Array.isArray(g?.dateRanges) && g.dateRanges.length ? g.dateRanges.join(', ') : '-'
          lines.push(`    ${idx + 1}) ${code}${name} / 총 입원일:${days} / 일자:${ranges}`)
        })
      }
      lines.push(
        `- 최근 5년 내 6대질병: ${major6.length ? `있음${major6Codes.length ? ` (${major6Codes.join(', ')})` : ''}` : '없음'}`,
      )
      lines.push('')

      // 3N5 결과 (가능 조합/이유)
      if (threeNnResult?.kind === 'SIMPLE_3N5') {
        if (Array.isArray(threeNnResult?.possibleCombos)) {
          lines.push(`- 가능 조합: ${threeNnResult.possibleCombos.length ? threeNnResult.possibleCombos.join(', ') : '없음'}`)
        }
        const reasons = Array.isArray(threeNnResult?.reasons) ? threeNnResult.reasons.slice(0, 3) : []

        // "최근 N년 내 입원/수술" 근거 문장 추가 (3N5 중 N 결정 이유 설명)
        // - N은 가능 조합(예: 3-3-5)의 가운데 숫자를 우선 사용
        // - 파싱 실패 시에는 2Y~5Y 중 가장 가까운 입원/수술 감지 구간을 역추정
        const nFromCombo = (() => {
          const combos = Array.isArray(threeNnResult?.possibleCombos) ? threeNnResult.possibleCombos : []
          const first = combos.length ? String(combos[0]).trim() : ''
          const m = first.match(/^\s*\d+\s*-\s*(\d+)\s*-\s*\d+\s*$/)
          if (!m) return null
          const n = Number(m[1])
          return Number.isFinite(n) && n >= 1 && n <= 5 ? n : null
        })()
        const nFromDetectedWindow = (() => {
          const windows = [1, 2, 3, 4, 5]
          for (const n of windows) {
            const key = `${n}Y`
            const g = Array.isArray(byPeriod?.[key]) ? byPeriod[key] : []
            const hit = g.filter((x) => !!x?.inpatient || (x?.surgeryName && String(x.surgeryName).trim()))
            if (hit.length > 0) return n
          }
          return 5
        })()
        const nYears = nFromCombo || nFromDetectedWindow
        const byPeriodNy = Array.isArray(byPeriod?.[`${nYears}Y`]) ? byPeriod[`${nYears}Y`] : []
        const inhospNy = byPeriodNy.filter((x) => !!x?.inpatient || (x?.surgeryName && String(x.surgeryName).trim()))
        const nReason =
          inhospNy.length === 0
            ? `최근 ${nYears}년 내 입원/수술 내역이 감지되지 않았습니다. (3N5 중 N을 결정한 근거)`
            : `최근 ${nYears}년 내 입원/수술 내역이 감지되었습니다. (3N5 중 N을 결정한 근거)`
        if (reasons.length < 3) reasons.push(nReason)
        else reasons[2] = nReason

        if (reasons.length > 0) {
          lines.push(`- 이유:`)
          reasons.forEach((r, idx) => lines.push(`   ${idx + 1}) ${r}`))
        }
        if (threeNnResult?.exception?.ignoreRecent3mOpinion) {
          lines.push('')
          lines.push('[인수기준 예외 처리]')
          lines.push('- 최근 3개월 항목은 “입원/수술/재검사 소견” 여부를 진료내역만으로 판단하기 어려워, 설계자가 확인 후 예외로 처리했습니다.')
        }
        lines.push('')
        if (selectedInsurerCodes.length > 0) {
          // 선택 보험사가 있으면, 설계의뢰 페이지에서 자동 선택되도록 전달(그리고 메모에도 요약 남김)
          const items = Array.isArray(threeNnTemplateResult?.items) ? threeNnTemplateResult.items : []
          const byCode = new Map(items.map((it) => [String(it?.insurerCode || ''), it]))
          lines.push('** 보험사별 예외질환/3NN 추천 결과를 반영하기 위해, 아래 보험사를 선택했습니다.')
          selectedInsurerCodes.slice(0, 50).forEach((code) => {
            const it = byCode.get(String(code)) || null
            const best = it?.best || null
            const name = it?.insurerName || null
            const tc = best?.templateCode || '-'
            const st = statusLabelKr(best?.status)
            const sc = best?.score ?? null
            lines.push(`   - ${name ? `${name} ` : ''}${code}: ${tc} / ${st}${sc !== null && sc !== undefined ? ` (점수 ${sc})` : ''}`)
          })
        } else {
          lines.push('** 보험사별 예외질환이 고려되어 있지 않습니다. 설계 시 진단 코드 참고하여 고려 부탁드립니다.')
        }
      } else if (threeNnResult?.message) {
        lines.push(`- 가능 조합: -`)
        lines.push(`- 이유:`)
        lines.push(`   1) ${threeNnResult.message}`)
        lines.push('')
        if (selectedInsurerCodes.length > 0) {
          lines.push('** 보험사별 예외질환/3NN 추천 결과를 반영하기 위해 선택한 보험사가 있습니다. (설계의뢰 생성 시 자동 선택)')
        } else {
          lines.push('** 보험사별 예외질환이 고려되어 있지 않습니다. 설계 시 진단 코드 참고하여 고려 부탁드립니다.')
        }
      }
    } else {
      lines.push('')
      const hs = healthyAutoSummary || computeHealthyQuestionnaireSummary()
      lines.push('[건강체(표준체) 질문서 요약]')
      lines.push(
        `- 최근 3개월(진료/검사/치료/입원/수술/투약): ${hs?.recent3m?.has ? '있음' : '없음'}`,
      )
      lines.push(
        `- 최근 1년(검사/추가검사/재검사 참고): ${hs?.check1y?.has ? '있음' : '없음'}`,
      )

      // 5년(같은 원인 7일+ 치료 또는 30일+ 투약): 있음이면 항목별로 정리
      const g730 = computeHealthy7305YSummaries()
      lines.push(`- 최근 5년(같은 원인 7일+ 치료 또는 30일+ 투약): ${g730.length ? '있음' : '없음'}`)
      if (g730.length) {
        g730.slice(0, 5).forEach((g, idx) => {
          const code = (g?.diagnosisCode || '-').toString().trim() || '-'
          const name = (g?.diagnosisName || '').toString().trim()
          const treat = Number.isFinite(Number(g?.totalTreatmentDays)) ? Number(g.totalTreatmentDays) : 0
          const drug = Number.isFinite(Number(g?.totalDrugDays)) ? Number(g.totalDrugDays) : 0
          const range = `${formatDate(g?.startDate) || '-'}~${formatDate(g?.endDate) || '-'}`
          lines.push(`     ${idx + 1}) [${code}${name ? ` ${name}` : ''} (진료${treat} / 투약${drug})] 기간 : ${range}`)
        })
      }

      // 5년(입원/수술): 있음이면 항목별로 정리
      const gHosp5y = Array.isArray(healthyViewFromSummary?.['HOSP_5Y']) ? healthyViewFromSummary['HOSP_5Y'] : []
      lines.push(`- 최근 5년(입원/수술): ${gHosp5y.length ? '있음' : '없음'}`)
      if (gHosp5y.length) {
        gHosp5y.slice(0, 5).forEach((g, idx) => {
          const code = (g?.diagnosisCode || '-').toString().trim() || '-'
          const name = (g?.diagnosisName || '').toString().trim()
          const treat = Number.isFinite(Number(g?.totalTreatmentDays)) ? Number(g.totalTreatmentDays) : 0
          const drug = Number.isFinite(Number(g?.totalDrugDays)) ? Number(g.totalDrugDays) : 0
          const range = `${formatDate(g?.startDate) || '-'}~${formatDate(g?.endDate) || '-'}`
          lines.push(`    ${idx + 1}) [${code}${name ? ` ${name}` : ''}] (진료${treat} / 투약${drug}) 기간 : ${range}`)
        })
      }

      lines.push(
        `- 최근 5년(11대 중대질병): ${hs?.major11_5y?.has ? '있음' : '없음'}`,
      )
    }
    lines.push('')
    // lines.push('첨부파일: (예정) 진료분석 리포트(PDF)')

    const prefill = {
      customerName,
      title,
      commonNote: lines.join('\n'),
      selectedInsurerCodes: selectedInsurerCodes.length > 0 ? selectedInsurerCodes : null,
    }
    const key = `requestNew.prefill.${Date.now()}`
    try {
      sessionStorage.setItem(key, JSON.stringify(prefill))
    } catch (_) {
      // ignore
    }
    navigate(`/requests/new?prefillKey=${encodeURIComponent(key)}`)
    setThreeNnModalOpen(false)
    setDesignRequestModalOpen(false)
  }, [navigate, selectedReport, selectedReportId, selectedReportMeta, show, threeNnResult, healthyAutoSummary, computeHealthyQuestionnaireSummary])

  const handleReanalyzeReport = async (reportId) => {
    const targetId = reportId || selectedReportId
    if (!targetId) {
      show('먼저 분석 결과를 선택해 주세요.', 'error')
      return
    }
    try {
      await api.post(`/health-reports/${targetId}/reanalyze`)
      // 최신 summaryJson 다시 로드
      const res = await api.get(`/health-reports/${targetId}`)
      const data = res.data?.data || res.data
      setSelectedReport(data)
      setSelectedReportId(targetId)
      show('재분석이 완료되었습니다. 최신 룰이 반영되었습니다.', 'success')
    } catch (e) {
      const msg = e?.response?.data?.message || e?.message || null
      show(`재분석에 실패했습니다.${msg ? ` (${msg})` : ''}`, 'error')
    }
  }

  const handleDownloadAnalysisResult = async (reportId, customerNameOverride, createdAtOverride) => {
    const targetId = reportId || selectedReportId
    if (!targetId) {
      show('먼저 분석 결과를 선택해 주세요.', 'error')
      return
    }
    try {
      setExporting(true)
      const res = await api.get(`/health-reports/${targetId}/export`, { responseType: 'blob' })
      const blob = new Blob([res.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      })

      // 파일명은 서버 Content-Disposition을 우선 사용하고, 없으면 기본값으로 다운로드한다.
      const cd = res.headers?.['content-disposition'] || res.headers?.['Content-Disposition'] || ''
      const filenameStar =
        cd && typeof cd === 'string' ? cd.match(/filename\*\s*=\s*UTF-8''([^;]+)/i) : null
      const filenamePlain = cd && typeof cd === 'string' ? cd.match(/filename="([^"]+)"/) : null
      const headerFilename = filenameStar
        ? decodeURIComponent(filenameStar[1])
        : filenamePlain
          ? filenamePlain[1]
          : ''
      const customerName = customerNameOverride || selectedReport?.customerName
      const createdAt = createdAtOverride || selectedReport?.createdAt
      const ymd = formatDate(createdAt || new Date())
      const fallbackCustomer = customerName
        ? `${customerName}_진료내역분석_${ymd}.xlsx`
        : `진료내역분석_${targetId}.xlsx`
      const filename = headerFilename && headerFilename !== 'health_report.xlsx' ? headerFilename : fallbackCustomer

      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = filename
      document.body.appendChild(a)
      a.click()
      a.remove()
      window.URL.revokeObjectURL(url)
      show('분석결과 다운로드가 시작되었습니다.', 'success')
    } catch (e) {
      console.error(e)
      show('분석결과 다운로드에 실패했습니다.', 'error')
    } finally {
      setExporting(false)
    }
  }

  const handleDeleteJob = async (job) => {
    if (!job?.jobId) return
    const label = job.customerName ? `${job.customerName} (${job.jobId})` : `Job ${job.jobId}`
    const ok = window.confirm(`${label} 분석 내역을 삭제할까요?\n(삭제 후 복구할 수 없습니다)`)
    if (!ok) return
    try {
      await api.delete(`/health-jobs/${job.jobId}`)
      // 선택 중인 결과였다면 닫기
      if (selectedReportId && job.healthReportId && selectedReportId === job.healthReportId) {
        setSelectedReport(null)
        setSelectedReportId(null)
      }
      // 폴링 중인 작업이었다면 초기화
      if (currentJob?.jobId && currentJob.jobId === job.jobId) {
        setCurrentJob(null)
      }
      queryClient.invalidateQueries({ queryKey: ['health-jobs'] })
      queryClient.invalidateQueries({ queryKey: ['health-job', job.jobId] })
      show('분석 내역을 삭제했습니다.', 'success')
    } catch (e) {
      const msg = e?.response?.data?.message || e?.message || null
      show(`삭제에 실패했습니다.${msg ? ` (${msg})` : ''}`, 'error')
    }
  }

  const handleEditNote = (customerId) => {
    const current = notes[customerId] || ''
    const next = window.prompt('고객 메모를 입력하세요.', current)
    if (next !== null) {
      setNotes((prev) => ({ ...prev, [customerId]: next }))
    }
  }

  const handleGoToReportsForCustomer = (name) => {
    if (name) {
      setSearch(name)
    }
    setActiveTab('reports')
  }

  const handleRetryIdentity = async () => {
    if (!retryJobId) return
    const identity = (retryIdentity || '').replaceAll(/[^0-9]/g, '')
    if (identity.length !== 13) {
      show('주민등록번호는 숫자 13자리로 입력해 주세요.', 'error')
      return
    }
    try {
      await api.post(`/health-jobs/${retryJobId}/identity`, { identity })
      setRetryJobId(null)
      setRetryIdentity('')
      await customersQuery.refetch()
      show('재시도 요청이 접수되었습니다. 잠시 후 상태가 갱신됩니다.', 'success')
    } catch (e) {
      console.error(e)
      show('재시도 요청에 실패했습니다.', 'error')
    }
  }

  const parsedSummary = useMemo(() => {
    if (!selectedReport?.summaryJson) return null
    try {
      return JSON.parse(selectedReport.summaryJson)
    } catch (e) {
      return null
    }
  }, [selectedReport])

  const profileFromSummary = parsedSummary?.profile || null
  byPeriodFromSummary = profileFromSummary?.byPeriod || null
  const byPeriodGroupedDiagnosisFromSummary = profileFromSummary?.byPeriodGroupedDiagnosis || null
  const hiddenCompFromSummary = profileFromSummary?.hiddenCompensation || null
  healthyViewFromSummary = profileFromSummary?.healthyView || null
  const substandardViewFromSummary = profileFromSummary?.substandardView || null
  const dentalViewFromSummary = profileFromSummary?.dentalView || null

  const episodesFromSummary = useMemo(() => {
    const eps = parsedSummary?.profile?.allEpisodes
    return Array.isArray(eps) && eps.length > 0 ? eps : DEMO_TREATMENTS
  }, [parsedSummary])

  rawEpisodesFromSummary = useMemo(() => {
    const eps = parsedSummary?.profile?.allEpisodesRaw
    return Array.isArray(eps) ? eps : []
  }, [parsedSummary])

  // 투약: 약국 우선 dedupe + 처방일↔조제일 연결(±N일)
  const PRESCRIBE_FILL_LINK_DAYS = 5
  const normalizeDateDigitsSafe = (v) => {
    if (!v) return null
    const s = String(v).replaceAll('-', '').trim()
    if (s.length !== 8) return null
    return s
  }
  const dateDigitsToDate = (yyyymmdd) => {
    const s = normalizeDateDigitsSafe(yyyymmdd)
    if (!s) return null
    const y = Number(s.slice(0, 4))
    const m = Number(s.slice(4, 6)) - 1
    const d = Number(s.slice(6, 8))
    const dt = new Date(y, m, d)
    return Number.isFinite(dt.getTime()) ? dt : null
  }
  const daysBetween = (aDigits, bDigits) => {
    const a = dateDigitsToDate(aDigits)
    const b = dateDigitsToDate(bDigits)
    if (!a || !b) return null
    const diffMs = Math.abs(a.getTime() - b.getTime())
    return Math.floor(diffMs / (24 * 60 * 60 * 1000))
  }
  const normalizeDrugKey = (line) => {
    const n = (line?.drugName || '').toString().trim().toLowerCase()
    const days = line?.totalDrugDays ?? ''
    return `${n}|${days}`
  }
  const isPharmacyLike = (line) => {
    const t = (line?.treatTypeDetail || line?.treatType || '').toString()
    const org = (line?.hospitalName || '').toString()
    if (t.includes('조제') || t.includes('처방')) return true
    if (org.includes('약국')) return true
    return false
  }
  const dedupeAndLinkDrugsPharmacyFirst = (drugLines) => {
    if (!Array.isArray(drugLines) || drugLines.length === 0) return []
    // 1) 같은 날짜/같은 약품키 중복 제거(약국 우선)
    const perDayKey = new Map()
    for (const line of drugLines) {
      const day = normalizeDateDigitsSafe(line?.startDate || line?.endDate)
      const key = normalizeDrugKey(line)
      if (!day || !key || key.startsWith('|')) continue
      const mapKey = `${day}__${key}`
      const prev = perDayKey.get(mapKey)
      if (!prev) {
        perDayKey.set(mapKey, line)
        continue
      }
      const prevIsPharm = isPharmacyLike(prev)
      const curIsPharm = isPharmacyLike(line)
      if (!prevIsPharm && curIsPharm) {
        perDayKey.set(mapKey, line)
      }
    }
    const compact = Array.from(perDayKey.values())

    // 2) 처방일(병원) ↔ 조제일(약국) 다른 경우 연결(±N일, 약품키 동일)
    const pharmacies = compact.filter((x) => isPharmacyLike(x))
    const hospitals = compact.filter((x) => !isPharmacyLike(x))
    const usedHospital = new Set()
    const enriched = []

    for (const p of pharmacies) {
      const pDay = normalizeDateDigitsSafe(p?.startDate || p?.endDate)
      const key = normalizeDrugKey(p)
      let best = null
      let bestDelta = null
      for (let i = 0; i < hospitals.length; i++) {
        if (usedHospital.has(i)) continue
        const h = hospitals[i]
        const hDay = normalizeDateDigitsSafe(h?.startDate || h?.endDate)
        if (!pDay || !hDay) continue
        if (normalizeDrugKey(h) !== key) continue
        const delta = daysBetween(pDay, hDay)
        if (delta === null) continue
        if (delta > PRESCRIBE_FILL_LINK_DAYS) continue
        if (bestDelta === null || delta < bestDelta) {
          bestDelta = delta
          best = { idx: i, h, hDay }
        }
      }
      if (best) {
        usedHospital.add(best.idx)
        enriched.push({
          ...p,
          prescriberOrgName: best.h?.hospitalName || null,
          prescriberDate: best.hDay || null,
        })
      } else {
        enriched.push(p)
      }
    }
    // 병원만 있는 기록(약국 데이터 누락/원내조제 등): 그대로 유지
    for (let i = 0; i < hospitals.length; i++) {
      if (usedHospital.has(i)) continue
      enriched.push(hospitals[i])
    }
    return enriched
  }

  const fieldLabels = {
    // id: 'ID',
    // groupType: '그룹 유형',
    diagnosisName: '진단명',
    diagnosisCode: '진단코드',
    diagnosisNames: '진단명(묶음)',
    drugNames: '약품명(묶음)',
    startDate: '진료 시작일',
    endDate: '진료 종료일',
    // NOTE: 기존 count는 "진단코드 그룹에 포함된 row 수"(라인아이템 수)에 가까워 혼란이 있어,
    // UI에서는 '진료건수'로 표기하고 encounterCount(날짜+기관 기준 dedupe)를 우선 노출한다.
    count: '진료건수',
    hiddenGuide: '숨은보상 가이드',
    totalTreatmentDays: '총 진료일',
    totalDrugDays: '총 투약일',
    inpatient: '입원 여부',
    surgeryName: '수술명',
    surgeryNames: '수술명(묶음)',
    diagnosisCodes: '진단코드(묶음)',
    dentalMain: '치아고지명',
    dentalDetail: '치아고지세부명',
    hospitalName: '병원명',
    hospitalCode: '병원코드',
    treatType: '진료형태',
    treatTypeDetail: '진료형태(상세)',
    drugName: '약품명',
    drugEffect: '약품효과/성분',
    sources: '데이터 출처',
    details: '세부 내역',
  }

  const detailFieldOrder = [
    'diagnosisCode',
    'diagnosisName',
    'diagnosisNames',
    'diagnosisCodes',
    'groupType',
    'hospitalName',
    'hospitalCode',
    'startDate',
    'endDate',
    'treatType',
    'treatTypeDetail',
    'drugName',
    'drugNames',
    'drugEffect',
    'totalTreatmentDays',
    'totalDrugDays',
    'inpatient',
    'surgeryName',
    'surgeryNames',
    'dentalMain',
    'dentalDetail',
    'hiddenGuide',
    'sources',
    'details',
    'count',
    'id',
  ]

  const isEmptyDetailValue = (value) => {
    if (value === null || value === undefined) return true
    if (typeof value === 'string') return value.trim() === ''
    if (Array.isArray(value)) return value.length === 0
    if (typeof value === 'object') return Object.keys(value).length === 0
    return false
  }

  const formatDetailValue = (key, value) => {
    if (key === 'inpatient') return value ? '있음' : '없음'
    if (key === 'startDate' || key === 'endDate') {
      return formatDate(value) || ''
    }
    const formatSourceLabel = (src) => {
      const s = (src || '').toString()
      if (!s) return ''
      if (s === 'BASIC_TREAT') return '진료(기본)'
      if (s === 'DETAIL_TREAT') return '진료(상세)'
      if (s === 'PRESCRIBE_DRUG') return '처방/조제'
      if (s === 'CAR_BASIC_TREAT') return '자동차보험(기본)'
      if (s === 'CAR_DETAIL_TREAT') return '자동차보험(상세)'
      if (s.startsWith('CAR_')) return '자동차보험'
      return s
    }
    if (key === 'sources') {
      if (Array.isArray(value)) return value.map(formatSourceLabel).filter(Boolean).join(', ')
      return formatSourceLabel(value)
    }
    // details는 팝업에서 카드형으로 별도 렌더링하므로 여기서는 문자열 변환하지 않는다.
    if (Array.isArray(value)) {
      const allPrimitive = value.every((v) => v === null || ['string', 'number', 'boolean'].includes(typeof v))
      if (allPrimitive) return value.filter((v) => v !== null && v !== undefined && String(v).trim() !== '').join(', ')
      return JSON.stringify(value)
    }
    if (typeof value === 'object') return JSON.stringify(value)
    return String(value)
  }

  // 진료내역 탭용 기간 필터
  const filterByPeriodLabel = (episodes, label) => {
    // 1) BE에서 계산된 byPeriod 데이터가 있으면 우선 사용
    if (byPeriodGroupedDiagnosisFromSummary) {
      const key =
        label === '1개월'
          ? '1M'
          : label === '3개월'
          ? '3M'
          : label === '1년'
          ? '1Y'
          : label === '3년'
          ? '3Y'
          : '5Y'
      const serverList = byPeriodGroupedDiagnosisFromSummary[key]
      if (Array.isArray(serverList) && serverList.length > 0) {
        return serverList
      }
    }

    if (byPeriodFromSummary) {
      const key =
        label === '1개월'
          ? '1M'
          : label === '3개월'
          ? '3M'
          : label === '1년'
          ? '1Y'
          : label === '3년'
          ? '3Y'
          : '5Y'
      const serverList = byPeriodFromSummary[key]
      if (Array.isArray(serverList) && serverList.length > 0) {
        return serverList
      }
    }

    // 2) 요약 정보가 없거나 비어 있으면 기존 클라이언트 사이드 필터 사용
    const now = new Date()
    const days = label === '1개월' ? 30 : label === '3개월' ? 90 : label === '1년' ? 365 : label === '3년' ? 365 * 3 : 365 * 5
    const threshold = new Date(now.getTime() - days * 24 * 60 * 60 * 1000)

    return episodes.filter((e) => {
      const d = e.startDate || e.endDate
      if (!d) return false
      // YYYYMMDD 또는 YYYY-MM-DD 모두 지원
      const normalized = d.includes('-') ? d : `${d.slice(0, 4)}-${d.slice(4, 6)}-${d.slice(6, 8)}`
      const dt = new Date(normalized)
      if (Number.isNaN(dt.getTime())) return false
      return dt >= threshold && dt <= now
    })
  }

  const isCarInsuranceLine = (line) => {
    const src = line && typeof line.source === 'string' ? line.source : ''
    return src.startsWith('CAR_')
  }

  const filterByTreatmentSource = (list, sourceLabel) => {
    if (!Array.isArray(list)) return []
    if (sourceLabel !== '자동차보험') return list

    // 서버 그룹(진단코드 그룹 + details)도 있을 수 있고, raw 라인아이템일 수도 있다.
    const out = []
    for (const item of list) {
      const details = item && Array.isArray(item.details) ? item.details : null
      if (details) {
        const filteredDetails = details.filter(isCarInsuranceLine)
        if (filteredDetails.length > 0) out.push({ ...item, details: filteredDetails })
        continue
      }
      if (isCarInsuranceLine(item)) out.push(item)
    }
    return out
  }

  const paginated = (list) => {
    const totalPages = Math.max(1, Math.ceil(list.length / PAGE_SIZE))
    const safePage = Math.min(page, totalPages - 1)
    const start = safePage * PAGE_SIZE
    const end = start + PAGE_SIZE
    return {
      items: list.slice(start, end),
      totalPages,
      currentPage: safePage,
    }
  }

  const renderTreatmentCard = (item, options = {}) => {
    const { showHiddenGuide, showDrugExtra, showInpatientExtra, showDentalExtra, detailContext } = options
    const context = detailContext || 'ALL'
    const computeSumFromDetails = (key) => {
      if (!item || !Array.isArray(item.details)) return null

      const byDayMaxSum = (valueKey) => {
        const maxByDay = new Map()
        let hasAny = false
        for (const d of item.details) {
          // 약국(조제) 라인은 "치료일수" 집계에서 제외
          if (valueKey === 'totalTreatmentDays' && isPharmacyLike(d)) continue
          const v = d ? d[valueKey] : null
          const n = typeof v === 'number' ? v : v !== null && v !== undefined && String(v).trim() !== '' ? Number(v) : null
          if (!Number.isFinite(n)) continue
          const day = normalizeDateDigitsSafe(d?.startDate || d?.endDate) || '__NO_DAY__'
          const prev = maxByDay.get(day)
          maxByDay.set(day, prev === undefined ? Math.max(0, n) : Math.max(prev, Math.max(0, n)))
          hasAny = true
        }
        if (!hasAny) return null
        let sum = 0
        for (const x of maxByDay.values()) sum += Math.max(0, x)
        return sum
      }

      // 총 투약일/총 진료일은 동일일자 중복이 있을 수 있어 "일자별 최대" 합산으로 계산한다.
      if (key === 'totalDrugDays') return byDayMaxSum('totalDrugDays')
      if (key === 'totalTreatmentDays') return byDayMaxSum('totalTreatmentDays')

      let sum = 0
      let hasAny = false
      for (const d of item.details) {
        const v = d ? d[key] : null
        const n = typeof v === 'number' ? v : v !== null && v !== undefined && String(v).trim() !== '' ? Number(v) : null
        if (Number.isFinite(n)) {
          sum += Math.max(0, n)
          hasAny = true
        }
      }
      return hasAny ? sum : null
    }

    const safeTotalTreatmentDays = (() => {
      // UI가 “첫번째 상세 내역 값”처럼 보이는 케이스가 있어,
      // 가능한 경우(details가 존재하면) 항상 details 합산을 우선한다.
      const sumFromDetails = computeSumFromDetails('totalTreatmentDays')
      if (Number.isFinite(sumFromDetails)) return sumFromDetails
      return item?.sumTreatmentDays ?? item?.totalTreatmentDays ?? null
    })()

    const safeTotalDrugDays = (() => {
      const sumFromDetails = computeSumFromDetails('totalDrugDays')
      if (Number.isFinite(sumFromDetails)) return sumFromDetails
      return item?.sumDrugDays ?? item?.totalDrugDays ?? null
    })()

    const computeEncounterCountFromDetails = (details, rawEpisodes, opts = {}) => {
      const { includeRawFallback = true } = opts || {}
      const list = Array.isArray(details) ? details : []
      if (list.length === 0) return null

      const dayOf = (x) => normalizeDateDigitsSafe(x?.startDate || x?.endDate)
      const isPharm = (x) => isPharmacyLike(x) || String(x?.hospitalName || '').includes('약국')
      const norm = (v) => (v || '').toString().replaceAll(/\s+/g, ' ').trim()
      const hospKey = (x) => {
        const name = norm(x?.hospitalName || '')
        const code = norm(x?.hospitalCode || '')
        return name ? name : (code ? code : '_')
      }

      const visitSet = new Set()
      const days = new Set()
      for (const x of list) {
        const d = dayOf(x)
        if (d) days.add(d)
        if (isPharm(x)) continue
        if (!d) continue
        visitSet.add(`${d}|${hospKey(x)}`)
      }
      if (visitSet.size > 0) return visitSet.size

      if (!includeRawFallback) return 0
      if (!Array.isArray(rawEpisodes) || rawEpisodes.length === 0) return 0
      for (const d of days) {
        if (!d) continue
        for (const x of rawEpisodes) {
          const src = String(x?.source || '')
          if (!(src.includes('DETAIL_TREAT') || src.includes('BASIC_TREAT'))) continue
          if (dayOf(x) !== d) continue
          if (isPharm(x)) continue
          visitSet.add(`${d}|${hospKey(x)}`)
        }
      }
      return visitSet.size
    }

    // 요약 카드의 "건수"는 기본적으로 서버 encounterCount(약국 제외, 기관+일자 dedupe)를 우선 사용한다.
    // 다만 서버가 진단코드 백필을 못 한 케이스(약국 라인에만 코드가 붙는 경우)엔 encounterCount가 0으로 내려올 수 있어,
    // 그 경우에만 "실제로 화면에 표시되는 병원 방문" 기준으로 0을 보정한다(팝업과 동일 철학).
    const computeEncounterCountFallback = () => {
      const details = Array.isArray(item?.details) ? item.details : []
      if (details.length === 0) return null

      const dayOf = (x) => normalizeDateDigitsSafe(x?.startDate || x?.endDate)
      const isPharm = (x) => isPharmacyLike(x) || String(x?.hospitalName || '').includes('약국')
      const norm = (v) => (v || '').toString().replaceAll(/\s+/g, ' ').trim()
      const hospKey = (x) => {
        const name = norm(x?.hospitalName || '')
        const code = norm(x?.hospitalCode || '')
        return name ? name : (code ? code : '_')
      }

      // 1) item.details(=서버 그룹 details) 내에서 병원 방문을 직접 집계
      const visitSet = new Set()
      const days = new Set()
      for (const x of details) {
        const d = dayOf(x)
        if (d) days.add(d)
        if (isPharm(x)) continue
        if (!d) continue
        visitSet.add(`${d}|${hospKey(x)}`)
      }
      if (visitSet.size > 0) return visitSet.size

      // 2) details에 병원 라인이 없고(예: 약국 라인만 포함된 그룹) 팝업처럼 raw를 보강해야 하는 경우
      if (!Array.isArray(rawEpisodesFromSummary) || rawEpisodesFromSummary.length === 0) return 0
      for (const d of days) {
        for (const x of rawEpisodesFromSummary) {
          const src = String(x?.source || '')
          if (!(src.includes('DETAIL_TREAT') || src.includes('BASIC_TREAT'))) continue
          if (dayOf(x) !== d) continue
          if (isPharm(x)) continue
          visitSet.add(`${d}|${hospKey(x)}`)
        }
      }
      return visitSet.size
    }

    const displayEncounterCount = (() => {
      const n = Number(item?.encounterCount)
      if (Number.isFinite(n) && n > 0) return n
      // 0일 때만 fallback을 시도
      const fb = computeEncounterCountFromDetails(item?.details, rawEpisodesFromSummary, { includeRawFallback: true })
      if (Number.isFinite(fb) && fb > 0) return fb
      if (Number.isFinite(n)) return Math.max(0, n)
      const c = Number(item?.count)
      return Number.isFinite(c) ? Math.max(0, c) : 0
    })()

    // 보험유니버스 기준 그룹(진단코드/병원/수술) 카드
    if (item && item.groupType) {
      if (item.groupType === 'HOSPITAL' || item.groupType === 'HOSPITAL_DX') {
        const displayCount = displayEncounterCount
        return (
          <div key={item.id} className="rounded border px-3 py-2 mb-2 text-sm bg-white">
            <div className="font-medium text-slate-900">
              {item.hospitalName || (item.hospitalCode ? '기관' : '기관코드 없음')}
            </div>
            <div className="text-xs text-slate-500">
              기관코드: {item.hospitalCode || '-'}
              {item.diagnosisCode || item.diagnosisName ? (
                <>
                  {' '}• 진단:{' '}
                  <span className="font-mono">{item.diagnosisCode || '-'}</span>
                  {item.diagnosisName ? <span> {item.diagnosisName}</span> : null}
                </>
              ) : null}
            </div>
            <div className="mt-1 text-xs text-slate-600">
              기간: {formatDate(item.startDate) || '-'} ~ {formatDate(item.endDate) || '-'} / 건수: {displayCount}
            </div>
            {showInpatientExtra && (
              <div className="mt-1 text-xs text-slate-600">
                입원: {item.inpatient ? '있음' : '없음'}
                {Array.isArray(item.surgeryNames) && item.surgeryNames.length > 0
                  ? ` • 수술/시술: ${item.surgeryNames.join(', ')}`
                  : null}
              </div>
            )}
            <div className="mt-2">
              <button
                type="button"
                className="rounded border px-2 py-0.5 text-xs hover:bg-slate-100"
                onClick={() => openDetailEpisode(item, { showHiddenGuide, context })}
              >
                상세내역
              </button>
            </div>
          </div>
        )
      }
      if (item.groupType === 'SURGERY') {
        const displayCount = displayEncounterCount
        return (
          <div key={item.id} className="rounded border px-3 py-2 mb-2 text-sm bg-white">
            {showHiddenGuide && item.hiddenGuide && (
              <div className="mb-1 text-xs font-semibold text-emerald-700">숨은보상: {item.hiddenGuide}</div>
            )}
            <div className="font-medium text-slate-900">{item.surgeryName || '수술'}</div>
            <div className="mt-1 text-xs text-slate-600">
              기간: {formatDate(item.startDate) || '-'} ~ {formatDate(item.endDate) || '-'} / 건수: {displayCount}
            </div>
            <div className="mt-2">
              <button
                type="button"
                className="rounded border px-2 py-0.5 text-xs hover:bg-slate-100"
                onClick={() => openDetailEpisode(item, { showHiddenGuide, context })}
              >
                상세내역
              </button>
            </div>
          </div>
        )
      }
      // 기본: 진단코드 그룹
      const displayCount = displayEncounterCount
      return (
        <div key={item.id} className="rounded border px-3 py-2 mb-2 text-sm bg-white">
          {showHiddenGuide && item.hiddenGuide && (
            <div className="mb-1 text-xs font-semibold text-emerald-700">숨은보상: {item.hiddenGuide}</div>
          )}
          <div className="font-medium text-slate-900">{item.diagnosisName}</div>
          <div className="text-xs text-slate-500">진단코드: {item.diagnosisCode || '-'}</div>
          <div className="mt-1 text-xs text-slate-600">
            기간: {formatDate(item.startDate) || '-'} ~ {formatDate(item.endDate) || '-'} / 건수: {displayCount}
          </div>
          {Number.isFinite(Number(safeTotalTreatmentDays)) && displayEncounterCount > Number(safeTotalTreatmentDays) ? (
            <div className="mt-1 text-[11px] text-slate-500">
              * 같은 날짜에 여러 기관을 방문한 경우, “진료건수”가 “총 진료일”보다 클 수 있습니다.
            </div>
          ) : null}
          {(showDrugExtra || showInpatientExtra) && (
            <div className="mt-1 text-xs text-slate-600">
              {showInpatientExtra && !showDrugExtra ? (
                <>
                  입원일수: {item.sumInpatientDays ?? item.sumTreatmentDays ?? item.totalTreatmentDays ?? '-'}일
                  {' / '}수술건수: {Number.isFinite(Number(item.surgeryCount)) ? Number(item.surgeryCount) : 0}회
                </>
              ) : (
                <>
                  총 진료일: {safeTotalTreatmentDays ?? '-'}일 • 총 투약일:{' '}
                  {safeTotalDrugDays ?? '-'}일
                </>
              )}
            </div>
          )}
          <div className="mt-2">
            <button
              type="button"
              className="rounded border px-2 py-0.5 text-xs hover:bg-slate-100"
              onClick={() => openDetailEpisode(item, { showHiddenGuide, context })}
            >
              상세내역
            </button>
          </div>
        </div>
      )
    }
    return (
      <div key={item.id} className="rounded border px-3 py-2 mb-2 text-sm bg-white">
        {showHiddenGuide && item.hiddenGuide && (
          <div className="mb-1 text-xs font-semibold text-emerald-700">숨은보상: {item.hiddenGuide}</div>
        )}
        <div className="font-medium text-slate-900">{item.diagnosisName}</div>
        <div className="text-xs text-slate-500">진단코드: {item.diagnosisCode}</div>
        <div className="mt-1 text-xs text-slate-600">
          진료 시작일: {formatDate(item.startDate) || '-'} / 진료 종료일: {formatDate(item.endDate) || '-'} / 진료건수:{' '}
          {displayEncounterCount}
        </div>
        {showDrugExtra && (
          <div className="mt-1 text-xs text-slate-600">
            총 진료일: {safeTotalTreatmentDays ?? '-'}일 • 총 투약일:{' '}
            {safeTotalDrugDays ?? '-'}일
          </div>
        )}
        {showInpatientExtra && (
          <div className="mt-1 text-xs text-slate-600">
            입원: {item.inpatient ? '있음' : '없음'} {item.surgeryName && `• 수술명: ${item.surgeryName}`}
          </div>
        )}
        {showDentalExtra && (item.dentalMain || item.dentalDetail) && (
          <div className="mt-1 text-xs text-slate-600">
            치아고지명: {item.dentalMain} / 치아고지세부명: {item.dentalDetail}
          </div>
        )}
        <div className="mt-2">
          <button
            type="button"
            className="rounded border px-2 py-0.5 text-xs hover:bg-slate-100"
            onClick={() => openDetailEpisode(item, { showHiddenGuide, context })}
          >
            상세내역
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <header className="space-y-1">
        <h1 className="text-2xl font-semibold text-slate-900">진료정보분석</h1>
        <p className="text-sm text-slate-600">
          GA 고객의 건강보험 진료·투약 정보를 기반으로 건강 리포트를 생성하고, 인수 가능성이 높은 상품을 추천받는 메뉴입니다.
        </p>
      </header>

      {/* 추가 인증 입력 모달 */}
      {twoWayModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 p-4">
          <div className="w-full max-w-lg rounded bg-white p-4 shadow">
            <div className="flex items-center justify-between">
              <h2 className="text-base font-semibold">추가 인증 입력</h2>
              <button
                type="button"
                className="text-sm text-slate-500 hover:text-slate-700"
                onClick={() => setTwoWayModalOpen(false)}
              >
                닫기
              </button>
            </div>
            <p className="mt-1 text-xs text-slate-600">
              보안문자 또는 SMS 인증번호 입력이 필요한 경우가 있습니다. 입력 후 제출하면 2차 요청을 재개합니다.
            </p>

            <div className="mt-3 space-y-3">
              <div>
                <label className="block text-slate-700 mb-1 text-sm">보안문자</label>
                <input
                  type="text"
                  value={twoWayInput.secureNo}
                  onChange={(e) => setTwoWayInput((p) => ({ ...p, secureNo: e.target.value }))}
                  className="w-full rounded border px-2 py-1 text-sm"
                  placeholder="보안문자(필요 시)"
                />
                {twoWayInfo?.extraInfo?.reqSecureNo && (
                  <p className="mt-1 text-[11px] text-slate-500">
                    서버가 보안문자 입력을 요구했습니다. (표시 데이터: reqSecureNo 존재)
                  </p>
                )}
              </div>

              <div>
                <label className="block text-slate-700 mb-1 text-sm">SMS 인증번호</label>
                <input
                  type="text"
                  value={twoWayInput.smsAuthNo}
                  onChange={(e) => setTwoWayInput((p) => ({ ...p, smsAuthNo: e.target.value }))}
                  className="w-full rounded border px-2 py-1 text-sm"
                  placeholder="SMS 인증번호(필요 시)"
                />
                {twoWayInfo?.extraInfo?.reqSMSAuthNo && (
                  <p className="mt-1 text-[11px] text-slate-500">
                    서버가 SMS 인증번호 입력을 요구했습니다. (표시 데이터: reqSMSAuthNo 존재)
                  </p>
                )}
              </div>

              <div className="flex items-center justify-end gap-2">
                <button
                  type="button"
                  className="rounded border px-3 py-1 text-sm"
                  onClick={() => setTwoWayModalOpen(false)}
                >
                  취소
                </button>
                <button
                  type="button"
                  className="rounded bg-brand-navy px-3 py-1 text-sm text-white hover:bg-brand-sky"
                  onClick={submitTwoWayInput}
                >
                  제출하고 재개
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 탭 네비게이션 */}
      <div className="border-b flex gap-4 text-sm">
        <button
          type="button"
          className={`pb-2 border-b-2 ${
            activeTab === 'new' ? 'border-brand-navy text-brand-navy' : 'border-transparent text-slate-500'
          }`}
          onClick={() => setActiveTab('new')}
        >
          신규 분석
        </button>
        <button
          type="button"
          className={`pb-2 border-b-2 ${
            activeTab === 'reports' ? 'border-brand-navy text-brand-navy' : 'border-transparent text-slate-500'
          }`}
          onClick={() => setActiveTab('reports')}
        >
          분석 목록
        </button>
        <button
          type="button"
          className={`pb-2 border-b-2 ${
            activeTab === 'customers' ? 'border-brand-navy text-brand-navy' : 'border-transparent text-slate-500'
          }`}
          onClick={() => setActiveTab('customers')}
        >
          고객 목록
        </button>
      </div>

      {/* 1) 신규 분석 탭 */}
      {activeTab === 'new' && (
        <section className="rounded-lg border bg-white p-4 shadow-sm space-y-4">
          <div className="flex items-center justify-between text-sm">
            <span className="font-medium">조회 가능 건수</span>
            <span className="font-mono">
              {quota.used} / {quota.total}
            </span>
          </div>
          <hr className="my-2" />
          <h2 className="text-base font-semibold text-slate-900">인증 고객 정보 입력</h2>
          <form onSubmit={handleCreateReport} className="space-y-3 text-sm">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div>
                <label className="block text-slate-700 mb-1">고객명</label>
                <input
                  type="text"
                  name="name"
                  value={form.name}
                  onChange={handleChange}
                  className="w-full rounded border px-2 py-1 text-sm"
                  required
                />
              </div>
              <div>
                <label className="block text-slate-700 mb-1">주민등록번호 (숫자만, 저장되지 않음)</label>
                <input
                  type="password"
                  name="identity"
                  value={form.identity}
                  onChange={handleChange}
                  className="w-full rounded border px-2 py-1 text-sm"
                  placeholder="예: 9001011234567"
                  required
                />
                {identityMaskedDisplay ? (
                  <div className="mt-1 text-[11px] text-slate-600">
                    표시: <span className="font-mono">{identityMaskedDisplay}</span>
                  </div>
                ) : null}
                <p className="mt-1 text-[11px] text-slate-500">
                  주민등록번호는 조회를 위해서만 사용되며 서버 DB에 저장하지 않습니다. 
                </p>
              </div>
              <div>
                <label className="block text-slate-700 mb-1">생년월일 (주민번호로 자동 입력)</label>
                <input
                  type="text"
                  name="birthDate"
                  value={form.birthDate ? formatDate(form.birthDate) : ''}
                  onChange={handleChange}
                  className="w-full rounded border px-2 py-1 text-sm bg-slate-50 text-slate-700"
                  placeholder="주민번호 입력 시 자동완성"
                  readOnly
                />
              </div>
              <div>
                <label className="block text-slate-700 mb-1">인증수단</label>
                <select
                  name="authProvider"
                  value={form.authProvider}
                  onChange={handleChange}
                  className="w-full rounded border px-2 py-1 text-sm"
                >
                  <option value="KAKAO">카카오톡(간편인증)</option>
                  {/* <option value="PASS">PASS(휴대폰인증)</option> */}
                  {/* <option value="SMS">SMS(휴대폰인증)</option> */}
                </select>
                <p className="mt-1 text-[11px] text-slate-500">
                  현재는 카카오톡 인증만 지원하고 있습니다.
                </p>
              </div>
              <div>
                <label className="block text-slate-700 mb-1">통신사</label>
                <select
                  name="telecom"
                  value={form.telecom}
                  onChange={handleChange}
                  className="w-full rounded border px-2 py-1 text-sm"
                >
                  <option value="0">SKT/알뜰폰</option>
                  <option value="1">KT/알뜰폰</option>
                  <option value="2">LG U+/알뜰폰</option>
                </select>
              </div>

              <div>
                <label className="block text-slate-700 mb-1">연락처</label>
                  <input
                    type="tel"
                    name="phoneNumber"
                    value={form.phoneNumber}
                    onChange={handleChange}
                    className="w-full rounded border px-2 py-1 text-sm"
                    placeholder="01012345678"
                    required
                  />
              </div>

            </div>
            <button
              type="submit"
              className="mt-2 inline-flex items-center rounded bg-brand-navy px-4 py-1.5 text-sm font-medium text-white hover:bg-brand-sky"
              disabled={createReportMutation.isLoading}
            >
              {createReportMutation.isLoading ? '요청 중...' : '인증 요청하기'}
            </button>
            {currentJob && (
              <p className="mt-2 text-xs text-slate-600">
                Job ID: <span className="font-mono">{currentJob.jobId}</span>{' '}
                / 상태:{' '}
                <span className="font-mono">
                  {jobStatusLabel(jobStatusQuery.data?.status || currentJob.status)}
                </span>
              </p>
            )}
            {jobStatusQuery.data?.healthReportId && (
              <p className="mt-1 text-xs text-emerald-700">
                분석이 완료되었습니다. 리포트 ID:{' '}
                <button
                  type="button"
                  className="underline"
                  onClick={() => handleViewResult(jobStatusQuery.data.healthReportId)}
                >
                  {jobStatusQuery.data.healthReportId}
                </button>
              </p>
            )}
            {lastReport && (
              <p className="mt-2 text-xs text-slate-600">
                최신 리포트 ID: <span className="font-mono">{lastReport.reportId}</span> / 위험도:{' '}
                <span className="font-mono">{lastReport.riskLevel}</span>
              </p>
            )}
          </form>
        </section>
      )}

      {/* 2) 분석 목록 탭 */}
      {activeTab === 'reports' && (
        <section className="rounded-lg border bg-white p-4 shadow-sm space-y-4">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2 text-sm">
            <div className="flex items-center gap-2">
              <input
                type="text"
                className="rounded border px-2 py-1 text-sm"
                placeholder="고객명 검색"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
              <button
                type="button"
                className="rounded border px-3 py-1 text-sm hover:bg-slate-50"
                onClick={() => jobsQuery.refetch()}
              >
                새로고침
              </button>
            </div>
          </div>

          {jobsQuery.isLoading ? (
            <p className="text-xs text-slate-500">분석 목록을 불러오는 중입니다...</p>
          ) : jobsQuery.isError ? (
            <p className="text-xs text-red-500">분석 목록을 불러오지 못했습니다.</p>
          ) : (
            <>
              <div className="space-y-2 text-sm">
                {filteredJobs.map((c) => (
                  <div key={c.jobId} className="rounded border px-3 py-2">
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="font-medium text-slate-900">
                          {c.customerName ? `고객명: ${c.customerName}` : '고객 (이름 미상)'}
                        </div>
                        {/* <div className="text-xs text-slate-500">
                          고객 ID: {c.customerId ?? '-'}
                        </div> */}
                        <div className="text-xs text-slate-500">
                          요청일시: {formatDateTime(c.createdAt) || '-'}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-emerald-600">
                          상태: {jobStatusLabel(c.status)}
                        </span>

                        {canRetryIdentityJob(c) && (
                          <button
                            type="button"
                            className="rounded border px-2 py-0.5 text-xs border-amber-300 bg-amber-50 text-amber-800 hover:bg-amber-100"
                            onClick={() => {
                              setRetryJobId(c.jobId)
                              setRetryIdentity('')
                            }}
                          >
                            재시도(주민번호 재입력)
                          </button>
                        )}
                        {c.status === 'AUTH_INPUT_REQUIRED' && (
                          <button
                            type="button"
                            className="rounded border px-2 py-0.5 text-xs border-purple-300 bg-purple-50 text-purple-800 hover:bg-purple-100"
                            onClick={async () => {
                              try {
                                const res = await api.get(`/health-jobs/${c.jobId}`)
                                const data = res.data?.data || res.data
                                openTwoWayInputModal(data)
                              } catch (_) {
                                show('추가 인증 정보를 불러오지 못했습니다.', 'error')
                              }
                            }}
                          >
                            추가 인증 입력
                          </button>
                        )}
                        {c.healthReportId && (
                          <>
                            <button
                              type="button"
                              className="rounded border px-2 py-0.5 text-xs hover:bg-slate-100"
                              onClick={() => {
                                if (selectedReportId === c.healthReportId) {
                                  setSelectedReport(null)
                                  setSelectedReportId(null)
                                } else {
                                  handleViewResult(c.healthReportId, {
                                    customerName: c.customerName,
                                    birthDate: c.birthDate || c.customerBirthDate || null,
                                    jobId: c.jobId,
                                    createdAt: c.createdAt,
                                  })
                                }
                              }}
                            >
                              {selectedReportId === c.healthReportId ? '결과 접기' : '결과 조회'}
                            </button>
                            {/* {c.healthReportId && (
                              <>
                                <select
                                  className="rounded border px-2 py-0.5 text-xs bg-white"
                                  value={recommendCoverageType}
                                  onChange={(e) => setRecommendCoverageType(e.target.value)}
                                >
                                  <option value="HEALTH">건강체</option>
                                  <option value="CANCER">암</option>
                                </select>
                                <button
                                  type="button"
                                  className="rounded border px-2 py-0.5 text-xs border-brand-navy text-brand-navy hover:bg-brand-navy hover:text-white"
                                  onClick={() => handleRecommendForReport(c.healthReportId)}
                                  disabled={recommendMutation.isLoading}
                                >
                                  상품 자동 추천
                                </button>
                              </>
                            )} */}
                          </>
                        )}
                        {(c.healthReportId || c.status === 'FAILED') && (
                          <button
                            type="button"
                            className="rounded border px-2 py-0.5 text-xs border-red-300 bg-red-50 text-red-700 hover:bg-red-100"
                            onClick={() => handleDeleteJob(c)}
                          >
                            삭제
                          </button>
                        )}
                      </div>
                    </div>

                    {selectedReport && selectedReportId === c.healthReportId && (
                      <div className="mt-3 rounded-lg border bg-slate-50 px-3 py-3 space-y-3">
                        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2 text-sm">
                          <div>
                            {/* <div className="font-semibold text-slate-900">선택된 분석 결과</div>
                            <div className="text-xs text-slate-600">
                              리포트 ID:{' '}
                              <span className="font-mono">
                                {selectedReport.reportId ?? selectedReport.id}
                              </span>{' '}
                              • 위험도{' '}
                              <span className="font-mono">
                                {selectedReport.riskScore ?? '-'}점 / {selectedReport.riskLevel || '-'}
                              </span>
                            </div> */}
                            <div className="text-xs text-slate-500">
                              생성일시:{' '}
                              <span className="font-mono">
                                {formatDateTime(selectedReport.createdAt) || '-'}
                              </span>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            {canShowReanalyzeButton && (
                              <button
                                type="button"
                                className="rounded border px-2 py-1 text-xs border-brand-navy text-brand-navy hover:bg-brand-navy hover:text-white"
                                onClick={() => handleReanalyzeReport(selectedReportId)}
                              >
                                재분석(7일 이내만 가능)
                              </button>
                            )}
                            <button
                              type="button"
                              className="rounded border px-2 py-1 text-xs border-brand-navy text-brand-navy hover:bg-brand-navy hover:text-white"
                              onClick={() => openDesignRequestModal('substandard')}
                            >
                              설계의뢰
                            </button>
                          <button
                            type="button"
                            className="rounded border px-2 py-1 text-xs border-brand-navy text-brand-navy hover:bg-brand-navy hover:text-white disabled:opacity-40"
                            onClick={() =>
                              handleDownloadAnalysisResult(selectedReportId, c.customerName, selectedReport?.createdAt)
                            }
                            disabled={exporting}
                          >
                            {exporting ? '다운로드 준비 중...' : '분석결과 다운로드'}
                            </button>
                          </div>
                        </div>

                        {/* 결과 세부 탭 */}
                        <div className="border-b mt-2 flex gap-3 text-xs md:text-sm">
                          <button
                            type="button"
                            className={`pb-2 border-b-2 ${
                              resultTab === 'hidden'
                                ? 'border-brand-navy text-brand-navy'
                                : 'border-transparent text-slate-500'
                            }`}
                            onClick={() => {
                              setResultTab('hidden')
                              setPage(0)
                            }}
                          >
                            숨은보상찾기
                          </button>
                          <button
                            type="button"
                            className={`pb-2 border-b-2 ${
                              resultTab === 'healthy'
                                ? 'border-brand-navy text-brand-navy'
                                : 'border-transparent text-slate-500'
                            }`}
                            onClick={() => {
                              setResultTab('healthy')
                              setPage(0)
                            }}
                          >
                            건강체
                          </button>
                          <button
                            type="button"
                            className={`pb-2 border-b-2 ${
                              resultTab === 'substandard'
                                ? 'border-brand-navy text-brand-navy'
                                : 'border-transparent text-slate-500'
                            }`}
                            onClick={() => {
                              setResultTab('substandard')
                              setPage(0)
                            }}
                          >
                            유병자
                          </button>
                          <button
                            type="button"
                            className={`pb-2 border-b-2 ${
                              resultTab === 'dental'
                                ? 'border-brand-navy text-brand-navy'
                                : 'border-transparent text-slate-500'
                            }`}
                            onClick={() => {
                              setResultTab('dental')
                              setPage(0)
                            }}
                          >
                            치아
                          </button>
                          <button
                            type="button"
                            className={`pb-2 border-b-2 ${
                              resultTab === 'treatments'
                                ? 'border-brand-navy text-brand-navy'
                                : 'border-transparent text-slate-500'
                            }`}
                            onClick={() => {
                              setResultTab('treatments')
                              setPage(0)
                            }}
                          >
                            모든진료내역
                          </button>
                          <button
                            type="button"
                            className={`pb-2 border-b-2 ${
                              resultTab === 'drugs' ? 'border-brand-navy text-brand-navy' : 'border-transparent text-slate-500'
                            }`}
                            onClick={() => {
                              setResultTab('drugs')
                              setPage(0)
                            }}
                          >
                            모든투약내역
                          </button>
                        </div>



                        {/* 이하 결과 탭 내용은 그대로 유지 */}
                        {/* 1. 숨은보상찾기 */}
                        {resultTab === 'hidden' && (
                          <div className="mt-3 space-y-3 text-xs">
                            <div className="flex flex-wrap gap-2">
                              {['자동차부상', '진단비', '수술·시술', '후유장해', '치아', '기타'].map((label) => (
                                <button
                                  key={label}
                                  type="button"
                                  className={`rounded-full border px-3 py-1 text-xs ${
                                    hiddenSubTab === label
                                      ? 'bg-brand-navy text-white border-brand-navy'
                                      : 'border-slate-300 text-slate-600 hover:bg-slate-100'
                                  }`}
                                  onClick={() => {
                                    setHiddenSubTab(label)
                                    setPage(0)
                                  }}
                                >
                                  {label}
                                </button>
                              ))}
                            </div>
                            <p className="text-slate-600">
                              선택한 카테고리에 해당하는 진료·수술·치아 내역 중, 아직 청구하지 않았을 수 있는 숨은 보상 가능성을
                              안내합니다.
                            </p>
                            <p className="text-[11px] text-slate-400">
                              * 모든 안내 내용은 일반적인 사례를 바탕으로 한 참고용 정보이며, 실제 보장 가능 여부와 지급 여부는 각
                              보험 상품의 약관 및 해당 보험사의 인수·지급 심사 기준에 따라 달라질 수 있습니다.
                            </p>
                            <div>
                              {(() => {
                                let filtered = episodesFromSummary
                                if (hiddenCompFromSummary) {
                                  const key =
                                    hiddenSubTab === '자동차부상'
                                      ? 'carInjury'
                                      : hiddenSubTab === '진단비'
                                      ? 'diagnosisBenefit'
                                      : hiddenSubTab === '수술·시술'
                                      ? 'surgeryProcedure'
                                      : hiddenSubTab === '후유장해'
                                      ? 'disability'
                                      : hiddenSubTab === '치아'
                                      ? 'dental'
                                      : 'etc'
                                  const serverList = hiddenCompFromSummary[key]
                                  // 서버가 빈 배열을 내려주면 "없음"이 정상인데, 기존엔 fallback 되어 서브탭 필터가 깨졌음
                                  if (Array.isArray(serverList)) {
                                    filtered = serverList
                                  }
                                }
                                const { items, totalPages, currentPage } = paginated(filtered)
                                return (
                                  <>
                                    <div>
                                      {items.map((item) =>
                                        renderTreatmentCard(item, {
                                          showHiddenGuide: true,
                                          detailContext: hiddenSubTab === '수술·시술' ? 'HIDDEN_SURGERY' : 'ALL',
                                        }),
                                      )}
                                    </div>
                                    {totalPages > 1 && (
                                      <div className="mt-2 flex items-center justify-end gap-2 text-[11px] text-slate-600">
                                        <button
                                          type="button"
                                          className="rounded border px-2 py-0.5 disabled:opacity-40"
                                          disabled={currentPage === 0}
                                          onClick={() => setPage((p) => Math.max(0, p - 1))}
                                        >
                                          이전
                                        </button>
                                        <span>
                                          {currentPage + 1} / {totalPages}
                                        </span>
                                        <button
                                          type="button"
                                          className="rounded border px-2 py-0.5 disabled:opacity-40"
                                          disabled={currentPage >= totalPages - 1}
                                          onClick={() => setPage((p) => p + 1)}
                                        >
                                          다음
                                        </button>
                                      </div>
                                    )}
                                  </>
                                )
                              })()}
                            </div>
                          </div>
                        )}

                        {/* 2. 진료내역 */}
                        {resultTab === 'treatments' && (
                          <div className="mt-3 space-y-3 text-xs">
                            <div className="flex flex-wrap gap-2">
                              {['1개월', '3개월', '1년', '3년', '5년'].map((label) => (
                                <button
                                  key={label}
                                  type="button"
                                  className={`rounded-full border px-3 py-1 text-xs ${
                                    treatmentSubTab === label
                                      ? 'bg-brand-navy text-white border-brand-navy'
                                      : 'border-slate-300 text-slate-600 hover:bg-slate-100'
                                  }`}
                                  onClick={() => {
                                    setTreatmentSubTab(label)
                                    setPage(0)
                                  }}
                                >
                                  {label}
                                </button>
                              ))}
                            </div>
                            <div className="flex flex-wrap gap-2">
                              {['전체', '자동차보험'].map((label) => (
                                <button
                                  key={label}
                                  type="button"
                                  className={`rounded-full border px-3 py-1 text-xs ${
                                    treatmentSourceSubTab === label
                                      ? 'bg-brand-navy text-white border-brand-navy'
                                      : 'border-slate-300 text-slate-600 hover:bg-slate-100'
                                  }`}
                                  onClick={() => {
                                    setTreatmentSourceSubTab(label)
                                    setPage(0)
                                  }}
                                >
                                  {label}
                                </button>
                              ))}
                            </div>
                            <p className="text-slate-600">
                              선택한 기간 내에 발생한 모든 진료 내역을 기간 기준으로만 나누어 보여줍니다.
                            </p>
                            {(() => {
                              const byPeriod = filterByPeriodLabel(episodesFromSummary, treatmentSubTab)
                              const filtered = filterByTreatmentSource(byPeriod, treatmentSourceSubTab)
                              const { items, totalPages, currentPage } = paginated(filtered)
                              return (
                                <>
                                  <div>{items.map((item) => renderTreatmentCard(item))}</div>
                                  {totalPages > 1 && (
                                    <div className="mt-2 flex items-center justify-end gap-2 text-[11px] text-slate-600">
                                      <button
                                        type="button"
                                        className="rounded border px-2 py-0.5 disabled:opacity-40"
                                        disabled={currentPage === 0}
                                        onClick={() => setPage((p) => Math.max(0, p - 1))}
                                      >
                                        이전
                                      </button>
                                      <span>
                                        {currentPage + 1} / {totalPages}
                                      </span>
                                      <button
                                        type="button"
                                        className="rounded border px-2 py-0.5 disabled:opacity-40"
                                        disabled={currentPage >= totalPages - 1}
                                        onClick={() => setPage((p) => p + 1)}
                                      >
                                        다음
                                      </button>
                                    </div>
                                  )}
                                </>
                              )
                            })()}
                          </div>
                        )}

                        {/* 2-1. 모든투약내역 */}
                        {resultTab === 'drugs' && (
                          <div className="mt-3 space-y-3 text-xs">
                            <div className="flex flex-wrap gap-2">
                              {['3개월', '1년', '3년', '5년'].map((label) => (
                                <button
                                  key={label}
                                  type="button"
                                  className={`rounded-full border px-3 py-1 text-xs ${
                                    drugSubTab === label
                                      ? 'bg-brand-navy text-white border-brand-navy'
                                      : 'border-slate-300 text-slate-600 hover:bg-slate-100'
                                  }`}
                                  onClick={() => {
                                    setDrugSubTab(label)
                                    setPage(0)
                                  }}
                                >
                                  {label}
                                </button>
                              ))}
                            </div>
                            <p className="text-slate-600">
                              투약 내역은 <span className="font-semibold">약국(조제)</span> 기록을 우선으로 중복 제거하며, 처방일과 조제일이 다른 경우
                              <span className="font-semibold"> ±{PRESCRIBE_FILL_LINK_DAYS}일</span> 이내이면 동일 처방으로 연결해 표시합니다.
                            </p>
                            <p className="text-[11px] text-slate-500">
                              * 진단코드 기준 그룹핑은 “일자 기준”으로 매핑합니다. 동일 일자에 복수 진단코드가 존재하면, 일부 투약은 진단코드로 귀속이
                              어려워 “약품 기준”으로 표시될 수 있습니다.
                            </p>
                            <p className="text-[11px] text-slate-500">
                              * 동일 일자에 여러 진단코드가 포함된 진료가 있는 경우, 화면/다운로드 결과에서는 일부 데이터가 “대표 1개 진단코드” 기준으로만
                              연결되어 보일 수 있으니 해석 시 주의가 필요합니다.
                            </p>

                            {(() => {
                              const key = drugSubTab === '3개월' ? '3M' : drugSubTab === '1년' ? '1Y' : drugSubTab === '3년' ? '3Y' : '5Y'
                              const rawByPeriod = byPeriodFromSummary && byPeriodFromSummary[key]
                              const rawList = Array.isArray(rawByPeriod) ? rawByPeriod : rawEpisodesFromSummary

                              const dateToCodes = new Map()
                              const codeToName = new Map()
                              for (const e of rawList) {
                                if (!e || e.source !== 'BASIC_TREAT') continue
                                const day = normalizeDateDigitsSafe(e.startDate || e.endDate)
                                const code = (e.diagnosisCode || '').toString().trim()
                                if (!day || !code) continue
                                if (!dateToCodes.has(day)) dateToCodes.set(day, new Set())
                                dateToCodes.get(day).add(code)
                                if (!codeToName.has(code) && e.diagnosisName) codeToName.set(code, e.diagnosisName)
                              }

                              const drugRaw = rawList.filter((e) => e && e.source === 'PRESCRIBE_DRUG' && e.drugName)
                              const deduped = dedupeAndLinkDrugsPharmacyFirst(drugRaw)

                              const diagGroups = new Map()
                              const unassignedDrugGroups = new Map()
                              for (const line of deduped) {
                                const day = normalizeDateDigitsSafe(line.startDate || line.endDate)
                                const explicitCode = (line.diagnosisCode || '').toString().trim()
                                let groupCode = explicitCode || null
                                if (!groupCode && day && dateToCodes.has(day)) {
                                  const set = dateToCodes.get(day)
                                  if (set && set.size === 1) {
                                    groupCode = Array.from(set)[0]
                                  }
                                }

                                if (groupCode) {
                                  if (!diagGroups.has(groupCode)) diagGroups.set(groupCode, [])
                                  diagGroups.get(groupCode).push(line)
                                } else {
                                  const dk = normalizeDrugKey(line)
                                  if (!unassignedDrugGroups.has(dk)) unassignedDrugGroups.set(dk, [])
                                  unassignedDrugGroups.get(dk).push(line)
                                }
                              }

                              const groupItems = []
                              for (const [code, lines] of diagGroups.entries()) {
                                groupItems.push({
                                  kind: 'DIAG',
                                  diagnosisCode: code,
                                  diagnosisName: codeToName.get(code) || null,
                                  lines,
                                })
                              }
                              // 약품키 그룹(진단코드 없음/복수 진단코드 등)
                              for (const [dk, lines] of unassignedDrugGroups.entries()) {
                                const first = lines[0] || {}
                                groupItems.push({
                                  kind: 'DRUG',
                                  drugKey: dk,
                                  drugName: first.drugName || '-',
                                  lines,
                                })
                              }

                              // 정렬: 진단코드 그룹 먼저, 그 다음 약품키 그룹
                              groupItems.sort((a, b) => {
                                if (a.kind !== b.kind) return a.kind === 'DIAG' ? -1 : 1
                                const ax = a.kind === 'DIAG' ? a.diagnosisCode : a.drugName
                                const bx = b.kind === 'DIAG' ? b.diagnosisCode : b.drugName
                                return String(ax || '').localeCompare(String(bx || ''))
                              })

                              const computeDrugTotalByDayMax = (lines) => {
                                if (!Array.isArray(lines) || lines.length === 0) return null
                                const maxByDay = new Map()
                                let hasAny = false
                                for (const x of lines) {
                                  const day = normalizeDateDigitsSafe(x?.startDate || x?.endDate) || '__NO_DAY__'
                                  const v = x?.totalDrugDays
                                  const n = typeof v === 'number' ? v : v !== null && v !== undefined && String(v).trim() !== '' ? Number(v) : null
                                  if (!Number.isFinite(n)) continue
                                  const prev = maxByDay.get(day)
                                  maxByDay.set(day, prev === undefined ? Math.max(0, n) : Math.max(prev, Math.max(0, n)))
                                  hasAny = true
                                }
                                if (!hasAny) return null
                                let sum = 0
                                for (const v of maxByDay.values()) sum += Math.max(0, v)
                                return sum
                              }
                              const minMaxDateFromLines = (lines) => {
                                let min = null
                                let max = null
                                for (const x of lines || []) {
                                  const day = normalizeDateDigitsSafe(x?.startDate || x?.endDate)
                                  if (!day) continue
                                  if (!min || day < min) min = day
                                  if (!max || day > max) max = day
                                }
                                return { min, max }
                              }

                              const summaries = groupItems.map((g, idx) => {
                                const lines = Array.isArray(g.lines) ? g.lines : []
                                const { min, max } = minMaxDateFromLines(lines)
                                const totalDrugDays = computeDrugTotalByDayMax(lines)
                                const diagnosisName =
                                  g.kind === 'DIAG'
                                    ? g.diagnosisName || '진단'
                                    : g.drugName || '투약'
                                const diagnosisCode = g.kind === 'DIAG' ? g.diagnosisCode : null
                                return {
                                  id: `${g.kind}-${idx}`,
                                  groupType: g.kind === 'DIAG' ? 'DRUGS_DIAG' : 'DRUGS_DRUGKEY',
                                  diagnosisName,
                                  diagnosisCode,
                                  startDate: min,
                                  endDate: max,
                                  count: lines.length,
                                  totalDrugDays,
                                  details: lines,
                                }
                              })

                              const { items, totalPages, currentPage } = paginated(summaries)

                              return (
                                <>
                                  {items.length === 0 && <p className="text-xs text-slate-500">표시할 투약 내역이 없습니다.</p>}
                                  <div className="space-y-2">
                                    {items.map((g) => (
                                      <div key={g.id} className="rounded border bg-white px-3 py-2">
                                        <div className="text-sm font-semibold text-slate-900">
                                          {g.diagnosisCode ? <span className="font-mono">{g.diagnosisCode}</span> : <span className="text-slate-500">진단코드 없음</span>}
                                          <span className="text-slate-700"> {g.diagnosisName}</span>
                                        </div>
                                        <div className="mt-1 text-xs text-slate-600">
                                          기간: {formatDate(g.startDate) || '-'} ~ {formatDate(g.endDate) || '-'} / 건수: {g.count}
                                        </div>
                                        <div className="mt-1 text-xs text-slate-600">
                                          총 투약일수: {g.totalDrugDays ?? '-'}일 (일자별 최대 투약일수 합산)
                                        </div>
                                        <div className="mt-2">
                                          <button
                                            type="button"
                                            className="rounded border px-2 py-0.5 text-xs hover:bg-slate-100"
                                            onClick={() => openDetailEpisode(g, { mode: 'drugs', context: 'DRUGS' })}
                                          >
                                            상세내역
                                          </button>
                                        </div>
                                      </div>
                                    ))}
                                  </div>

                                  {totalPages > 1 && (
                                    <div className="mt-2 flex items-center justify-end gap-2 text-[11px] text-slate-600">
                                      <button
                                        type="button"
                                        className="rounded border px-2 py-0.5 disabled:opacity-40"
                                        disabled={currentPage === 0}
                                        onClick={() => setPage((p) => Math.max(0, p - 1))}
                                      >
                                        이전
                                      </button>
                                      <span>
                                        {currentPage + 1} / {totalPages}
                                      </span>
                                      <button
                                        type="button"
                                        className="rounded border px-2 py-0.5 disabled:opacity-40"
                                        disabled={currentPage >= totalPages - 1}
                                        onClick={() => setPage((p) => p + 1)}
                                      >
                                        다음
                                      </button>
                                    </div>
                                  )}
                                </>
                              )
                            })()}
                          </div>
                        )}

                        {/* 3. 건강체 */}
                        {resultTab === 'healthy' && (
                          <div className="mt-3 space-y-3 text-xs">
                            <div className="flex flex-wrap items-center justify-between gap-2">
                              <div className="flex flex-wrap gap-2">
                                {['3개월', '1년내 검사', '5년 내 7일 치료/30일 투약', '5년내 입원/수술', '5년내 11대 중대질병'].map(
                                  (label) => (
                                    <button
                                      key={label}
                                      type="button"
                                      className={`rounded-full border px-3 py-1 text-xs ${
                                        healthySubTab === label
                                          ? 'bg-brand-navy text-white border-brand-navy'
                                          : 'border-slate-300 text-slate-600 hover:bg-slate-100'
                                      }`}
                                      onClick={() => {
                                        setHealthySubTab(label)
                                        setPage(0)
                                      }}
                                    >
                                      {label}
                                    </button>
                                  ),
                                )}
                              </div>
                              <button
                                type="button"
                                className="rounded border px-2 py-1 text-[11px] text-slate-600 hover:bg-slate-100"
                                onClick={() => openQuestionnaire('healthy')}
                              >
                                질문서 보기
                              </button>
                            </div>
                            <p className="text-slate-600">
                              건강체 인수 기준(단기 진료 이력, 최근 건강검진, 단기간·단기투약, 입원/수술 여부, 11대 중대질병 이력 등)을
                              기준으로 진료 내역을 필터링해 보여줍니다.
                            </p>
                            {healthySubTab === '1년내 검사' && (
                              <p className="rounded border border-amber-200 bg-amber-50 px-3 py-2 text-[11px] text-amber-800">
                                주의: 원래는 추가검사/재검사를 받은 사실 여부에 대한 질문이지만, 진료내역만으로는 파악이 어렵습니다. 이 화면은
                                진단명에 ‘검사’가 포함된 모든 내역을 포함합니다.
                              </p>
                            )}
                            {healthySubTab.includes('7일') && healthySubTab.includes('투약') && (
                              <p className="text-[11px] text-slate-500">
                                * 동일 일자에 복수 진단코드가 포함된 진료가 있는 경우, 투약 내역이 특정 진단코드로 정확히 귀속되지 못해 일부가 누락되거나
                                “대표 1개 진단코드” 기준으로만 연결되어 보일 수 있으니 해석 시 주의가 필요합니다.
                              </p>
                            )}
                            <div>
                              {(() => {
                                let filtered = episodesFromSummary

                                // 요구사항: [5년내 7일 치료/30일 투약]은 [모든투약내역]의 5년 기준 "총 투약일수(일자별 최대 합산) >= 30"인
                                // "진단코드 그룹" 중, (총 투약일수 >= 30) 또는 (총 치료일수 >= 7) 인 항목을 모두 보여준다.
                                if (healthySubTab.includes('7일') && healthySubTab.includes('투약')) {
                                  const key = '5Y'
                                  const rawByPeriod = byPeriodFromSummary && byPeriodFromSummary[key]
                                  const rawList = Array.isArray(rawByPeriod) ? rawByPeriod : rawEpisodesFromSummary

                                  const dateToCodes = new Map()
                                  const codeToName = new Map()
                                  for (const e of rawList) {
                                    if (!e || e.source !== 'BASIC_TREAT') continue
                                    const day = normalizeDateDigitsSafe(e.startDate || e.endDate)
                                    const code = (e.diagnosisCode || '').toString().trim()
                                    if (!day || !code) continue
                                    if (!dateToCodes.has(day)) dateToCodes.set(day, new Set())
                                    dateToCodes.get(day).add(code)
                                    if (!codeToName.has(code) && e.diagnosisName) codeToName.set(code, e.diagnosisName)
                                  }

                                  // 치료(외래/입원)일수: BASIC_TREAT 기반으로 동일 진단코드 그룹핑
                                  const treatGroups = new Map()
                                  for (const e of rawList) {
                                    if (!e || e.source !== 'BASIC_TREAT') continue
                                    // 약국(조제) 라인은 치료일수 집계에서 제외
                                    if (isPharmacyLike(e)) continue
                                    const code = (e.diagnosisCode || '').toString().trim()
                                    if (!code) continue
                                    if (!treatGroups.has(code)) treatGroups.set(code, [])
                                    treatGroups.get(code).push(e)
                                  }

                                  const drugRaw = rawList.filter((e) => e && e.source === 'PRESCRIBE_DRUG' && e.drugName)
                                  const deduped = dedupeAndLinkDrugsPharmacyFirst(drugRaw)

                                  const diagGroups = new Map()
                                  for (const line of deduped) {
                                    const day = normalizeDateDigitsSafe(line.startDate || line.endDate)
                                    const explicitCode = (line.diagnosisCode || '').toString().trim()
                                    let groupCode = explicitCode || null
                                    if (!groupCode && day && dateToCodes.has(day)) {
                                      const set = dateToCodes.get(day)
                                      if (set && set.size === 1) groupCode = Array.from(set)[0]
                                    }
                                    if (!groupCode) continue
                                    if (!diagGroups.has(groupCode)) diagGroups.set(groupCode, [])
                                    diagGroups.get(groupCode).push(line)
                                  }

                                  const computeTotalByDayMax = (lines, valueKey) => {
                                    if (!Array.isArray(lines) || lines.length === 0) return null
                                    const maxByDay = new Map()
                                    let hasAny = false
                                    for (const x of lines) {
                                      const day = normalizeDateDigitsSafe(x?.startDate || x?.endDate) || '__NO_DAY__'
                                      const v = x?.[valueKey]
                                      const n =
                                        typeof v === 'number' ? v : v !== null && v !== undefined && String(v).trim() !== '' ? Number(v) : null
                                      if (!Number.isFinite(n)) continue
                                      const prev = maxByDay.get(day)
                                      maxByDay.set(day, prev === undefined ? Math.max(0, n) : Math.max(prev, Math.max(0, n)))
                                      hasAny = true
                                    }
                                    if (!hasAny) return null
                                    let sum = 0
                                    for (const v of maxByDay.values()) sum += Math.max(0, v)
                                    return sum
                                  }
                                  const minMaxDateFromLines = (lines) => {
                                    let min = null
                                    let max = null
                                    for (const x of lines || []) {
                                      const day = normalizeDateDigitsSafe(x?.startDate || x?.endDate)
                                      if (!day) continue
                                      if (!min || day < min) min = day
                                      if (!max || day > max) max = day
                                    }
                                    return { min, max }
                                  }

                                  const summaries = []
                                  const allCodes = new Set([
                                    ...Array.from(diagGroups.keys()),
                                    ...Array.from(treatGroups.keys()),
                                  ])
                                  for (const code of allCodes) {
                                    const drugLines = diagGroups.get(code) || []
                                    const treatLines = treatGroups.get(code) || []
                                    const totalDrugDays = computeTotalByDayMax(drugLines, 'totalDrugDays') || 0
                                    const totalTreatmentDays = computeTotalByDayMax(treatLines, 'totalTreatmentDays') || 0
                                    if (totalDrugDays < 30 && totalTreatmentDays < 7) continue

                                    const mergedDetails = [...treatLines, ...drugLines]
                                    const { min, max } = minMaxDateFromLines(mergedDetails)
                                    summaries.push({
                                      id: `DRUG_7_30_5Y-${code}`,
                                      groupType: 'DRUGS_DIAG',
                                      diagnosisCode: code,
                                      diagnosisName: codeToName.get(code) || '진단',
                                      startDate: min,
                                      endDate: max,
                                      count: mergedDetails.length,
                                      totalDrugDays,
                                      totalTreatmentDays,
                                      details: mergedDetails,
                                    })
                                  }
                                  summaries.sort((a, b) => String(a.diagnosisCode || '').localeCompare(String(b.diagnosisCode || '')))
                                  filtered = summaries
                                } else if (healthyViewFromSummary) {
                                  let key = null
                                  if (healthySubTab === '3개월') {
                                    key = '3M'
                                  } else if (healthySubTab === '1년내 검사') {
                                    key = '1Y_CHECK'
                                  } else if (healthySubTab === '5년내 입원/수술') {
                                    key = 'HOSP_5Y'
                                  } else if (healthySubTab === '5년내 11대 중대질병') {
                                    key = 'MAJOR11_5Y'
                                  }
                                  // 서버가 빈 배열을 내려주면 "없음"이 정상인데, 기존엔 전체 episodes로 fallback 되어 기간 필터가 깨졌음
                                  if (key && Array.isArray(healthyViewFromSummary[key])) {
                                    filtered = healthyViewFromSummary[key]
                                  }
                                }
                                const { items, totalPages, currentPage } = paginated(filtered)
                                return (
                                  <>
                                    <div>
                                      {items.length === 0 && (
                                        <p className="text-xs text-slate-500">표시할 내역이 없습니다.</p>
                                      )}
                                      {items.map((item) =>
                                        renderTreatmentCard(item, {
                                          showDrugExtra: healthySubTab.includes('투약'),
                                          showInpatientExtra: healthySubTab.includes('입원/수술'),
                                          detailContext:
                                            healthySubTab === '1년내 검사'
                                              ? 'EXAM'
                                              : healthySubTab.includes('입원/수술')
                                              ? 'INHOSP_SURGERY'
                                              : healthySubTab.includes('7일')
                                              ? 'TREATMENTS'
                                              : 'ALL',
                                        }),
                                      )}
                                    </div>
                                    {totalPages > 1 && (
                                      <div className="mt-2 flex items-center justify-end gap-2 text-[11px] text-slate-600">
                                        <button
                                          type="button"
                                          className="rounded border px-2 py-0.5 disabled:opacity-40"
                                          disabled={currentPage === 0}
                                          onClick={() => setPage((p) => Math.max(0, p - 1))}
                                        >
                                          이전
                                        </button>
                                        <span>
                                          {currentPage + 1} / {totalPages}
                                        </span>
                                        <button
                                          type="button"
                                          className="rounded border px-2 py-0.5 disabled:opacity-40"
                                          disabled={currentPage >= totalPages - 1}
                                          onClick={() => setPage((p) => p + 1)}
                                        >
                                          다음
                                        </button>
                                      </div>
                                    )}
                                  </>
                                )
                              })()}
                            </div>
                          </div>
                        )}

                        {/* 4. 유병자 */}
                        {resultTab === 'substandard' && (
                          <div className="mt-3 space-y-3 text-xs">
                            <div className="flex flex-wrap items-center justify-between gap-2">
                              <div className="flex flex-wrap gap-2">
                              {[
                                '3개월',
                                '1년내 입원/수술',
                                '2년내 입원/수술',
                                '3년내 입원/수술',
                                '4년내 입원/수술',
                                '5년내 입원/수술',
                                '5년내 6대 중대질병',
                              ].map((label) => (
                                <button
                                  key={label}
                                  type="button"
                                  className={`rounded-full border px-3 py-1 text-xs ${
                                    substandardSubTab === label
                                      ? 'bg-brand-navy text-white border-brand-navy'
                                      : 'border-slate-300 text-slate-600 hover:bg-slate-100'
                                  }`}
                                  onClick={() => {
                                    setSubstandardSubTab(label)
                                    setPage(0)
                                  }}
                                >
                                  {label}
                                </button>
                              ))}
                              </div>
                              <button
                                type="button"
                                className="rounded border px-2 py-1 text-[11px] text-slate-600 hover:bg-slate-100"
                                onClick={() => openQuestionnaire('substandard')}
                              >
                                질문서 보기
                              </button>
                            </div>
                            <p className="text-slate-600">
                              최근 입원/수술 이력과 6대 중대질병 기록 등을 기반으로 유병자 인수 가능성을 검토할 수 있도록 관련
                              진료내역을 묶어서 보여줍니다.
                            </p>
                            {substandardSubTab === '3개월' && (
                              <p className="rounded border border-amber-200 bg-amber-50 px-3 py-2 text-[11px] text-amber-800">
                                안내: 유병자 인수 기준의 ‘입원 소견/수술 소견/재검사 소견’ 여부는 진료내역만으로 정확히 판단하기 어렵습니다. 이 화면은
                                최근 3개월 내 <b>진료 내역(투약 제외)</b>을 진단코드 기준으로 그룹화하여 참고용으로 보여줍니다.
                              </p>
                            )}
                            <div>
                              {(() => {
                                let filtered = episodesFromSummary
                                if (substandardViewFromSummary) {
                                  let key = null
                                  if (substandardSubTab === '3개월') {
                                    key = 'recentHighRisk3M'
                                  } else if (substandardSubTab === '1년내 입원/수술') {
                                    key = 'inpatientOrSurgeryLike1Y'
                                  } else if (substandardSubTab === '2년내 입원/수술') {
                                    key = 'inpatientOrSurgeryLike2Y'
                                  } else if (substandardSubTab === '3년내 입원/수술') {
                                    key = 'inpatientOrSurgeryLike3Y'
                                  } else if (substandardSubTab === '4년내 입원/수술') {
                                    key = 'inpatientOrSurgeryLike4Y'
                                  } else if (substandardSubTab === '5년내 입원/수술') {
                                    key = 'inpatientOrSurgeryLike5Y'
                                  } else if (substandardSubTab === '5년내 6대 중대질병') {
                                    key = 'MAJOR6_5Y'
                                  }
                                  // 서버가 빈 배열을 내려주면 "없음"이 정상인데, 기존엔 전체 episodes로 fallback 되어 기간 필터가 깨졌음
                                  if (key && Array.isArray(substandardViewFromSummary[key])) {
                                    filtered = substandardViewFromSummary[key]
                                  }
                                }
                                const { items, totalPages, currentPage } = paginated(filtered)
                                return (
                                  <>
                                    <div>
                                      {items.length === 0 && (
                                        <p className="text-xs text-slate-500">표시할 내역이 없습니다.</p>
                                      )}
                                      {items.map((item) =>
                                        renderTreatmentCard(item, {
                                          showDrugExtra: substandardSubTab.includes('투약'),
                                          showInpatientExtra: substandardSubTab.includes('입원/수술'),
                                          detailContext: substandardSubTab.includes('입원/수술') ? 'INHOSP_SURGERY' : 'ALL',
                                        }),
                                      )}
                                    </div>
                                    {totalPages > 1 && (
                                      <div className="mt-2 flex items-center justify-end gap-2 text-[11px] text-slate-600">
                                        <button
                                          type="button"
                                          className="rounded border px-2 py-0.5 disabled:opacity-40"
                                          disabled={currentPage === 0}
                                          onClick={() => setPage((p) => Math.max(0, p - 1))}
                                        >
                                          이전
                                        </button>
                                        <span>
                                          {currentPage + 1} / {totalPages}
                                        </span>
                                        <button
                                          type="button"
                                          className="rounded border px-2 py-0.5 disabled:opacity-40"
                                          disabled={currentPage >= totalPages - 1}
                                          onClick={() => setPage((p) => p + 1)}
                                        >
                                          다음
                                        </button>
                                      </div>
                                    )}
                                  </>
                                )
                              })()}
                            </div>
                          </div>
                        )}

                        {/* 6. 치아 */}
                        {resultTab === 'dental' && (
                          <div className="mt-3 space-y-3 text-xs">
                            <div className="flex flex-wrap gap-2">
                              {['틀니', '1년 내', '5년 내'].map((label) => (
                                <button
                                  key={label}
                                  type="button"
                                  className={`rounded-full border px-3 py-1 text-xs ${
                                    dentalSubTab === label
                                      ? 'bg-brand-navy text-white border-brand-navy'
                                      : 'border-slate-300 text-slate-600 hover:bg-slate-100'
                                  }`}
                                  onClick={() => {
                                    setDentalSubTab(label)
                                    setPage(0)
                                  }}
                                >
                                  {label}
                                </button>
                              ))}
                            </div>
                            <p className="text-slate-600">
                              틀니, 스케일링, 보철 등 치과 관련 진료내역을 기간·유형별로 나누어 보여주며, 치아고지명/세부명을 함께
                              확인할 수 있습니다.
                            </p>
                            <div>
                              {(() => {
                                let filtered = episodesFromSummary
                                if (dentalViewFromSummary) {
                                  let key = null
                                  if (dentalSubTab === '틀니') {
                                    key = 'denture'
                                  } else if (dentalSubTab === '1년 내') {
                                    key = 'within1Y'
                                  } else if (dentalSubTab === '5년 내') {
                                    key = 'within5Y'
                                  }
                                  // 서버가 빈 배열을 내려주면 "없음"이 정상인데, 기존엔 전체 episodes로 fallback 되어 일반진단이 섞였음
                                  if (key && Array.isArray(dentalViewFromSummary[key])) {
                                    filtered = dentalViewFromSummary[key]
                                  }
                                }
                                const { items, totalPages, currentPage } = paginated(filtered)
                                return (
                                  <>
                                    <div>
                                      {items.length === 0 && (
                                        <p className="text-xs text-slate-500">표시할 내역이 없습니다.</p>
                                      )}
                                      {items.map((item) =>
                                        renderTreatmentCard(item, {
                                          showDentalExtra: true,
                                        }),
                                      )}
                                    </div>
                                    {totalPages > 1 && (
                                      <div className="mt-2 flex items-center justify-end gap-2 text-[11px] text-slate-600">
                                        <button
                                          type="button"
                                          className="rounded border px-2 py-0.5 disabled:opacity-40"
                                          disabled={currentPage === 0}
                                          onClick={() => setPage((p) => Math.max(0, p - 1))}
                                        >
                                          이전
                                        </button>
                                        <span>
                                          {currentPage + 1} / {totalPages}
                                        </span>
                                        <button
                                          type="button"
                                          className="rounded border px-2 py-0.5 disabled:opacity-40"
                                          disabled={currentPage >= totalPages - 1}
                                          onClick={() => setPage((p) => p + 1)}
                                        >
                                          다음
                                        </button>
                                      </div>
                                    )}
                                  </>
                                )
                              })()}
                            </div>
                          </div>
                        )}

                        {parsedSummary == null && (
                          <p className="mt-2 text-[11px] text-slate-400">
                            * 현재는 CODEF 데모 응답을 기반으로 한 예시 데이터이며, 실제 진료/투약 내역 분석 로직 및 세부 필드는
                            추후 연동 시 정교하게 반영될 예정입니다.
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                ))}
                {filteredJobs.length === 0 && (
                  <p className="text-xs text-slate-500">표시할 분석 내역이 없습니다.</p>
                )}
              </div>
              <div className="mt-2 flex items-center justify-end gap-2 text-[11px] text-slate-600">
                <button
                  type="button"
                  className="rounded border px-2 py-0.5 disabled:opacity-40"
                  disabled={reportsPage === 0}
                  onClick={() => setReportsPage((p) => Math.max(0, p - 1))}
                >
                  이전
                </button>
                <span>{reportsPage + 1}</span>
                <button
                  type="button"
                  className="rounded border px-2 py-0.5 disabled:opacity-40"
                  disabled={!jobsHasNext}
                  onClick={() => setReportsPage((p) => p + 1)}
                >
                  다음
                </button>
              </div>

            </>
          )}
        </section>
      )}

      {/* 3) 고객 목록 탭 */}
      {activeTab === 'customers' && (
        <section className="rounded-lg border bg-white p-4 shadow-sm space-y-4">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2 text-sm">
            <div className="flex items-center gap-2">
              <input
                type="text"
                className="rounded border px-2 py-1 text-sm"
                placeholder="고객명/연락처 검색"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
              <button
                type="button"
                className="rounded border px-3 py-1 text-sm hover:bg-slate-50"
                onClick={() => customersQuery.refetch()}
              >
                새로고침
              </button>
            </div>
          </div>

          {customersQuery.isLoading ? (
            <p className="text-xs text-slate-500">고객 목록을 불러오는 중입니다...</p>
          ) : customersQuery.isError ? (
            <p className="text-xs text-red-500">고객 목록을 불러오지 못했습니다.</p>
          ) : (
            <>
            <div className="space-y-2 text-sm">
              {filteredCustomers.map((c) => (
                <div key={`${c.customerName || 'UNKNOWN'}|${c.phoneNumber || ''}`} className="rounded border px-3 py-2">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="font-medium text-slate-900">
                        {c.customerName ? `고객명: ${c.customerName}` : '고객 (이름 미상)'}
                      </div>
                      <div className="text-xs text-slate-500">연락처: {c.phoneNumber || '-'}</div>
                    </div>
                    <div className="flex flex-col items-end gap-1">
                      <div className="text-xs text-slate-500">
                        메모: {notes[c.customerName || ''] ? notes[c.customerName || ''] : '등록된 메모가 없습니다.'}
                      </div>
                      <div className="flex gap-1">
                        <button
                          type="button"
                          className="rounded border px-2 py-0.5 text-xs hover:bg-slate-100"
                          onClick={() => handleEditNote(c.customerName || '')}
                        >
                          메모
                        </button>
                        <button
                          type="button"
                          className="rounded border px-2 py-0.5 text-xs border-brand-navy text-brand-navy hover:bg-brand-navy hover:text-white"
                          onClick={() => handleGoToReportsForCustomer(c.customerName || '')}
                        >
                          분석목록조회
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
              {filteredCustomers.length === 0 && (
                <p className="text-xs text-slate-500">표시할 고객이 없습니다.</p>
              )}
            </div>
              <div className="mt-2 flex items-center justify-end gap-2 text-[11px] text-slate-600">
                <button
                  type="button"
                  className="rounded border px-2 py-0.5 disabled:opacity-40"
                  disabled={customersPage === 0}
                  onClick={() => setCustomersPage((p) => Math.max(0, p - 1))}
                >
                  이전
                </button>
                <span>{customersPage + 1}</span>
                <button
                  type="button"
                  className="rounded border px-2 py-0.5 disabled:opacity-40"
                  disabled={!customersHasNext}
                  onClick={() => setCustomersPage((p) => p + 1)}
                >
                  다음
                </button>
              </div>
            </>
          )}
        </section>
      )}

      {/* 상세 내역 팝업 */}
      {detailEpisode && (
        <div
          className="fixed inset-0 z-40 flex items-center justify-center bg-black/40 p-4 overscroll-contain"
          onMouseDown={(e) => {
            // 배경 클릭 시 닫기 (팝업 내부 클릭은 무시)
            if (e.target === e.currentTarget) closeDetailEpisode()
          }}
        >
          {/* 팝업 자체는 고정 높이/비스크롤. 스크롤은 하단 세부내역(details) 리스트만. */}
          <div
            className="w-full max-w-lg rounded-lg bg-white shadow-lg p-4 text-sm max-h-[80vh] overflow-hidden flex flex-col overscroll-contain"
            onWheel={(e) => {
              // 모달 헤더/상단 필드 영역에서 스크롤해도 "세부내역 리스트"만 스크롤되도록 유도
              const target = e.target
              const overDetails =
                target && typeof target.closest === 'function'
                  ? target.closest('[data-detail-scroll]')
                  : null
              if (overDetails) return
              const el = detailListScrollRef.current
              if (!el) return
              e.preventDefault()
              e.stopPropagation()
              el.scrollTop += e.deltaY
            }}
          >
            <div className="flex items-center justify-between mb-2">
              <div className="font-semibold text-slate-900">진료/투약 상세 내역</div>
              <button
                type="button"
                className="inline-flex items-center gap-1 rounded border px-2 py-1 text-xs font-semibold text-slate-700 hover:bg-slate-50"
                onClick={closeDetailEpisode}
                aria-label="상세내역 닫기"
              >
                <span className="text-slate-500">✕</span>
                <span>닫기</span>
              </button>
            </div>
            {/* NOTE: 이 영역이 "높이를 가진 flex 컨테이너"여야 하단 details 리스트가 flex로 남은 높이를 받아 스크롤된다. */}
            <div className="text-xs flex flex-1 min-h-0 flex-col overflow-hidden">
              {(() => {
                const keysInEpisode = detailEpisode ? Object.keys(detailEpisode) : []
                const allowedKeys = keysInEpisode.filter((k) => Boolean(fieldLabels[k]))
                const ordered = [
                  ...detailFieldOrder.filter((k) => allowedKeys.includes(k)),
                  ...allowedKeys.filter((k) => !detailFieldOrder.includes(k)),
                ]
                const nonDetailKeys = ordered
                  .filter((k) => k !== 'details')
                  .filter((k) => (k === 'hiddenGuide' ? detailShowHiddenGuide : true))
                  .filter((k) => !isEmptyDetailValue(detailEpisode[k]))

                const detailsValue = detailEpisode?.details
                const hasDetails = Array.isArray(detailsValue) && detailsValue.length > 0
                const sumFromDetails = (key) => {
                  if (!hasDetails) return null
                  // totalDrugDays는 단순 합산하면 과대계상될 수 있어,
                  // "일자별 최대 투약일수"만 합산한다.
                  if (key === 'totalDrugDays') {
                    const maxByDay = new Map()
                    let hasAny = false
                    for (const d of detailsValue) {
                      const v = d ? d[key] : null
                      const n =
                        typeof v === 'number'
                          ? v
                          : v !== null && v !== undefined && String(v).trim() !== ''
                          ? Number(v)
                          : null
                      if (!Number.isFinite(n)) continue
                      const day = normalizeDateDigitsSafe(d?.startDate || d?.endDate) || '__NO_DAY__'
                      const prev = maxByDay.get(day)
                      maxByDay.set(day, prev === undefined ? Math.max(0, n) : Math.max(prev, Math.max(0, n)))
                      hasAny = true
                    }
                    if (!hasAny) return null
                    let sum = 0
                    for (const x of maxByDay.values()) sum += Math.max(0, x)
                    return sum
                  }

                  // totalTreatmentDays: 약국(조제) 라인은 치료일수 집계에서 제외 + 일자별 최대치만 합산
                  if (key === 'totalTreatmentDays') {
                    const maxByDay = new Map()
                    let hasAny = false
                    for (const d of detailsValue) {
                      if (isPharmacyLike(d)) continue
                      const v = d ? d[key] : null
                      const n =
                        typeof v === 'number'
                          ? v
                          : v !== null && v !== undefined && String(v).trim() !== ''
                          ? Number(v)
                          : null
                      if (!Number.isFinite(n)) continue
                      const day = normalizeDateDigitsSafe(d?.startDate || d?.endDate) || '__NO_DAY__'
                      const prev = maxByDay.get(day)
                      maxByDay.set(day, prev === undefined ? Math.max(0, n) : Math.max(prev, Math.max(0, n)))
                      hasAny = true
                    }
                    if (!hasAny) return null
                    let sum = 0
                    for (const x of maxByDay.values()) sum += Math.max(0, x)
                    return sum
                  }

                  // 그 외는 단순 합산
                  let sum = 0
                  let hasAny = false
                  for (const d of detailsValue) {
                    const v = d ? d[key] : null
                    const n =
                      typeof v === 'number'
                        ? v
                        : v !== null && v !== undefined && String(v).trim() !== ''
                        ? Number(v)
                        : null
                    if (Number.isFinite(n)) {
                      sum += Math.max(0, n)
                      hasAny = true
                    }
                  }
                  return hasAny ? sum : null
                }
                const popupSumTreatmentDays = sumFromDetails('totalTreatmentDays')
                const popupSumDrugDays = sumFromDetails('totalDrugDays')
                const popupEncounterCount = (() => {
                  if (!hasDetails) return null
                  // 요약 카드와 동일 기준으로 계산(약국 제외, 기관+일자 dedupe, 필요 시 raw fallback)
                  const details = detailEpisode?.details
                  const list = Array.isArray(details) ? details : []
                  if (list.length === 0) return null

                  const dayOf = (x) => normalizeDateDigitsSafe(x?.startDate || x?.endDate)
                  const isPharm = (x) => isPharmacyLike(x) || String(x?.hospitalName || '').includes('약국')
                  const norm = (v) => (v || '').toString().replaceAll(/\s+/g, ' ').trim()
                  const hospKey = (x) => {
                    const name = norm(x?.hospitalName || '')
                    const code = norm(x?.hospitalCode || '')
                    return name ? name : (code ? code : '_')
                  }

                  const visitSet = new Set()
                  const days = new Set()
                  for (const x of list) {
                    const d = dayOf(x)
                    if (d) days.add(d)
                    if (isPharm(x)) continue
                    if (!d) continue
                    visitSet.add(`${d}|${hospKey(x)}`)
                  }
                  if (visitSet.size > 0) return visitSet.size

                  if (!Array.isArray(rawEpisodesFromSummary) || rawEpisodesFromSummary.length === 0) return 0
                  for (const d of days) {
                    for (const x of rawEpisodesFromSummary) {
                      const src = String(x?.source || '')
                      if (!(src.includes('DETAIL_TREAT') || src.includes('BASIC_TREAT'))) continue
                      if (dayOf(x) !== d) continue
                      if (isPharm(x)) continue
                      visitSet.add(`${d}|${hospKey(x)}`)
                    }
                  }
                  return visitSet.size
                })()

                return (
                  // NOTE: 부모가 flex 컨테이너이므로 flex-1로 남은 높이를 채운다.
                  <div className="min-h-0 flex flex-1 flex-col overflow-hidden">
                    {/* 상단 필드 영역(고정, 비스크롤) */}
                    <div className="space-y-2 shrink-0">
                      {nonDetailKeys.map((key) => {
                        // UX: "총 진료일/총 투약일"은 중복 노출 대신, 기존 필드의 표시값을 합산값으로 치환한다.
                        const value =
                          key === 'count'
                            ? (popupEncounterCount ?? detailEpisode?.encounterCount ?? detailEpisode?.count)
                            : key === 'totalTreatmentDays' && popupSumTreatmentDays !== null
                            ? popupSumTreatmentDays
                            : key === 'totalDrugDays' && popupSumDrugDays !== null
                            ? popupSumDrugDays
                            : detailEpisode[key]
                        const label = fieldLabels[key]
                        if (!label) return null // 영어 키/라벨 노출 방지
                        const displayValue = formatDetailValue(key, value)
                        if (isEmptyDetailValue(displayValue)) return null
                        return (
                          <div key={key} className="flex">
                            <div className="w-32 font-semibold text-slate-700">{label}</div>
                            <div className="flex-1 text-slate-600 break-all">{displayValue}</div>
                          </div>
                        )
                      })}
                    </div>

                    {/* 검사 탭: 영상진단 포함 토글(기본 OFF) */}
                    {String(detailEpisodeContext || '').toUpperCase() === 'EXAM' && (
                      <div className="mt-2 shrink-0">
                        <label className="inline-flex items-center gap-2 text-[11px] text-slate-700 select-none">
                          <input
                            type="checkbox"
                            className="h-3 w-3"
                            checked={examIncludeImaging}
                            onChange={(e) => setExamIncludeImaging(Boolean(e.target.checked))}
                          />
                          영상진단 포함해서 보기
                        </label>
                        <div className="mt-0.5 text-[11px] text-slate-500">
                          기본은 검사료/‘검사’ 키워드만 표시합니다. (X-ray/CT/MRI 등은 옵션)
                        </div>
                      </div>
                    )}

                    {/* 입원/수술 탭: 수술이 없을 때만(또는 필요 시) 입원 처치/치료 라인까지 확장 표시 */}
                    {String(detailEpisodeContext || '').toUpperCase() === 'INHOSP_SURGERY' && (
                      <div className="mt-2 shrink-0">
                        <label className="inline-flex items-center gap-2 text-[11px] text-slate-700 select-none">
                          <input
                            type="checkbox"
                            className="h-3 w-3"
                            checked={inhospIncludeNonSurgery}
                            onChange={(e) => setInhospIncludeNonSurgery(Boolean(e.target.checked))}
                          />
                          수술 외 처치/치료/재료까지 모두 보기(참고용)
                        </label>
                        <div className="mt-0.5 text-[11px] text-slate-500">
                          기본은 “수술명(봉합술/성형술/절제술 등)”만 표시합니다. 켜면 입원 중 처치/치료/재료 라인도 함께 표시됩니다.
                        </div>
                      </div>
                    )}

                    {/* 숨은보상(수술/시술) 탭: 처치 및 수술 전체 보기 토글 */}
                    {String(detailEpisodeContext || '').toUpperCase() === 'HIDDEN_SURGERY' && (
                      <div className="mt-2 shrink-0">
                        <label className="inline-flex items-center gap-2 text-[11px] text-slate-700 select-none">
                          <input
                            type="checkbox"
                            className="h-3 w-3"
                            checked={hiddenIncludeAllProcedures}
                            onChange={(e) => setHiddenIncludeAllProcedures(Boolean(e.target.checked))}
                          />
                          처치 및 수술 전체 보기(재료/요법 포함, 참고용)
                        </label>
                        <div className="mt-0.5 text-[11px] text-slate-500">
                          기본은 “수술명(봉합술/성형술/절제술 등)” 위주로만 표시합니다.
                        </div>
                      </div>
                    )}

                    {/* 투약(상세팝업): 모든 정보 보기 토글
                        - DRUGS 탭 뿐 아니라, '5년내 7일 치료/30일 투약' 등에서 상세팝업이 투약 모드로 열리는 경우에도 노출되어야 한다.
                    */}
                    {detailEpisodeMode === 'drugs' && (
                      <div className="mt-2 shrink-0">
                        <label className="inline-flex items-center gap-2 text-[11px] text-slate-700 select-none">
                          <input
                            type="checkbox"
                            className="h-3 w-3"
                            checked={drugsShowAllInfo}
                            onChange={(e) => setDrugsShowAllInfo(Boolean(e.target.checked))}
                          />
                          조제료/수출명 포함 전체 보기(참고용)
                        </label>
                        <div className="mt-0.5 text-[11px] text-slate-500">
                          기본은 실제 투약 약품명 위주로만 표시하며, 약국/조제료 성격 라인은 숨깁니다.
                        </div>
                      </div>
                    )}

                    {/* 하단 세부내역(일자별 그룹) 리스트만 스크롤 */}
                    {hasDetails && (
                      <div className="mt-3 flex flex-1 min-h-0 flex-col overflow-hidden">
                        <div className="mb-2 font-semibold text-slate-700">{fieldLabels.details}</div>
                        <div
                          ref={detailListScrollRef}
                          data-detail-scroll
                          className="min-h-0 flex-1 overflow-y-auto pr-1 space-y-2 overscroll-contain"
                        >
                          {(() => {
                            const dayKey = (x) => normalizeDateDigitsSafe(x?.startDate || x?.endDate) || '__NO_DAY__'
                            const groups = new Map()
                            for (const x of detailsValue) {
                              const k = dayKey(x)
                              if (!groups.has(k)) groups.set(k, [])
                              groups.get(k).push(x)
                            }
                            const days = Array.from(groups.keys()).sort((a, b) => {
                              if (a === '__NO_DAY__') return 1
                              if (b === '__NO_DAY__') return -1
                              return b.localeCompare(a)
                            })

                            return (
                              <div className="space-y-3">
                                {days.map((d) => {
                                  const lines = groups.get(d) || []
                                  const detailTreats = lines.filter((x) => x?.source === 'DETAIL_TREAT' || String(x?.source || '').includes('DETAIL_TREAT'))
                                  const basics = lines.filter((x) => x?.source === 'BASIC_TREAT' || String(x?.source || '').includes('BASIC_TREAT'))

                                  // (보강) details에 DETAIL_TREAT가 누락되는 케이스가 있어, allEpisodesRaw에서 같은 날짜의 DETAIL_TREAT를 합쳐서 보여준다.
                                  // - 진단코드 없는 처치/행위 행은 byPeriodGroupedDiagnosis.details에 빠지기 쉬움(CODEF resDetailTreatList 특성)
                                  const mergedDetailTreats = (() => {
                                    const base = Array.isArray(detailTreats) ? detailTreats : []
                                    if (!Array.isArray(rawEpisodesFromSummary) || rawEpisodesFromSummary.length === 0) return base
                                    const dayDigits = d === '__NO_DAY__' ? null : d
                                    if (!dayDigits) return base

                                    const keyOf = (x) => {
                                      const day = normalizeDateDigitsSafe(x?.startDate || x?.endDate) || ''
                                      const org = (x?.hospitalCode || x?.hospitalName || '').toString().trim()
                                      const t = (x?.treatType || x?.treatTypeDetail || '').toString().trim()
                                      const cn = (x?.codeName || '').toString().trim()
                                      return `${day}|${org}|${t}|${cn}`
                                    }
                                    const seen = new Set(base.map(keyOf))

                                    const extra = rawEpisodesFromSummary
                                      .filter((x) => String(x?.source || '').includes('DETAIL_TREAT'))
                                      .filter((x) => normalizeDateDigitsSafe(x?.startDate || x?.endDate) === dayDigits)
                                      .filter((x) => !seen.has(keyOf(x)))

                                    return [...base, ...extra]
                                  })()

                                  const titleDate = d === '__NO_DAY__' ? '일자 정보 없음' : (formatDate(d) || d)
                                  const isCollapsed = collapsedDetailDays.has(d)

                                  // NOTE: 하루 단위에서 "세부진료/투약"을 먼저 보여주고, 필요하면 기본진료는 하단에 붙인다.
                                  // (개선) 팝업 표시 정책
                                  // - 진료내역 모드: 병원 방문은 "병원 카드 1개 + (진료형태|코드명) 목록", 약국은 방문 요약만(조제료등 목록 숨김)
                                  // - 투약내역 모드: 병원 방문은 동일하게 합산 표시, 약국/병원 투약은 "투약 내역"만 목록화, 조제료등 목록 숨김
                                  const isPharmacyLine = (x) => isPharmacyLike(x) || String(x?.hospitalName || '').includes('약국')
                                  // (중요) hospitalCode 유무에 따라 동일 병원이 분리되는 문제 방지:
                                  // 병원명 우선(정규화)로 key를 만들고, 병원코드는 fallback으로만 사용한다.
                                  const normalizeOrgKey = (v) => (v || '').toString().replaceAll(/\s+/g, ' ').trim()
                                  const hospitalKey = (x) => {
                                    const name = normalizeOrgKey(x?.hospitalName)
                                    if (name) return name
                                    const code = normalizeOrgKey(x?.hospitalCode)
                                    return code
                                  }
                                  const normalizeSlash = (s) => {
                                    if (!s) return ''
                                    const parts = String(s)
                                      .split('/')
                                      .map((p) => p.trim())
                                      .filter(Boolean)
                                    const out = []
                                    for (const p of parts) {
                                      if (out.length === 0 || out[out.length - 1] !== p) out.push(p)
                                    }
                                    return out.join(' / ')
                                  }
                                  const normalizeDetailContext = (ctx) => {
                                    if (!ctx) return 'ALL'
                                    const c = String(ctx).toUpperCase()
                                    return c === 'EXAM' ||
                                      c === 'INHOSP_SURGERY' ||
                                      c === 'HIDDEN_SURGERY' ||
                                      c === 'TREATMENTS' ||
                                      c === 'DRUGS'
                                      ? c
                                      : 'ALL'
                                  }
                                  const ctx = normalizeDetailContext(detailEpisodeContext)

                                  const isExamLine = (x) => {
                                    const tt = (x?.treatType || x?.treatTypeDetail || '').toString()
                                    const cn = (x?.codeName || '').toString()
                                    // "검사" 탭은 검사료/검사 키워드만 노출 (영상진단은 기본 제외, 토글 ON이면 포함)
                                    if (tt.includes('검사료') || cn.includes('검사')) return true
                                    if (examIncludeImaging && (tt.includes('영상진단') || tt.includes('방사선치료') || tt.includes('영상저장'))) return true
                                    return false
                                  }
                                  const isTreatmentLine = (x) => {
                                    const tt = (x?.treatType || x?.treatTypeDetail || '').toString()
                                    const cn = (x?.codeName || '').toString()
                                    // 치료/진료로 의미있는 라인 위주: 처치/수술/이학요법 계열만 남김
                                    if (tt.includes('처치') || cn.includes('처치')) return true
                                    if (tt.includes('수술') || cn.includes('수술') || cn.includes('시술')) return true
                                    if (tt.includes('이학요법료') || tt.includes('이학요법')) return true
                                    return false
                                  }
                                  const isInhospSurgeryLine = (x) => {
                                    const tt = (x?.treatType || x?.treatTypeDetail || '').toString()
                                    const cn = (x?.codeName || '').toString()
                                    // 입원/수술 탭: 진찰료 + 이학요법 + 처치/수술 위주로 축약(식대/입원료/검사/영상/재료 등 제외)
                                    if (tt.includes('진찰료')) return true
                                    if (tt.includes('이학요법료') || tt.includes('이학요법')) return true
                                    if (tt.includes('처치') || cn.includes('처치')) return true
                                    if (tt.includes('수술') || cn.includes('수술') || cn.includes('시술')) return true
                                    return false
                                  }
                                  const isLikelySurgeryLine = (x) => {
                                    // IMPORTANT:
                                    // treatType에는 "처치 및 수술" 문자열이 포함되어 있어,
                                    // 그 문자열만으로 수술로 판정하면 SURGIPRO 같은 재료/요법까지 오탐한다.
                                    // 따라서 '수술탭' UI 필터는 codeName(세부 진료명)만 보고 판정한다.
                                    const cn = (x?.codeName || '').toString().trim()
                                    if (!cn) return false

                                    const text = cn

                                    // 수술 확정/강하게 시사하는 키워드(명칭 기준)
                                    const kw =
                                      text.includes('제왕절개') ||
                                      text.includes('절제술') ||
                                      text.includes('제거술') ||
                                      text.includes('성형술') ||
                                      text.includes('재건술') ||
                                      text.includes('봉합술') ||
                                      text.includes('봉합') ||
                                      text.includes('절개') ||
                                      text.includes('적출') ||
                                      text.includes('소작')

                                    if (!kw) return false

                                    // 수술로 보기 어려운 요법/처치/재료성 라인은 제외
                                    const ex =
                                      text.includes('요법') ||
                                      text.includes('치료') ||
                                      text.includes('관리') ||
                                      text.includes('감시') ||
                                      // 마취/통증조절/주입 처치는 "수술명"으로 보지 않음 (예: 경막외 주입(Epidural PCA)-시술당일...)
                                      text.includes('PCA') ||
                                      text.toLowerCase().includes('epidural') ||
                                      text.toLowerCase().includes('infuser') ||
                                      text.includes('경막외') ||
                                      text.includes('주입') ||
                                      text.includes('카테터') ||
                                      text.includes('catheter') ||
                                      text.includes('단순처치') ||
                                      text.includes('체위변경') ||
                                      text.includes('배액') ||
                                      text.includes('산소') ||
                                      // 재료/제품명(영문 대문자) 및 흔한 키워드
                                      text.includes('SUTURE') ||
                                      text.includes('SILK') ||
                                      text.includes('VICRYL') ||
                                      text.includes('ETHIBOND') ||
                                      text.includes('PDS') ||
                                      text.includes('TIGHTROPE') ||
                                      text.includes('SCREW') ||
                                      text.includes('ANCHOR') ||
                                      text.includes('CANNULA') ||
                                      text.includes('WASHER') ||
                                      text.includes('FILTERLINE') ||
                                      text.includes('VAC') ||
                                      text.includes('치료재료') ||
                                      text.includes('수술팩')

                                    // 전부 대문자/숫자/기호에 가까운 라인은 제품 코드일 가능성이 높아 제외
                                    const looksLikeProduct = /^[A-Z0-9 _\\-\\.]+$/.test(text)

                                    return !(ex || looksLikeProduct)
                                  }
                                  const filterTreatDetailsByContext = (list) => {
                                    const arr = Array.isArray(list) ? list : []
                                    if (ctx === 'EXAM') return arr.filter(isExamLine)
                                    if (ctx === 'TREATMENTS') return arr.filter(isTreatmentLine)
                                    if (ctx === 'INHOSP_SURGERY') {
                                      const surgOnly = arr.filter(isLikelySurgeryLine)
                                      const base = surgOnly
                                      if (!inhospIncludeNonSurgery) return base

                                      // 토글 ON: 수술 라인은 유지하면서, 입원 처치/치료 라인(진찰/이학요법/처치/수술 계열)을 추가로 보여준다.
                                      const extra = arr.filter(isInhospSurgeryLine)
                                      const seen = new Set()
                                      const out = []
                                      const keyOf = (x) => {
                                        const t = (x?.treatType || x?.treatTypeDetail || '').toString().trim()
                                        const cn = (x?.codeName || '').toString().trim()
                                        return `${t}|${cn}`
                                      }
                                      for (const x of base) {
                                        const k = keyOf(x)
                                        if (k && seen.has(k)) continue
                                        if (k) seen.add(k)
                                        out.push(x)
                                      }
                                      for (const x of extra) {
                                        const k = keyOf(x)
                                        if (k && seen.has(k)) continue
                                        if (k) seen.add(k)
                                        out.push(x)
                                      }
                                      return out
                                    }
                                    if (ctx === 'HIDDEN_SURGERY') {
                                      const surgOnly = arr.filter(isLikelySurgeryLine)
                                      if (!hiddenIncludeAllProcedures) return surgOnly
                                      // 토글 ON: 처치 및 수술(카테고리) 전체를 보여준다(재료/요법 포함)
                                      const proc = arr.filter((x) => String(x?.treatType || x?.treatTypeDetail || '').includes('처치'))
                                      const seen = new Set()
                                      const out = []
                                      const keyOf = (x) => {
                                        const t = (x?.treatType || x?.treatTypeDetail || '').toString().trim()
                                        const cn = (x?.codeName || '').toString().trim()
                                        return `${t}|${cn}`
                                      }
                                      for (const x of surgOnly) {
                                        const k = keyOf(x)
                                        if (k && seen.has(k)) continue
                                        if (k) seen.add(k)
                                        out.push(x)
                                      }
                                      for (const x of proc) {
                                        const k = keyOf(x)
                                        if (k && seen.has(k)) continue
                                        if (k) seen.add(k)
                                        out.push(x)
                                      }
                                      return out
                                    }
                                    return arr
                                  }

                                  const stripExportNamesFromDrugName = (name) => {
                                    const s = (name || '').toString().trim()
                                    if (!s) return ''
                                    // [수출명: ...] 구간만 제거
                                    return s.replace(/\[수출명:[^\]]*\]/g, '').replace(/\s+/g, ' ').trim()
                                  }

                                  const drugs = lines.filter((x) => x?.source === 'PRESCRIBE_DRUG')
                                  const nonPharmacyBasics = basics.filter((x) => !isPharmacyLine(x))
                                  // IMPORTANT: 약국(조제료) DETAIL_TREAT를 hospitalCards에 섞으면 "약국이 2번" 보이는 문제가 생김.
                                  // 토글로 확장 표시할 때도 약국은 drug_group 카드 안에서만 확장 렌더링한다.
                                  const nonPharmacyDetailTreats = mergedDetailTreats.filter((x) => !isPharmacyLine(x))
                                  const pharmacyBasics = basics.filter((x) => isPharmacyLine(x))
                                  const pharmacyDetailTreats = mergedDetailTreats.filter((x) => isPharmacyLine(x))

                                  const hospitalGroups = (() => {
                                    const map = new Map()
                                    // 기본 진료(병원 라인)부터 그룹 생성
                                    for (const b of nonPharmacyBasics) {
                                      const k = hospitalKey(b)
                                      if (!k) continue
                                      if (!map.has(k)) map.set(k, { base: b, details: [] })
                                      else if (!map.get(k).base) map.get(k).base = b
                                    }
                                    // 상세 처치(DETAIL_TREAT) 붙이기
                                    for (const dt of nonPharmacyDetailTreats) {
                                      const k = hospitalKey(dt)
                                      if (!k) continue
                                      if (!map.has(k)) map.set(k, { base: null, details: [] })
                                      map.get(k).details.push(dt)
                                    }
                                    return Array.from(map.values())
                                  })()

                                  // 진료일수는 병원 기본 라인의 totalTreatmentDays가 있으면 우선 사용
                                  const formatTreatType = (x) => (x?.treatType || x?.treatTypeDetail || '').toString().trim()
                                  const formatCodeName = (x) => (x?.codeName || '').toString().trim()
                                  const detailKey = (x) => `${formatTreatType(x)}|${formatCodeName(x)}`

                                  const hospitalCards = hospitalGroups
                                    .map((g) => {
                                      const base = g.base
                                      const org = base?.hospitalName || g.details?.[0]?.hospitalName || base?.hospitalCode || g.details?.[0]?.hospitalCode || '-'
                                      const treatText = (() => {
                                        const t = base?.treatTypeDetail || base?.treatType || ''
                                        const dept = base?.department || ''
                                        const s = [t, dept].filter(Boolean).join(' / ')
                                        return s ? normalizeSlash(s) : ''
                                      })()
                                      const daysText = base?.totalTreatmentDays ? `${base.totalTreatmentDays}일` : ''

                                      const list = filterTreatDetailsByContext(Array.isArray(g.details) ? g.details : [])
                                      const unique = []
                                      const seen = new Set()
                                      for (const x of list) {
                                        const k = detailKey(x)
                                        if (!k || seen.has(k)) continue
                                        seen.add(k)
                                        unique.push(x)
                                      }

                                      return { org, treatText, daysText, details: unique }
                                    })
                                    .filter((c) => c.org && c.org !== '-')

                                  const buildPharmacyVisitCards = () => {
                                    // 약국은 진료내역 모드에서 "방문 요약"만
                                    const map = new Map()
                                    for (const x of pharmacyBasics) {
                                      const k = hospitalKey(x)
                                      if (!k) continue
                                      if (!map.has(k)) map.set(k, x)
                                    }
                                    return Array.from(map.values()).map((x) => ({ kind: 'pharmacy_visit', x }))
                                  }

                                  const buildDrugGroupCards = () => {
                                    // 투약내역 모드: 병원/약국 모두 "투약 내역"만 묶어서 목록화 (조제료등 제외)
                                    const map = new Map()
                                    const add = (org, item) => {
                                      const k = org
                                      if (!k) return
                                      if (!map.has(k)) map.set(k, [])
                                      map.get(k).push(item)
                                    }
                                    for (const x of drugs) {
                                      const org = hospitalKey(x)
                                      add(org, x)
                                    }
                                    // 약국 DETAIL_TREAT(조제료등)은 기본 숨김이지만, 토글 ON이면 drug_group 카드 내부에 "추가 정보"로 붙여준다.
                                    const extraByOrg = new Map()
                                    if (drugsShowAllInfo) {
                                      for (const x of pharmacyDetailTreats) {
                                        const org = hospitalKey(x)
                                        if (!org) continue
                                        if (!extraByOrg.has(org)) extraByOrg.set(org, [])
                                        extraByOrg.get(org).push(x)
                                      }
                                    }
                                    const cards = []
                                    for (const [org, list] of map.entries()) {
                                      // 약품명 기준 dedupe
                                      const seen = new Set()
                                      const uniq = []
                                      for (const x of list) {
                                        const rawName = (x?.drugName || '').toString().trim()
                                        const name = drugsShowAllInfo ? rawName : stripExportNamesFromDrugName(rawName)
                                        const k = name
                                        if (!name || seen.has(k)) continue
                                        seen.add(k)
                                          uniq.push({ ...x, drugName: name })
                                      }
                                      const extras = drugsShowAllInfo ? (extraByOrg.get(org) || []) : []
                                      cards.push({ kind: 'drug_group', org, list: uniq, extras })
                                    }
                                    return cards
                                  }

                                  const ordered =
                                    detailEpisodeMode === 'drugs'
                                      ? [
                                          ...hospitalCards.map((h) => ({ kind: 'hospital', h })),
                                          ...buildDrugGroupCards(),
                                        ]
                                      : [
                                          ...hospitalCards.map((h) => ({ kind: 'hospital', h })),
                                          ...buildPharmacyVisitCards(),
                                        ]

                                  return (
                                    <div key={d} className="rounded border bg-slate-50 px-3 py-2">
                                      <button
                                        type="button"
                                        className="w-full flex items-center justify-between text-left"
                                        onClick={() => {
                                          setCollapsedDetailDays((prev) => {
                                            const next = new Set(prev)
                                            if (next.has(d)) next.delete(d)
                                            else next.add(d)
                                            return next
                                          })
                                        }}
                                      >
                                        <div className="text-xs font-semibold text-slate-900">{titleDate}</div>
                                        <div className="text-[11px] text-slate-500">
                                          {isCollapsed ? '펼치기' : '접기'} ({ordered.length})
                                        </div>
                                      </button>
                                      {!isCollapsed && (
                                        <div className="mt-2 space-y-2">
                                          {ordered.map((h, idx) => (
                                            <div key={idx} className="rounded border bg-white px-3 py-2">
                                              {h.kind === 'hospital' && (
                                                <>
                                                  <div className="text-xs text-slate-700">
                                                    <span className="font-medium">{h.h.org}</span>
                                                    {h.h.treatText ? <span className="text-slate-500"> • {h.h.treatText}</span> : null}
                                        </div>
                                                  {h.h.daysText ? (
                                                    <div className="mt-1 text-[11px] text-slate-500">진료일수: {h.h.daysText}</div>
                                                  ) : null}
                                                  {h.h.details && h.h.details.length > 0 ? (
                                                    <div className="mt-2 text-[11px] text-slate-700">
                                                      <div className="font-semibold text-slate-600">진료형태 | 코드명 목록 :</div>
                                                      <ul className="mt-1 list-disc pl-4 space-y-0.5 text-slate-700">
                                                        {h.h.details.map((x, i2) => (
                                                          <li key={i2}>
                                                            {formatTreatType(x) || '-'}
                                                            {'  |  '}
                                                            {formatCodeName(x) || '-'}
                                                          </li>
                                                        ))}
                                                      </ul>
                                                    </div>
                                                  ) : ctx === 'INHOSP_SURGERY' ? (
                                                    <div className="mt-2 text-[11px] text-slate-500">
                                                      표시할 수술 내역이 없습니다.
                                                      {!inhospIncludeNonSurgery ? (
                                                        <span className="text-slate-500"> (필요하면 위 옵션에서 ‘수술 외 처치/치료/재료까지 모두 보기’를 켜세요)</span>
                                                      ) : null}
                                                    </div>
                                                  ) : ctx === 'HIDDEN_SURGERY' ? (
                                                    <div className="mt-2 text-[11px] text-slate-500">
                                                      표시할 수술 내역이 없습니다.
                                                      {!hiddenIncludeAllProcedures ? (
                                                        <span className="text-slate-500"> (필요하면 위 옵션에서 ‘처치 및 수술 전체 보기’를 켜세요)</span>
                                                      ) : null}
                                                    </div>
                                                  ) : null}
                                                </>
                                              )}
                                              {h.kind === 'drug_group' && (
                                                <>
                                                  <div className="text-xs text-slate-700">
                                                    <span className="font-medium">{h.org}</span>
                                                  </div>
                                                  {(() => {
                                                    const list = Array.isArray(h.list) ? h.list : []
                                                    let maxDays = null
                                                    for (const x of list) {
                                                      const v = x?.totalDrugDays
                                                      const n =
                                                        typeof v === 'number'
                                                          ? v
                                                          : v !== null && v !== undefined && String(v).trim() !== ''
                                                          ? Number(v)
                                                          : null
                                                      if (!Number.isFinite(n)) continue
                                                      maxDays = maxDays === null ? Math.max(0, n) : Math.max(maxDays, Math.max(0, n))
                                                    }
                                                    return maxDays !== null ? (
                                                      <div className="mt-1 text-[11px] text-slate-500">투약일 : {maxDays}일</div>
                                                    ) : null
                                                  })()}
                                                  {Array.isArray(h.list) && h.list.length > 0 ? (
                                                    <div className="mt-2 text-[11px] text-slate-700">
                                                      <div className="font-semibold text-slate-600">투약내역 목록 :</div>
                                                      <ul className="mt-1 list-disc pl-4 space-y-0.5 text-slate-700">
                                                        {h.list.map((x, i2) => (
                                                          <li key={i2}>{x?.drugName || '-'}</li>
                                                        ))}
                                                      </ul>
                                                    </div>
                                                  ) : (
                                                    <div className="mt-1 text-[11px] text-slate-600">-</div>
                                                  )}
                                                  {Array.isArray(h.extras) && h.extras.length > 0 ? (
                                                    <div className="mt-2 text-[11px] text-slate-700">
                                                      <div className="font-semibold text-slate-600">조제료/기타(숨김 포함) :</div>
                                                      <ul className="mt-1 list-disc pl-4 space-y-0.5 text-slate-700">
                                                        {(() => {
                                                          const seen = new Set()
                                                          const uniq = []
                                                          for (const x of h.extras) {
                                                            const k = detailKey(x)
                                                            if (!k || seen.has(k)) continue
                                                            seen.add(k)
                                                            uniq.push(x)
                                                          }
                                                          return uniq.map((x, i2) => (
                                                            <li key={i2}>
                                                              {formatTreatType(x) || '-'}
                                                              {'  |  '}
                                                              {formatCodeName(x) || '-'}
                                                            </li>
                                                          ))
                                                        })()}
                                                      </ul>
                                                    </div>
                                                  ) : null}
                                                </>
                                              )}
                                              {h.kind === 'pharmacy_visit' && (
                                                <>
                                                  <div className="text-xs text-slate-700">
                                                    <span className="font-medium">{h.x?.hospitalName || h.x?.hospitalCode || '-'}</span>
                                                    {(h.x?.treatTypeDetail || h.x?.treatType) ? (
                                                      <span className="text-slate-500"> • {normalizeSlash(h.x?.treatTypeDetail || h.x?.treatType)}</span>
                                                    ) : null}
                                                  </div>
                                                  {h.x?.totalTreatmentDays ? (
                                                    <div className="mt-1 text-[11px] text-slate-500">진료일수: {h.x.totalTreatmentDays}일</div>
                                                  ) : null}
                                                </>
                                              )}
                                            </div>
                                          ))}
                                        </div>
                                      )}
                                    </div>
                                  )
                                })}
                              </div>
                            )
                          })()}
                        </div>
                      </div>
                    )}
                  </div>
                )
              })()}
            </div>
          </div>
        </div>
      )}

      {/* 3NN 템플릿 추천 결과 팝업 */}
      {threeNnModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-2xl rounded-lg bg-white shadow-lg p-4 text-sm max-h-[80vh] overflow-auto">
            <div className="flex items-center justify-between mb-2">
              <div className="font-semibold text-slate-900">
                {threeNnModalData?.kind === 'SIMPLE_3N5' ? '유병자 3N5 가능 여부(1차)' : '보험사별 예외 적용(예외질환/3NN)'}
              </div>
              <div className="flex items-center gap-2">
                {threeNnModalData?.kind !== 'SIMPLE_3N5' && (
                  <button
                    type="button"
                    className="rounded border px-2 py-1 text-xs border-brand-navy text-brand-navy hover:bg-brand-navy hover:text-white"
                    onClick={() => {
                      const codes = Array.from(selectedInsurerCodesForRequest || [])
                      handleGoToRequestNewFrom3nn({
                        coverageType: 'substandard',
                        selectedInsurerCodes: codes.length > 0 ? codes : null,
                      })
                    }}
                  >
                    선택된 보험사로 설계의뢰
                  </button>
                )}
              <button
                type="button"
                className="text-xs text-slate-500 hover:text-slate-800"
                onClick={() => {
                  setThreeNnModalOpen(false)
                  setThreeNnModalMode('SIMPLE_3N5')
                  setThreeNnTemplateExpandedTemplates(new Set())
                  setThreeNnTemplateKcdExpanded(new Set())
                  setThreeNnTemplateCellDetail(null)
                }}
              >
                닫기
              </button>
              </div>
            </div>

            <div className="text-xs text-slate-500">
              기준일:{' '}
              <span className="font-mono">{threeNnModalData?.asOf || '-'}</span>
            </div>

            <div className="mt-3 space-y-3">
              {threeNnModalData?.kind === 'SIMPLE_3N5' && (
                <div className="rounded border bg-slate-50 p-3 text-sm text-slate-800">
                  <div className="font-semibold text-slate-900">안내</div>
                  <div className="mt-1 text-xs text-slate-700 whitespace-pre-wrap">{threeNnModalData?.message || '-'}</div>

                  {Array.isArray(threeNnModalData?.reasons) && threeNnModalData.reasons.length > 0 && (
                    <div className="mt-3">
                      <div className="text-xs font-semibold text-slate-900">조합을 이렇게 안내한 이유</div>
                      <div className="mt-1 space-y-0.5 text-[11px] text-slate-700">
                        {threeNnModalData.reasons.map((x, idx) => (
                          <div key={idx} className="text-slate-600">- {x}</div>
                        ))}
                      </div>
                    </div>
                  )}

                  {Array.isArray(threeNnModalData?.possibleCombos) && (
                    <div className="mt-3">
                      <div className="text-xs font-semibold text-slate-900">가능한 조합</div>
                      {threeNnModalData.possibleCombos.length === 0 ? (
                        <div className="mt-1 text-xs text-slate-600">없음</div>
                      ) : (
                        <div className="mt-1 flex flex-wrap gap-2">
                          {threeNnModalData.possibleCombos.map((x) => (
                            <span key={x} className="rounded-full border bg-white px-2 py-0.5 text-xs font-mono text-slate-700">
                              {x}
                            </span>
                          ))}
                        </div>
                      )}
                      {threeNnModalData.possibleCombos.includes('3-7-5') && (
                        <div className="mt-2 text-[11px] text-slate-600 whitespace-pre-wrap">
                          * 최근 5년 내 입원/수술 내역에서 필터링되는 항목이 없다면, 보험사별로 3-7-5 / 3-10-5 / 3-10-10 등도 가입이 가능할 수 있으니 확인해주세요.
                        </div>
                      )}
                    </div>
                  )}

                  {threeNnModalData?.footnote && (
                    <div className="mt-3 text-[11px] text-slate-500 whitespace-pre-wrap">{threeNnModalData.footnote}</div>
                  )}
                </div>
              )}
              {threeNnModalMode === 'TEMPLATE_3NN' && (
                <>
                  {/* 필터 영역: 선택된 보험사 목록만 표시 */}
                  <div className="mt-2 rounded border bg-slate-50 p-2">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <div className="text-xs text-slate-700">
                        선택된 보험사: <span className="font-mono font-semibold">{selectedInsurerCodesForRequest.size}</span>
                      </div>
                      <button
                        type="button"
                        className="rounded border bg-white px-2 py-1 text-xs hover:bg-slate-50 disabled:opacity-40"
                        onClick={() => setSelectedInsurerCodesForRequest(new Set())}
                        disabled={selectedInsurerCodesForRequest.size === 0}
                      >
                        선택 해제
                      </button>
                    </div>
                    {selectedInsurerCodesForRequest.size > 0 && (
                      <div className="mt-2 flex flex-wrap gap-2">
                        {Array.from(selectedInsurerCodesForRequest)
                          .slice(0, 12)
                          .map((code) => (
                            <span
                              key={code}
                              className="inline-flex items-center gap-2 rounded-full border bg-white px-2 py-0.5 text-[11px] text-slate-700"
                            >
                              <span className="font-semibold">{insurerNameKrOf(code)}</span>
                              <button
                                type="button"
                                className="text-slate-400 hover:text-slate-700"
                                onClick={() => {
                                  const next = new Set(selectedInsurerCodesForRequest)
                                  next.delete(code)
                                  setSelectedInsurerCodesForRequest(next)
                                }}
                              >
                                ×
                              </button>
                            </span>
                          ))}
                        {selectedInsurerCodesForRequest.size > 12 && (
                          <span className="text-[11px] text-slate-500">
                            … 외 {selectedInsurerCodesForRequest.size - 12}개
                          </span>
                        )}
                      </div>
                    )}
                  </div>

                  {/* 매트릭스 뷰(기본: 전체 보험사, 선택 체크박스는 설계의뢰용) */}
                  <div className="mt-3 rounded border bg-white">
                    {(() => {
                      const rawItems = Array.isArray(threeNnTemplateResult?.items) ? threeNnTemplateResult.items : []
                      const asOfForDetail = threeNnTemplateResult?.asOf || null

                      const statusRankLocal = (v) => {
                        const s = toStatusCode(v)
                        if (s === 'OK') return 4
                        if (s === 'WARN') return 3
                        if (s === 'REVIEW') return 2
                        if (s === 'FAIL') return 1
                        return 0
                      }

                      const pickBestLocal = (results) => {
                        const list = Array.isArray(results) ? results.slice() : []
                        list.sort((a, b) => {
                          const sa = statusRankLocal(a?.status)
                          const sb = statusRankLocal(b?.status)
                          if (sa !== sb) return sb - sa
                          const scA = Number.isFinite(Number(a?.score)) ? Number(a.score) : -1
                          const scB = Number.isFinite(Number(b?.score)) ? Number(b.score) : -1
                          if (scA !== scB) return scB - scA
                          const rkA = Number.isFinite(Number(a?.rankScore)) ? Number(a.rankScore) : -1
                          const rkB = Number.isFinite(Number(b?.rankScore)) ? Number(b.rankScore) : -1
                          if (rkA !== rkB) return rkB - rkA
                          return String(a?.templateCode || '').localeCompare(String(b?.templateCode || ''))
                        })
                        return list.length > 0 ? list[0] : null
                      }

                      // 중복 보험사(동일 insurerCode)가 내려오는 경우를 방어적으로 병합한다.
                      const mergedByCode = new Map()
                      for (const it of rawItems) {
                        const rawCode = String(it?.insurerCode || '').trim()
                        const code = canonicalInsurerCodeForUi(rawCode)
                        if (!code) continue
                        const prev = mergedByCode.get(code)
                        if (!prev) {
                          mergedByCode.set(code, {
                            ...it,
                            insurerCode: code,
                            __uiOriginalCodes: [rawCode].filter(Boolean),
                          })
                          continue
                        }
                        const prevResults = Array.isArray(prev?.results) ? prev.results : []
                        const nextResults = Array.isArray(it?.results) ? it.results : []
                        const prevResultsBefore = Array.isArray(prev?.resultsBeforeException) ? prev.resultsBeforeException : []
                        const nextResultsBefore = Array.isArray(it?.resultsBeforeException) ? it.resultsBeforeException : []
                        const mergedResultsMap = new Map()
                        for (const r of [...prevResults, ...nextResults]) {
                          const tc = String(r?.templateCode || '').trim()
                          if (!tc) continue
                          // 같은 템플릿코드가 중복이면 더 좋은 결과로 유지
                          const cur = mergedResultsMap.get(tc)
                          if (!cur) mergedResultsMap.set(tc, r)
                          else {
                            const best = pickBestLocal([cur, r])
                            mergedResultsMap.set(tc, best || r)
                          }
                        }
                        const mergedResults = Array.from(mergedResultsMap.values())

                        const mergedResultsBeforeMap = new Map()
                        for (const r of [...prevResultsBefore, ...nextResultsBefore]) {
                          const tc = String(r?.templateCode || '').trim()
                          if (!tc) continue
                          const cur = mergedResultsBeforeMap.get(tc)
                          if (!cur) mergedResultsBeforeMap.set(tc, r)
                          else {
                            const best = pickBestLocal([cur, r])
                            mergedResultsBeforeMap.set(tc, best || r)
                          }
                        }
                        const mergedResultsBefore = Array.from(mergedResultsBeforeMap.values())
                        const merged = {
                          ...prev,
                          ...it,
                          insurerCode: code,
                          insurerName: prev?.insurerName || it?.insurerName || null,
                          results: mergedResults,
                          best: pickBestLocal(mergedResults) || prev?.best || it?.best || null,
                          resultsBeforeException: mergedResultsBefore,
                          bestBeforeException:
                            pickBestLocal(mergedResultsBefore) || prev?.bestBeforeException || it?.bestBeforeException || null,
                          __uiOriginalCodes: Array.from(
                            new Set([...(Array.isArray(prev?.__uiOriginalCodes) ? prev.__uiOriginalCodes : []), rawCode].filter(Boolean)),
                          ),
                        }
                        mergedByCode.set(code, merged)
                      }

                      const items = Array.from(mergedByCode.values())
                      const filtered = items
                        .slice()
                        .sort((a, b) => {
                          const ca = insurerCategoryOf(a?.insurerCode)
                          const cb = insurerCategoryOf(b?.insurerCode)
                          if (ca !== cb) return ca === 'NONLIFE' ? -1 : 1
                          const na = insurerNameKrOf(a?.insurerCode, a?.insurerName)
                          const nb = insurerNameKrOf(b?.insurerCode, b?.insurerName)
                          return String(na).localeCompare(String(nb))
                        })
                      const byCode = mergedByCode

                      // 컬럼 리스트는 중복 제거(보험사 코드 기준)
                      const colsAll = (() => {
                        const out = []
                        const seen = new Set()
                        for (const it of filtered) {
                          const c = String(it?.insurerCode || '').trim()
                          if (!c) continue
                          if (!byCode.has(c)) continue
                          if (seen.has(c)) continue
                          seen.add(c)
                          out.push(c)
                        }
                        return out
                      })()

                      if (colsAll.length === 0) {
                        return <div className="p-3 text-xs text-slate-500">표시할 보험사가 없습니다.</div>
                      }

                      const nonlifeCols = colsAll.filter((c) => insurerCategoryOf(c) === 'NONLIFE')
                      const lifeCols = colsAll.filter((c) => insurerCategoryOf(c) === 'LIFE')

                      // 같은 상품코드(335/355/375 등)는 항상 동일 색상으로 표시
                      const templateClass = (templateCode) => {
                        const tc = String(templateCode || '').trim()
                        const fixed = {
                          '335': 'bg-emerald-50 text-emerald-900 border-emerald-200',
                          '345': 'bg-blue-50 text-blue-900 border-blue-200',
                          '355': 'bg-sky-50 text-sky-900 border-sky-200',
                          '375': 'bg-violet-50 text-violet-900 border-violet-200',
                        }
                        if (fixed[tc]) return fixed[tc]
                        const palette = [
                          'bg-amber-50 text-amber-900 border-amber-200',
                          'bg-rose-50 text-rose-900 border-rose-200',
                          'bg-teal-50 text-teal-900 border-teal-200',
                          'bg-lime-50 text-lime-900 border-lime-200',
                        ]
                        const digits = tc.replaceAll(/[^0-9]/g, '')
                        const n = digits ? Number(digits) : 0
                        const idx = Number.isFinite(n) && n > 0 ? n % palette.length : 0
                        return palette[idx] || 'bg-slate-50 text-slate-800 border-slate-200'
                      }

                      const catAllSelected = (codes) => codes.length > 0 && codes.every((c) => selectedInsurerCodesForRequest.has(String(c)))
                      const toggleCatAll = (codes) => {
                        const next = new Set(selectedInsurerCodesForRequest)
                        const allOn = catAllSelected(codes)
                        for (const c of codes) {
                          if (allOn) next.delete(String(c))
                          else next.add(String(c))
                        }
                        setSelectedInsurerCodesForRequest(next)
                      }

                      const renderCategoryTable = (label, cols) => {
                        if (!cols || cols.length === 0) return null
                        const COL_MIN_W = 120
                        return (
                          <div className="mt-3 first:mt-0">
                            <div className="flex items-center justify-between px-3 py-2 bg-slate-50 border-b">
                              <label className="inline-flex items-center gap-2 text-xs">
                                <input
                                  type="checkbox"
                                  checked={catAllSelected(cols)}
                                  onChange={() => toggleCatAll(cols)}
                                />
                                <span className="font-semibold text-slate-900">{label}</span>
                                <span className="text-[11px] text-slate-600">(전체선택)</span>
                              </label>
                              <div className="text-[11px] text-slate-500">보험사 {cols.length}개</div>
                            </div>
                            <div className="overflow-auto">
                              <table className="w-max min-w-full text-xs">
                                <thead>
                                  <tr className="bg-slate-50">
                                    <th className="p-2 text-left border-b w-[90px]" rowSpan={2}>보험사</th>
                                    {cols.map((code) => {
                                      const it = byCode.get(code)
                                      const nameKr = insurerNameKrOf(code, it?.insurerName)
                                      return (
                                        <th key={code} className="p-2 text-center border-b min-w-[120px]" style={{ minWidth: COL_MIN_W }}>
                                          <label className="inline-flex items-center gap-2 justify-center text-[11px] text-slate-700">
                                            <input
                                              type="checkbox"
                                              checked={selectedInsurerCodesForRequest.has(String(code))}
                                              onChange={(e) => {
                                                const next = new Set(selectedInsurerCodesForRequest)
                                                if (Boolean(e.target.checked)) next.add(String(code))
                                                else next.delete(String(code))
                                                setSelectedInsurerCodesForRequest(next)
                                              }}
                                            />
                                            <span className="sr-only">선택</span>
                                          </label>
                                          <div className="mt-1 font-semibold text-slate-900">{nameKr}</div>
                                        </th>
                                      )
                                    })}
                                  </tr>
                                </thead>
                                <tbody>
                                  <tr className="border-b last:border-b-0">
                                    <td className="p-2 font-semibold text-slate-800">상품</td>
                                    {cols.map((code) => {
                                      const it = byCode.get(code)
                                      const best = it?.best || null
                                      const bestCode = String(best?.templateCode || '')
                                      const r =
                                        bestCode
                                          ? (Array.isArray(it?.results) ? it.results : []).find((x) => String(x?.templateCode || '') === bestCode) || best
                                          : best
                                      return (
                                        <td key={`${code}__BEST`} className="p-2 text-center min-w-[120px]" style={{ minWidth: COL_MIN_W }}>
                                          <div className={`w-full rounded border px-3 py-3 ${templateClass(bestCode)}`}>
                                            <div className="font-mono font-semibold text-lg leading-none">{bestCode || '-'}</div>
                                            <div className="mt-2">
                                              <button
                                                type="button"
                                                className="rounded border bg-white/70 px-2 py-1 text-[11px] hover:bg-white whitespace-nowrap"
                                                onClick={() => {
                                                  setThreeNnTemplateCellDetail({
                                                    insurerCode: code,
                                                    insurerName: insurerNameKrOf(code),
                                                    templateCode: bestCode || '-',
                                                    r,
                                                    bestBeforeException: it?.bestBeforeException || null,
                                                    asOf: asOfForDetail,
                                                  })
                                                }}
                                              >
                                                [상세]
                                              </button>
                                            </div>
                                          </div>
                                        </td>
                                      )
                                    })}
                                  </tr>
                                </tbody>
                              </table>
                            </div>
                          </div>
                        )
                      }

                      return (
                        <div>
                          {renderCategoryTable('손해보험', nonlifeCols)}
                          {renderCategoryTable('생명보험', lifeCols)}
                        </div>
                      )
                    })()}
                  </div>

                  {/* 셀 상세 팝업 */}
                  {threeNnTemplateCellDetail && (
                    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 p-4">
                      <div className="w-full max-w-xl rounded-lg bg-white shadow-lg max-h-[80vh] overflow-auto">
                        <div className="sticky top-0 z-10 bg-white border-b px-4 py-3">
                          <div className="flex items-center justify-between">
                            <div className="font-semibold text-slate-900">
                              {threeNnTemplateCellDetail.insurerName}{' '}
                              <span className="font-mono">{threeNnTemplateCellDetail.templateCode}</span>
                            </div>
                            <button
                              type="button"
                              className="text-xs text-slate-500 hover:text-slate-800"
                              onClick={() => {
                                setThreeNnTemplateCellDetail(null)
                                setThreeNnTemplateKcdExpanded(new Set())
                              }}
                            >
                              닫기
                            </button>
                          </div>
                        </div>

                        {(() => {
                          const r = threeNnTemplateCellDetail.r || {}
                          const st = toStatusCode(r?.status)
                          const matched = Array.isArray(r?.matchedRules) ? r.matchedRules : []
                          const evidences = Array.isArray(r?.evidences) ? r.evidences : []
                          const ruleRows = matched
                            .map((x) => ({
                              decisionType: toStatusCode(x?.decisionType),
                              rawDecisionType: toStatusCode(x?.rawDecisionType),
                              applied: x?.applied,
                              conditionKey: (x?.conditionKey || '').toString().trim(),
                              message: (x?.message || '').toString().trim(),
                              ruleId: x?.ruleId ?? null,
                              waitingDays: x?.waitingDays ?? null,
                              maxCount: x?.maxCount ?? null,
                              inpatientMaxDays: x?.inpatientMaxDays ?? null,
                              inpatientDaysMax: x?.inpatientDaysMax ?? null,
                              note: (x?.note || '').toString().trim(),
                              matchedCount: x?.matchedCount ?? null,
                              daysSinceEnd: x?.daysSinceEnd ?? null,
                            }))
                            .filter((x) => x.conditionKey && x.decisionType)

                          const nonOk = ruleRows.filter((x) => x.decisionType && x.decisionType !== 'OK')

                          const normalizeKcd = (value) => {
                            const s = (value || '').toString().trim().toUpperCase().replaceAll(/[^A-Z0-9.]/g, '')
                            if (!s) return ''
                            const noDot = s.replaceAll('.', '')
                            const m = noDot.match(/^([A-Z]\d{2,5})/)
                            return m ? m[1] : ''
                          }

                          const conditionKeyToUiCode = (conditionKey) => {
                            const raw = (conditionKey || '').toString().trim().toUpperCase()
                            // 예: KCD_K291_3 -> K291.3
                            const m = raw.match(/^KCD_([A-Z]\d{2,5})(?:_(\d+))?$/)
                            if (m && m[1]) {
                              const base = m[1]
                              const suf = m[2]
                              return suf ? `${base}.${suf}` : base
                            }
                            return raw || '-'
                          }

                          const extractKcdFromRule = (rule) => {
                            const pool = `${rule?.conditionKey || ''} ${rule?.message || ''}`.toString().toUpperCase()
                            const out = new Set()
                            // 1) KCD_XXX 형태 우선
                            ;(pool.matchAll(/KCD_([A-Z]\d{2,5})/g) || []).forEach((m) => {
                              if (m?.[1]) out.add(m[1])
                            })
                            // 2) 그래도 없으면 일반 KCD코드 패턴(문장에 노출된 경우)
                            if (out.size === 0) {
                              ;(pool.matchAll(/\b([A-Z]\d{2,5})\b/g) || []).forEach((m) => {
                                if (m?.[1]) out.add(m[1])
                              })
                            }
                            return Array.from(out)
                          }

                          const episodeSummary = (ep) => {
                            const start = (ep?.startDate || '').toString()
                            const end = (ep?.endDate || '').toString()
                            const dx = (ep?.diagnosisCode || '').toString()
                            const name = (ep?.diagnosisName || '').toString()
                            const hosp = (ep?.hospitalName || ep?.hospitalCode || '').toString()
                            const inpatient = ep?.inpatient ? '입원' : '외래'
                            const treatDays = ep?.totalTreatmentDays != null ? String(ep.totalTreatmentDays) : ''
                            const drugDays = ep?.totalDrugDays != null ? String(ep.totalDrugDays) : ''
                            const range = start || end ? `${start || '-'}~${end || '-'}` : '-'
                            const daysPart = (treatDays || drugDays) ? ` / 진료${treatDays || '-'}일 / 투약${drugDays || '-'}일` : ''
                            return `${dx}${name ? `(${name})` : ''} - ${range} / ${inpatient}${daysPart}${hosp ? ` / ${hosp}` : ''}`
                          }

                          const whyText = (() => {
                            const tc = threeNnTemplateCellDetail.templateCode
                            const b = threeNnTemplateCellDetail?.bestBeforeException || null
                            const btc = (b?.templateCode || '').toString().trim()
                            const bst = toStatusCode(b?.status)
                            const afterSt = toStatusCode(r?.status)
                            const statusToSentence = (stCode) => {
                              if (stCode === 'OK') return '가입이 가능합니다.'
                              if (stCode === 'WARN') return '가입은 가능할 수 있으나 조건/확인이 필요합니다.'
                              if (stCode === 'REVIEW') return '심사(검토)가 필요합니다.'
                              if (stCode === 'FAIL') return '가입이 어렵습니다.'
                              return '판단이 필요합니다.'
                            }
                            const lines = []
                            if (btc) {
                              lines.push(`예외질환 미고려 시 ${btc} 상품 ${statusToSentence(bst)}`)
                            }
                            lines.push(`예외질환 적용 후 ${tc} 상품 ${statusToSentence(afterSt)}`)
                            // lines.push(`아래는 해당 결과에 영향을 준 진료내역/룰 근거입니다.`)
                            return lines.join('\n')
                          })()

                          const episodeEvidences = evidences.filter((x) => (x?.type || '') === 'EPISODE')
                          const asOfDt = toDateObject(threeNnTemplateCellDetail?.asOf || null)

                          // 코드별(예: K291)로 진료내역을 요약/중복 묶기
                          const episodesByKcd = (() => {
                            const m = new Map()
                            for (const ep of episodeEvidences) {
                              const rawCode = (ep?.diagnosisCode || '').toString()
                              const kcd = normalizeKcd(rawCode)
                              if (!kcd) continue
                              const name = (ep?.diagnosisName || '').toString().trim()
                              const start = (ep?.startDate || '').toString()
                              const end = (ep?.endDate || '').toString()
                              const inpatient = Boolean(ep?.inpatient)
                              const hosp = (ep?.hospitalName || ep?.hospitalCode || '').toString().trim()
                              const treatDays = ep?.totalTreatmentDays != null ? String(ep.totalTreatmentDays) : ''
                              const drugDays = ep?.totalDrugDays != null ? String(ep.totalDrugDays) : ''
                              const key = `${start}|${end}|${inpatient ? 'I' : 'O'}|${hosp || '-'}|${treatDays || '-'}|${drugDays || '-'}`

                              if (!m.has(kcd)) {
                                m.set(kcd, {
                                  kcd,
                                  diagnosisName: name || '',
                                  groups: new Map(),
                                  counts: { total: 0, inpatient: 0, outpatient: 0 },
                                  minStart: null,
                                  maxEnd: null,
                                  hospitals: new Map(), // hosp -> count
                                })
                              }
                              const cur = m.get(kcd)
                              cur.counts.total += 1
                              if (inpatient) cur.counts.inpatient += 1
                              else cur.counts.outpatient += 1
                              if (!cur.diagnosisName && name) cur.diagnosisName = name

                              const sdt = toDateObject(start)
                              const edt = toDateObject(end)
                              if (sdt && (!cur.minStart || sdt < cur.minStart)) cur.minStart = sdt
                              if (edt && (!cur.maxEnd || edt > cur.maxEnd)) cur.maxEnd = edt

                              const prevHosp = cur.hospitals.get(hosp || '-') || 0
                              cur.hospitals.set(hosp || '-', prevHosp + 1)

                              if (!cur.groups.has(key)) {
                                cur.groups.set(key, { count: 1, ep })
                              } else {
                                cur.groups.get(key).count += 1
                              }
                            }
                            return m
                          })()

                          // 룰을 KCD 코드별로 묶기 (예: conditionKey=KCD_K291_3)
                          const rulesByKcd = (() => {
                            const m = new Map()
                            for (const rule of ruleRows) {
                              const kcds = extractKcdFromRule(rule)
                              if (kcds.length === 0) {
                                const key = '__OTHER__'
                                if (!m.has(key)) m.set(key, [])
                                m.get(key).push(rule)
                                continue
                              }
                              for (const raw of kcds) {
                                const kcd = normalizeKcd(raw) || raw
                                if (!m.has(kcd)) m.set(kcd, [])
                                m.get(kcd).push(rule)
                              }
                            }
                            return m
                          })()

                          const exceptionKcdKeys = (() => {
                            const set = new Set()
                            for (const k of rulesByKcd.keys()) if (k !== '__OTHER__') set.add(k)
                            for (const k of episodesByKcd.keys()) set.add(k)
                            return Array.from(set).sort((a, b) => String(a).localeCompare(String(b)))
                          })()

                          // 전체 진료내역(리포트 원본)에서 "예외질환 미해당" 코드를 간략 표시(코드/진단명)
                          const allEpisodeKcdMap = (() => {
                            const m = new Map() // kcd -> { kcd, diagnosisName }
                            const rawList = Array.isArray(rawEpisodesFromSummary) ? rawEpisodesFromSummary : []
                            for (const ep of rawList) {
                              const kcd = normalizeKcd(ep?.diagnosisCode || '')
                              if (!kcd) continue
                              const name = (ep?.diagnosisName || '').toString().trim()
                              if (!m.has(kcd)) m.set(kcd, { kcd, diagnosisName: name || '' })
                              else {
                                const cur = m.get(kcd)
                                if (!cur.diagnosisName && name) cur.diagnosisName = name
                              }
                            }
                            return m
                          })()

                          const nonExceptionKcdKeys = (() => {
                            const exSet = new Set(exceptionKcdKeys)
                            const all = Array.from(allEpisodeKcdMap.keys())
                            return all
                              .filter((k) => !exSet.has(k))
                              .sort((a, b) => String(a).localeCompare(String(b)))
                          })()

                          return (
                            <div className="p-4 space-y-3 text-xs">
                              <div className="rounded border bg-slate-50 p-3">
                                <div className="font-semibold text-slate-900">추천 사유</div>
                                <div className="mt-1 text-slate-700 whitespace-pre-wrap">{whyText}</div>
                              </div>

                              <div className="rounded border bg-slate-50 p-3 text-[11px] text-slate-500">
                                * 예외질환의 세부 조건은 각 보험사별로 내부적인 규칙에 의해 변경될 수 있습니다. 따라서 각 진료내역에 따른 보장 및 상품 가입 가능성은 담당
                                매니저에게 문의해주세요.
                              </div>

                              <div className="rounded border bg-white p-3">
                                <details className="rounded border bg-white p-2">
                                  <summary className="cursor-pointer text-[11px] font-semibold text-slate-900">
                                    예외질환 해당 진료내역 ({exceptionKcdKeys.length})
                                  </summary>
                                  {exceptionKcdKeys.length === 0 ? (
                                    <div className="mt-2 text-[11px] text-slate-500">예외질환 해당 진료내역이 없습니다.</div>
                                  ) : (
                                    <div className="mt-2 space-y-2">
                                    {exceptionKcdKeys.map((kcd) => {
                                      const epPack = episodesByKcd.get(kcd) || null
                                      const rulePack = rulesByKcd.get(kcd) || []
                                      const name = epPack?.diagnosisName || ''
                                      const dateRange =
                                        epPack?.minStart || epPack?.maxEnd
                                          ? `${epPack?.minStart ? formatDate(epPack.minStart) : '-'}~${epPack?.maxEnd ? formatDate(epPack.maxEnd) : '-'}`
                                          : null
                                      const groups =
                                        epPack && epPack.groups.size > 0
                                          ? Array.from(epPack.groups.values()).sort((a, b) => b.count - a.count)
                                          : []

                                      const expandKey = `${threeNnTemplateCellDetail?.insurerCode || ''}__${threeNnTemplateCellDetail?.templateCode || ''}__${kcd}`
                                      const isExpanded = threeNnTemplateKcdExpanded.has(expandKey)

                                      const daysSinceEnd = (() => {
                                        if (!asOfDt || !epPack?.maxEnd) return null
                                        const ms = asOfDt.getTime() - epPack.maxEnd.getTime()
                                        const days = Math.floor(ms / (24 * 60 * 60 * 1000))
                                        return Number.isFinite(days) ? days : null
                                      })()

                                      return (
                                        <div key={kcd} className="rounded border bg-slate-50 p-2">
                                          <div className="font-mono font-semibold text-slate-900">
                                            {kcd}
                                            {name ? <span className="font-sans text-slate-700">({name})</span> : null}
                                          </div>

                                          {epPack ? (
                                            <div className="mt-1 text-[11px] text-slate-700 space-y-0.5">
                                              <div>
                                                - 건수: <span className="font-mono">{epPack.counts.total}</span>건 (외래{' '}
                                                <span className="font-mono">{epPack.counts.outpatient}</span>/입원{' '}
                                                <span className="font-mono">{epPack.counts.inpatient}</span>)
                                              </div>
                                              {dateRange ? <div>- 기간: <span className="font-mono">{dateRange}</span></div> : null}
                                            </div>
                                          ) : (
                                            <div className="mt-1 text-[11px] text-slate-500">- 진료내역 근거 없음</div>
                                          )}

                                          {rulePack.length > 0 ? (
                                            <div className="mt-2">
                                              <div className="text-[11px] font-semibold text-slate-900">관련 룰</div>
                                              <div className="mt-0.5 space-y-0.5 text-[11px] text-slate-700">
                                                {rulePack.slice(0, 8).map((x, idx) => (
                                                  <div key={idx}>
                                                    -{' '}
                                                    <span className="font-mono font-semibold">
                                                      {x.applied === false ? '미적용' : statusLabelKr(x.decisionType)}
                                                    </span>{' '}
                                                    <span className="font-mono">{conditionKeyToUiCode(x.conditionKey)}</span>
                                                    {(() => {
                                                      const parts = []

                                                      // 핵심 조건만 요약(가독성 목적)
                                                      if (x.waitingDays !== null && x.waitingDays !== undefined && String(x.waitingDays).trim() !== '') {
                                                        const wd = Number(x.waitingDays)
                                                        const dse = x.daysSinceEnd !== null && x.daysSinceEnd !== undefined ? Number(x.daysSinceEnd) : null
                                                        const ok = Number.isFinite(wd) && dse !== null && Number.isFinite(dse) ? dse >= wd : null
                                                        const suffix = ok === null ? `대기 ${x.waitingDays}일` : `대기 ${x.waitingDays}일`
                                                        parts.push(suffix)
                                                      }
                                                      if (x.maxCount !== null && x.maxCount !== undefined && String(x.maxCount).trim() !== '') {
                                                        const mc = Number(x.maxCount)
                                                        const cur =
                                                          x.matchedCount !== null && x.matchedCount !== undefined ? Number(x.matchedCount) : epPack?.counts?.total ?? null
                                                        const ok = Number.isFinite(mc) && cur !== null && Number.isFinite(Number(cur)) ? Number(cur) <= mc : null
                                                        const suffix = ok === null ? `최대 ${x.maxCount}` : `최대 ${x.maxCount}`
                                                        parts.push(suffix)
                                                      }
                                                      if (
                                                        x.inpatientMaxDays !== null &&
                                                        x.inpatientMaxDays !== undefined &&
                                                        String(x.inpatientMaxDays).trim() !== ''
                                                      ) {
                                                        const lim = Number(x.inpatientMaxDays)
                                                        const mx =
                                                          x.inpatientDaysMax !== null && x.inpatientDaysMax !== undefined
                                                            ? Number(x.inpatientDaysMax)
                                                            : null
                                                        const ok =
                                                          Number.isFinite(lim) && mx !== null && Number.isFinite(mx) ? mx <= lim : null
                                                        const suffix = ok === null ? `입원 ${x.inpatientMaxDays}일 이내` : `입원 ${x.inpatientMaxDays}일 이내`
                                                        parts.push(suffix)
                                                      }
                                                      const cleanedNote = (() => {
                                                        const raw = (x.note || '').toString().trim()
                                                        if (!raw) return ''
                                                        // note 예: "inpatient_max_days=10; source_row=18"
                                                        const tokens = raw
                                                          .split(';')
                                                          .map((t) => t.trim())
                                                          .filter(Boolean)
                                                          .filter((t) => !/^source_row\s*=\s*\d+$/i.test(t))
                                                          .filter((t) => !/^inpatient_max_days\s*=\s*\d+$/i.test(t))
                                                          .filter((t) => !/^surgery_condition_type\s*=\s*.+$/i.test(t))
                                                          .filter((t) => !/^requires_(no_)?surgery\s*=\s*(true|false)$/i.test(t))
                                                        return tokens.join('; ')
                                                      })()
                                                      if (cleanedNote) parts.push(cleanedNote)
                                                      if (parts.length === 0) return null
                                                      return <span className="text-slate-500"> • {parts.join(' • ')}</span>
                                                    })()}
                                                    {x.message ? <span className="text-slate-700"> • {x.message}</span> : null}
                                                  </div>
                                                ))}
                                              </div>
                                              {rulePack.length > 8 ? (
                                                <div className="mt-1 text-[11px] text-slate-500">* 룰은 상위 8건만 표시됩니다.</div>
                                              ) : null}
                                            </div>
                                          ) : (
                                            <div className="mt-2 text-[11px] text-slate-500">- 걸린 룰 없음</div>
                                          )}

                                          {groups.length > 0 ? (
                                            <div className="mt-2">
                                              <button
                                                type="button"
                                                className="rounded border bg-white px-2 py-1 text-[11px] hover:bg-slate-50"
                                                onClick={() => {
                                                  const next = new Set(threeNnTemplateKcdExpanded)
                                                  if (next.has(expandKey)) next.delete(expandKey)
                                                  else next.add(expandKey)
                                                  setThreeNnTemplateKcdExpanded(next)
                                                }}
                                              >
                                                {isExpanded ? '근거 진료내역 닫기' : `근거 진료내역 보기 (${groups.length})`}
                                              </button>
                                              {isExpanded ? (
                                                <div className="mt-2">
                                                  <div className="text-[11px] font-semibold text-slate-900">근거 진료내역(중복 요약)</div>
                                                  <div className="mt-0.5 space-y-0.5 text-[11px] text-slate-700">
                                                    {groups.slice(0, 6).map((g, idx) => (
                                                      <div key={idx}>
                                                        - {episodeSummary(g.ep)}
                                                        {g.count > 1 ? <span className="ml-1 font-mono text-slate-500">x{g.count}</span> : null}
                                                      </div>
                                                    ))}
                                                  </div>
                                                  {groups.length > 6 ? (
                                                    <div className="mt-1 text-[11px] text-slate-500">* 진료내역은 상위 6그룹만 표시됩니다.</div>
                                                  ) : null}
                                                </div>
                                              ) : null}
                                            </div>
                                          ) : null}
                                        </div>
                                      )
                                    })}
                                  </div>
                                  )}
                                </details>

                                {nonExceptionKcdKeys.length > 0 ? (
                                  <details className="mt-3 rounded border bg-white p-2">
                                    <summary className="cursor-pointer text-[11px] font-semibold text-slate-900">
                                      예외질환 미해당 진료내역 ({nonExceptionKcdKeys.length})
                                    </summary>
                                    <div className="mt-2 space-y-0.5 text-[11px] text-slate-700">
                                      {nonExceptionKcdKeys.slice(0, 20).map((k) => {
                                        const nm = allEpisodeKcdMap.get(k)?.diagnosisName || ''
                                        return (
                                          <div key={k}>
                                            - <span className="font-mono font-semibold">{k}</span>
                                            {nm ? <span className="text-slate-700"> ({nm})</span> : null}
                                          </div>
                                        )
                                      })}
                                      {nonExceptionKcdKeys.length > 20 ? (
                                        <div className="text-[11px] text-slate-500">… 외 {nonExceptionKcdKeys.length - 20}개</div>
                                      ) : null}
                                    </div>
                                  </details>
                                ) : null}

                                {rulesByKcd.get('__OTHER__')?.length > 0 ? (
                                  <div className="mt-3">
                                    <div className="text-[11px] font-semibold text-slate-900">기타(코드 매칭 불가) 룰</div>
                                    <div className="mt-1 space-y-0.5 text-[11px] text-slate-700">
                                      {rulesByKcd
                                        .get('__OTHER__')
                                        .slice(0, 6)
                                        .map((x, idx) => (
                                          <div key={idx}>
                                            - <span className="font-mono font-semibold">{statusLabelKr(x.decisionType)}</span>{' '}
                                            <span className="font-mono text-slate-500">({x.decisionType})</span> {x.conditionKey}
                                            {x.message ? <span className="text-slate-500"> • {x.message}</span> : null}
                                          </div>
                                        ))}
                                    </div>
                                  </div>
                                ) : null}
                              </div>
                            </div>
                          )
                        })()}
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
            {threeNnModalData?.kind !== 'SIMPLE_3N5' && (
              <div className="mt-3 rounded border bg-slate-50 p-2 text-[11px] text-slate-600">
                각 보험사별 예외질환 규칙은 지속적으로 업데이트 중입니다. 예외질환 추가 요청은{' '}
                <a className="font-semibold text-brand-sky hover:underline" href="/support/inquiries">
                  고객문의
                </a>
                를 이용해 주세요.
              </div>
            )}
          </div>
        </div>
      )}

      {/* 설계의뢰 팝업 (표준체/유병자 선택) */}
      {designRequestModalOpen && (
        <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/40 p-2 sm:p-4">
          <div className="w-full max-w-2xl rounded-lg bg-white shadow-lg p-3 sm:p-4 text-sm max-h-[90vh] sm:max-h-[85vh] overflow-auto">
            <div className="flex items-center justify-between gap-2 mb-2">
              <div className="font-semibold text-slate-900">설계의뢰</div>
              <button
                type="button"
                className="shrink-0 text-xs text-slate-500 hover:text-slate-800"
                onClick={() => setDesignRequestModalOpen(false)}
              >
                닫기
              </button>
            </div>

            <div className="rounded border bg-slate-50 p-3">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3 min-w-0">
                  <div className="text-xs font-semibold text-slate-900 whitespace-nowrap">설계 유형</div>
                  <div className="flex flex-wrap items-center gap-3 text-xs">
                    <label className="inline-flex items-center gap-2 whitespace-nowrap">
                      <input
                        type="radio"
                        name="designCoverageType"
                        value="healthy"
                        checked={designCoverageType === 'healthy'}
                        onChange={() => setDesignCoverageType('healthy')}
                      />
                      <span>표준체(건강체)</span>
                    </label>
                    <label className="inline-flex items-center gap-2 whitespace-nowrap">
                      <input
                        type="radio"
                        name="designCoverageType"
                        value="substandard"
                        checked={designCoverageType === 'substandard'}
                        onChange={async () => {
                          setDesignCoverageType('substandard')
                          setSubstandardIgnoreRecent3mOpinion(false)
                          if (selectedReportId) {
                            await handleEvaluate3nn(selectedReportId, { openModal: false, ignoreRecent3mOpinion: false })
                          }
                        }}
                      />
                      <span>유병자(간편보험)</span>
                    </label>
                  </div>
                </div>

                <div className="flex items-center gap-2 sm:justify-end flex-shrink-0">
                  <button
                    type="button"
                    className="flex-1 sm:flex-none sm:w-auto whitespace-nowrap rounded border px-2 py-2 sm:px-2 sm:py-1 text-xs border-slate-300 bg-white hover:bg-slate-50"
                    onClick={() => openQuestionnaire(designCoverageType === 'healthy' ? 'healthy' : 'substandard')}
                  >
                    질문서 보기
                  </button>
                  <button
                    type="button"
                    className="flex-1 sm:flex-none sm:w-auto whitespace-nowrap rounded border px-2 py-2 sm:px-2 sm:py-1 text-xs border-slate-300 bg-white hover:bg-slate-50 disabled:opacity-40"
                    disabled={designCoverageType !== 'substandard' || threeNnTemplateLoading}
                    onClick={async () => {
                      if (designCoverageType !== 'substandard') return
                      if (selectedReportId) {
                        await handleEvaluate3nnTemplates(selectedReportId, { openModal: true })
                      } else {
                        show('리포트를 먼저 선택해 주세요.', 'error')
                      }
                    }}
                    title={designCoverageType !== 'substandard' ? '유병자(간편보험)에서만 사용 가능합니다.' : undefined}
                  >
                    {threeNnTemplateLoading ? '불러오는 중...' : '보험사별 예외 적용'}
                  </button>
                  <button
                    type="button"
                    className="flex-1 sm:flex-none sm:w-auto whitespace-nowrap rounded border px-2 py-2 sm:px-2 sm:py-1 text-xs border-brand-navy text-brand-navy hover:bg-brand-navy hover:text-white disabled:opacity-40"
                    disabled={designCoverageType === 'substandard' && threeNnLoading}
                    onClick={() =>
                      handleGoToRequestNewFrom3nn({
                        coverageType: designCoverageType,
                        selectedInsurerCodes:
                          designCoverageType === 'substandard' ? Array.from(selectedInsurerCodesForRequest || []) : null,
                      })
                    }
                  >
                    {designCoverageType === 'substandard' && threeNnLoading ? '분석 중...' : '설계의뢰 생성'}
                  </button>
                </div>
              </div>
            </div>

            <div className="mt-3 space-y-3">
              {designCoverageType === 'substandard' ? (
                <div className="rounded border bg-slate-50 p-3">
                  <div className="flex items-center justify-between gap-2">
                    <div className="text-xs font-semibold text-slate-900">1차 요약</div>
                    <span className="rounded bg-slate-200 px-2 py-0.5 text-[11px] font-semibold text-slate-700">유병자 3N5</span>
                  </div>
                  {threeNnResult?.kind === 'SIMPLE_3N5' ? (
                    <div className="mt-2 text-xs text-slate-700 space-y-1">
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0 text-slate-700">
                          - <span className="font-semibold text-slate-900">인수기준 예외 처리</span>{' '}
                          <span className="text-slate-700">(최근 3개월 “입원/수술/재검사 소견” 제외)</span>
                          <div className="mt-0.5 ml-4 text-[11px] text-slate-500">
                            진료내역만으로 소견 여부 판단이 어려워, 체크 시 3N5의 “3개월” 조건을 판정에서 제외하고(경고로 표시) 나머지 기준으로 조합을 안내합니다.
                          </div>
                        </div>
                        <input
                          type="checkbox"
                          className="mt-0.5"
                          checked={substandardIgnoreRecent3mOpinion}
                          onChange={async (e) => {
                            const next = Boolean(e.target.checked)
                            setSubstandardIgnoreRecent3mOpinion(next)
                            if (selectedReportId) {
                              await handleEvaluate3nn(selectedReportId, { openModal: false, ignoreRecent3mOpinion: next })
                            }
                          }}
                        />
                      </div>
                      {substandardIgnoreRecent3mOpinion && (
                        <div className="ml-4 rounded border bg-white px-2 py-2 text-[11px] text-slate-700">
                          <div className="font-semibold text-slate-900">최근 3개월(투약 제외) 감지 근거</div>
                          {Array.isArray(threeNnResult?.exception?.recent3mNonDrugExamples) &&
                          threeNnResult.exception.recent3mNonDrugExamples.length > 0 ? (
                            <div className="mt-1 space-y-0.5 text-slate-600">
                              {threeNnResult.exception.recent3mNonDrugExamples.map((x, idx) => (
                                <div key={idx}>- {x}</div>
                              ))}
                            </div>
                          ) : (
                            <div className="mt-1 text-slate-500">표시할 예시가 없습니다.</div>
                          )}
                        </div>
                      )}
                      <div>
                        - 결과:{' '}
                        <span className="font-semibold">
                          {threeNnResult?.status === 'POSSIBLE'
                            ? '가능'
                            : threeNnResult?.status === 'DECLINED'
                              ? '불가'
                              : threeNnResult?.status || '-'}
                        </span>
                      </div>
                      {Array.isArray(threeNnResult?.possibleCombos) && (
                        <div>- 가능 조합: {threeNnResult.possibleCombos.length ? threeNnResult.possibleCombos.join(', ') : '없음'}</div>
                      )}
                      {Array.isArray(threeNnResult?.reasons) && threeNnResult.reasons.length > 0 && (
                        <div className="mt-1">
                          <div className="font-semibold text-slate-900">이유</div>
                          <div className="mt-1 space-y-0.5">
                            {threeNnResult.reasons.map((x, idx) => (
                              <div key={idx} className="text-slate-600">
                                - {x}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                      {threeNnResult?.footnote && (
                        <div className="mt-2 text-[11px] text-slate-500 whitespace-pre-wrap">{threeNnResult.footnote}</div>
                      )}
                    </div>
                  ) : (
                    <div className="mt-2 text-xs text-slate-600">
                      아직 유병자 3N5(1차) 결과가 없습니다. (필요 시 다시 시도해 주세요)
                    </div>
                  )}

                  {selectedInsurerCodesForRequest && selectedInsurerCodesForRequest.size > 0 && (
                    <div className="mt-3 rounded border bg-white p-2 text-xs text-slate-700">
                      <div className="flex items-center justify-between gap-2">
                        <div className="font-semibold text-slate-900">
                          선택된 보험사 ({selectedInsurerCodesForRequest.size})
                        </div>
                        <button
                          type="button"
                          className="text-[11px] text-slate-500 hover:text-slate-800"
                          onClick={() => setSelectedInsurerCodesForRequest(new Set())}
                        >
                          선택 해제
                        </button>
                      </div>
                      <div className="mt-1 space-y-0.5">
                        {Array.from(selectedInsurerCodesForRequest)
                          .slice(0, 8)
                          .map((code) => (
                            <div key={code} className="text-[11px] text-slate-700">
                              - <span className="font-semibold">{insurerNameKrOf(code)}</span>{' '}
                              <span className="font-mono text-slate-500">({String(code)})</span>
                            </div>
                          ))}
                        {selectedInsurerCodesForRequest.size > 8 && (
                          <div className="text-[11px] text-slate-500">… 외 {selectedInsurerCodesForRequest.size - 8}개</div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="rounded border bg-slate-50 p-3">
                  <div className="flex items-center justify-between gap-2">
                    <div className="text-xs font-semibold text-slate-900">1차 요약</div>
                    <span className="rounded bg-slate-200 px-2 py-0.5 text-[11px] font-semibold text-slate-700">표준체</span>
                  </div>
                  {(() => {
                    const hs = healthyAutoSummary || computeHealthyQuestionnaireSummary()
                    const row = (label, obj) => (
                      <div>
                        - {label}: <span className="font-semibold">{obj?.has ? '있음' : '없음'}</span>
                        {obj?.has && obj?.range && (
                          <div className="mt-0.5 ml-4 text-[11px] text-slate-600">기간: {obj.range}</div>
                        )}
                        {obj?.has && Array.isArray(obj?.examples) && obj.examples.length > 0 && (
                          <div className="mt-0.5 ml-4 text-[11px] text-slate-600">예: {obj.examples.join(', ')}</div>
                        )}
                      </div>
                    )
                    return (
                      <div className="mt-2 text-xs text-slate-700 space-y-1">
                        {row('최근 3개월(진료/검사/치료/입원/수술/투약)', hs?.recent3m)}
                        {row('최근 1년(검사/추가검사/재검사 참고)', hs?.check1y)}
                        {row('최근 5년(같은 원인 7일+ 치료 또는 30일+ 투약)', hs?.treat7orDrug30_5y)}
                        {row('최근 5년(입원/수술)', hs?.hosp5y)}
                        {row('최근 5년(11대 중대질병)', hs?.major11_5y)}
                        <div className="mt-2 text-[11px] text-slate-500">
                          * “최근 1년 내 검사”는 진단명에 ‘검사’가 포함된 내역 기준(추가검사/재검사 여부는 진료내역만으로는 한계가 있음)
                        </div>
                      </div>
                    )
                  })()}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* 건강체/유병자 질문서 팝업 */}
      {questionnaireModalType && (
        <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-3xl rounded-lg bg-white shadow-lg p-4 max-h-[85vh] overflow-auto">
            <div className="flex items-center justify-between mb-2">
              <div className="font-semibold text-slate-900">
                {questionnaireModalType === 'healthy' ? '질문서: 건강체(표준체)' : '질문서: 유병자(간편보험)'}
              </div>
              <button
                type="button"
                className="text-xs text-slate-500 hover:text-slate-800"
                onClick={() => setQuestionnaireModalType(null)}
              >
                닫기
              </button>
            </div>

            {questionnaireModalType === 'healthy' ? (
              <div className="space-y-3 text-xs text-slate-700">
                <div className="rounded border bg-slate-50 p-3">
                  <div className="font-semibold text-slate-900">최근 3개월</div>
                  <ol className="mt-1 list-decimal pl-5 space-y-1">
                    <li>
                      진찰/검사(건강검진 포함) 후{' '}
                      <span className="font-semibold">질병확정진단·질병의심소견·치료·입원·수술(제왕절개 포함)·투약</span> 여부
                    </li>
                    <li>마약 사용 또는 혈압강하제/신경안정제/수면제/각성제/흥분제/진통제 등 약물 상시 복용 여부</li>
                  </ol>
                </div>
                <div className="rounded border bg-slate-50 p-3">
                  <div className="font-semibold text-slate-900">최근 1년</div>
                  <ol className="mt-1 list-decimal pl-5 space-y-1">
                    <li>진찰/검사 후 추가검사·재검사 여부</li>
                  </ol>
                </div>
                <div className="rounded border bg-slate-50 p-3">
                  <div className="font-semibold text-slate-900">최근 5년</div>
                  <ol className="mt-1 list-decimal pl-5 space-y-1">
                    <li>입원/수술(제왕절개 포함) 여부</li>
                    <li>같은 원인으로 계속 7일 이상 치료 또는 30일 이상 투약 여부</li>
                    <li>
                      10대 질병(암/백혈병/고혈압/협심증/심근경색/심장판막증/간경화/뇌졸중(뇌출혈·뇌경색)/당뇨병/에이즈) 관련{' '}
                      <span className="font-semibold">진단·치료·입원·수술·투약</span> 여부
                    </li>
                  </ol>
                </div>
                <div className="text-[11px] text-slate-500">
                  * 실제 고지/인수는 보험사·상품·예외질환/면책 등에 따라 달라질 수 있습니다.
                </div>
              </div>
            ) : (
              <div className="space-y-3 text-xs text-slate-700">
                <div className="rounded border bg-slate-50 p-3">
                  <div className="font-semibold text-slate-900">최근 3개월</div>
                  <ol className="mt-1 list-decimal pl-5 space-y-1">
                    <li>입원소견/수술소견/추가검사·재검사 소견/질병확정진단·질병의심소견 여부</li>
                  </ol>
                </div>
                <div className="rounded border bg-slate-50 p-3">
                  <div className="font-semibold text-slate-900">최근 N년</div>
                  <ol className="mt-1 list-decimal pl-5 space-y-1">
                    <li>입원/수술 여부</li>
                    <li>암/뇌졸중/심근경색/협심증/심장판막증/간경화로 인한 진단/입원/수술 여부</li>
                  </ol>
                </div>
                <div className="rounded border bg-slate-50 p-3">
                  <div className="font-semibold text-slate-900">고지(예시)</div>
                  <div className="mt-1 text-xs text-slate-700">
                    당뇨/고혈압/고지혈/우울증/갑상선항진증 등은 약 투약 포함 고지 필요(보험사/상품별 상이)
                  </div>
                </div>
                <div className="text-[11px] text-slate-500">
                  * 유병자 탭의 3개월/입원·수술 필터는 진료내역 기반 “1차 참고용”이며, 소견 여부는 추가 확인이 필요합니다.
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* 주민번호 재입력 팝업 (TTL 만료 재시도) */}
      {retryJobId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-md rounded-lg bg-white p-4 shadow-lg">
            <div className="flex items-center justify-between">
              <div className="font-semibold text-slate-900">재시도: 주민등록번호 재입력</div>
              <button
                type="button"
                className="text-xs text-slate-500 hover:text-slate-800"
                onClick={() => setRetryJobId(null)}
              >
                닫기
              </button>
            </div>
            <p className="mt-2 text-xs text-slate-600">
              주민등록번호는 조회를 위해서만 사용되며 서버 DB에 저장하지 않습니다. 
            </p>
            <div className="mt-3">
              <label className="block text-xs font-semibold text-slate-700 mb-1">주민등록번호 (숫자만)</label>
              <input
                type="password"
                className="w-full rounded border px-2 py-1 text-sm"
                value={retryIdentity}
                onChange={(e) => setRetryIdentity(e.target.value)}
                placeholder="예: 9001011234567"
              />
              {(() => {
                const digits = (retryIdentity || '').toString().replaceAll(/[^0-9]/g, '')
                const masked = formatMaskedIdentityForUi(digits)
                if (!masked) return null
                return (
                  <div className="mt-1 text-[11px] text-slate-600">
                    표시: <span className="font-mono">{masked}</span>
                  </div>
                )
              })()}
            </div>
            <div className="mt-4 flex justify-end gap-2">
              <button
                type="button"
                className="rounded border px-3 py-1 text-sm hover:bg-slate-50"
                onClick={() => setRetryJobId(null)}
              >
                취소
              </button>
              <button
                type="button"
                className="rounded bg-brand-navy px-3 py-1 text-sm text-white hover:bg-brand-sky"
                onClick={handleRetryIdentity}
              >
                재시도 시작
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

